import axios from "axios";

import { browserAuth, getCsrfToken } from "@/lib/auth-client";

export const api = axios.create({
  baseURL: "/api/v1",
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Cookie sesji jest HttpOnly i trafia automatycznie wyłącznie do tego originu.
// JavaScript dodaje tylko jawny token CSRF oraz kontekst aktywnego sklepu.
api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const method = (config.method || "get").toLowerCase();
    if (!["get", "head", "options"].includes(method)) {
      const csrf = getCsrfToken();
      if (csrf) {
        // Po rotacji refresh retry nie może zachować starego nagłówka CSRF.
        config.headers["X-CSRF-Token"] = csrf;
      }
    }
    // M52b: auto-inject X-Shop-Id z localStorage. Backend respektuje header
    // dla endpointów które obsługują multi-shop (audyt, oferty, drafts).
    const shopId = localStorage.getItem("active_shop_id");
    if (shopId && !config.headers["X-Shop-Id"]) {
      config.headers["X-Shop-Id"] = shopId;
    }
  }
  return config;
});

let refreshPromise: Promise<void> | null = null;

function refreshSessionOnce(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = browserAuth
      .refreshSession()
      .then(() => undefined)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Response interceptor:
// - 401 → jedna współdzielona próba rotacji cookie, potem /login.
//   Wyjątek: 401 z code=TWOFA_VERIFY_REQUIRED → emit zdarzenie '2fa-required'
//   żeby UI pokazało modal/redirect na verify, NIE wylogowujemy.
// - 402 (moduł wymagany) → emit zdarzenie 'module-required' żeby frontend pokazał upsell.
// - 412 (Precondition Failed) używamy dla "brak połączenia z Allegro".
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && typeof window !== "undefined") {
      const detail = error.response.data?.detail;
      // Soft 2FA session check — backend mówi że trzeba ponownie wpisać kod 2FA
      if (typeof detail === "object" && detail?.code === "TWOFA_VERIFY_REQUIRED") {
        window.dispatchEvent(
          new CustomEvent("2fa-required", {
            detail: { message: detail.message },
          }),
        );
        return Promise.reject(error);
      }
      const config = error.config as
        | (typeof error.config & { _healsyRetried?: boolean })
        | undefined;
      if (config && !config._healsyRetried) {
        config._healsyRetried = true;
        try {
          await refreshSessionOnce();
          return api.request(config);
        } catch {
          // Refresh fail-closed; Auth Service wyczyścił nieaktywną sesję.
        }
      }
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    if (error.response?.status === 402 && typeof window !== "undefined") {
      const detail = error.response.data?.detail || {};
      window.dispatchEvent(
        new CustomEvent("module-required", {
          detail: {
            module: detail.module,
            module_name: detail.module_name,
            message: detail.message,
          },
        })
      );
    }
    return Promise.reject(error);
  }
);

// === Me (account/modules) ===
export interface ModuleCatalogItem {
  key: string;
  name: string;
  description: string;
  active: boolean;
}

export interface MyModulesResponse {
  plan: string;
  active_modules: string[];
  trial_expires_at: string | null;
  catalog: ModuleCatalogItem[];
}

export const meApi = {
  modules: () => api.get<MyModulesResponse>("/me/modules"),
  toggleModule: (key: string, active: boolean) =>
    api.post(`/me/modules/${encodeURIComponent(key)}/toggle`, { active }),
  resetTrial: () => api.post("/me/modules/reset-trial"),
  resetToPlan: () => api.post("/me/modules/reset-to-plan"),
  applyCart: (payload: { bundle_id?: string | null; module_keys?: string[] }) =>
    api.post("/me/cart/apply", payload),

  // --- Brand Settings (Sesja M9) ---
  brandSettings: () =>
    api.get<BrandSettingsResponse>("/me/brand-settings"),
  updateBrandSettings: (payload: BrandSettingsPatch) =>
    api.patch<BrandSettingsResponse>("/me/brand-settings", payload),
  generatePalette: (payload: { category?: string; mood?: string; base_color?: string }) =>
    api.post<{ suggestions: PaletteSuggestion[] }>(
      "/me/brand-settings/generate-palette", payload
    ),
  uploadBrandLogo: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post<BrandSettingsResponse>(
      "/me/brand-settings/logo", formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
  },
  deleteBrandLogo: () =>
    api.delete<BrandSettingsResponse>("/me/brand-settings/logo"),
  // M21: extract palette from logo via k-means
  extractPaletteFromLogo: () =>
    api.post<{ suggestion: PaletteSuggestion }>(
      "/me/brand-settings/extract-palette-from-logo", {}
    ),
  // M10: Frame catalog + preview
  listFrames: () =>
    api.get<{ frames: Array<{ id: string; label: string; description: string }> }>(
      "/me/brand-settings/frames"
    ),
  /** Pobiera PNG bytes preview ramki przez klienta z sesją cookie. */
  frameThumbObjectUrl: async (frameId: string, size = 400): Promise<string> => {
    const resp = await api.get<Blob>(
      `/me/brand-settings/frame-preview/${encodeURIComponent(frameId)}?size=${size}`,
      { responseType: "blob" }
    );
    return URL.createObjectURL(resp.data);
  },
  // M14: AI custom frame generator (single)
  generateCustomFrame: (payload: { category?: string; mood?: string }) =>
    api.post<BrandSettingsResponse>(
      "/me/brand-settings/generate-custom-frame", payload
    ),
  // M16: AI custom frame variants (3 propozycje)
  generateCustomFrameVariants: (payload: { category?: string; mood?: string }) =>
    api.post<{
      variants: Array<{ variant_id: string; preview_data_url: string }>;
      total_cost_usd: number;
    }>("/me/brand-settings/generate-custom-frame-variants", payload),
  selectCustomFrameVariant: (variant_id: string) =>
    api.post<BrandSettingsResponse>(
      "/me/brand-settings/custom-frame/select-variant", { variant_id }
    ),
  deleteCustomFrame: () =>
    api.delete<BrandSettingsResponse>("/me/brand-settings/custom-frame"),
  // M24: multi-frame storage
  listCustomFrames: () =>
    api.get<{ frames: Array<{
      id: number;
      label: string;
      frame_path: string;
      is_active: boolean;
      created_at: string;
    }> }>("/me/brand-settings/custom-frames"),
  activateCustomFrame: (frameId: number) =>
    api.post<BrandSettingsResponse>(
      `/me/brand-settings/custom-frames/${frameId}/activate`, {}
    ),
  deleteCustomFrameEntry: (frameId: number) =>
    api.delete<BrandSettingsResponse>(
      `/me/brand-settings/custom-frames/${frameId}`
    ),
  /** M17: upload custom photo + zwraca object URL z naklejoną aktualną ramką usera */
  frameOnPhotoObjectUrl: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    const resp = await api.post<Blob>(
      "/me/brand-settings/frame-preview-on-photo",
      formData,
      {
        responseType: "blob",
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return URL.createObjectURL(resp.data);
  },
};

// === Brand Settings types ===
export interface BrandSettingsResponse {
  client_id: number;
  primary_color: string | null;
  secondary_color: string | null;
  accent_color: string | null;
  palette_name: string | null;
  logo_path: string | null;
  slogan: string | null;
  contact_text: string | null;
  gallery_frame_enabled: boolean;
  frame_style: "classic" | "minimal" | "bold";
  frame_template_id: string;
  custom_frame_path: string | null;
  palette_complete: boolean;
  logo_uploaded: boolean;
  custom_frame_uploaded: boolean;
  completeness_score: number;
  completeness_checklist: Array<{
    key: string;
    label: string;
    weight: number;
    done: boolean;
  }>;
}
export interface BrandSettingsPatch {
  primary_color?: string | null;
  secondary_color?: string | null;
  accent_color?: string | null;
  palette_name?: string | null;
  slogan?: string | null;
  contact_text?: string | null;
  gallery_frame_enabled?: boolean;
  frame_style?: "classic" | "minimal" | "bold";
  frame_template_id?: string;
}
export interface PaletteSuggestion {
  name: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  description: string;
}

// === Pricing (public — no auth) ===
export interface PricingModule {
  key: string;
  name: string;
  description: string;
  price_monthly: number;
  is_free: boolean;
}

export interface PricingBundle {
  id: string;
  name: string;
  tagline: string;
  target_persona: string;
  price_monthly: number;
  is_custom_price?: boolean;
  module_keys: string[];
  highlights: string[];
  badge: string | null;
  sum_alacarte_monthly: number;
  savings_monthly: number;
  savings_pct: number;
  module_count: number;
}

export interface PricingCatalog {
  modules: PricingModule[];
  bundles: PricingBundle[];
  trial_days: number;
  free_modules: string[];
}

export const pricingApi = {
  catalog: () => api.get<PricingCatalog>("/pricing/catalog"),
};

// === Allegro Ads (Campaign Center) ===
export interface AdsAuditProblem {
  type: string;
  severity: "critical" | "warning" | "info" | string;
  offer_id: number | null;
  detected_at: string;
  summary: string | null;
  metrics_before?: Record<string, unknown> | null;
  metrics_after?: Record<string, unknown> | null;
  suggested_actions: Array<{
    type: string | null;
    description: string;
    requires_approval: boolean;
  }>;
}

export interface AdsAuditResponse {
  problems_count: number;
  problems: AdsAuditProblem[];
}

export interface AdsCampaignDetailResponse {
  campaign_id: string;
  raw_stats: unknown;
  offers: Array<{
    allegro_offer_id: string;
    metrics: {
      impressions: number | null;
      clicks: number | null;
      cost: number | null;
      sales_count: number | null;
      sales_amount: number | null;
      ctr: number | null;
      cpc: number | null;
      cvr: number | null;
      roas: number | null;
    };
    local: { id: number; allegro_offer_id: string; title: string } | null;
  }>;
}

export interface AdsSyncResponse {
  success: boolean;
  campaigns_processed: number;
  campaigns_upserted?: number;
  signals_written: number;
  errors: string[];
  note?: string;
}

// === Agency (multi-agent) ===
export type RiskLevel = "low" | "medium" | "high";
export type RecommendationStatus =
  | "pending"
  | "auto_approved"
  | "approved"
  | "rejected"
  | "vetoed"
  | "implemented"
  | "failed";

export interface AgencyCase {
  id: number;
  client_id?: number;
  case_type: string;
  status: string;
  severity: string;
  opened_by: string;
  opened_at: string;
  closed_at: string | null;
  offer_id: number | null;
  summary: string | null;
  shop_id?: number | null;
  signal_payload?: Record<string, unknown> | null;
  synthesis?: {
    synthesis_summary?: string;
    root_causes?: Array<{
      description: string;
      probability: number;
      severity: string;
    }>;
    prioritized_action_kinds?: string[];
    reasoning?: string;
  } | null;
}

export interface AgencyCaseEvent {
  id: number;
  ts: string;
  actor: string;
  kind: string;
  payload: Record<string, unknown> | null;
}

export interface AgencyAgentRun {
  id: number;
  agent_key: string;
  model: string | null;
  cost_usd: number | null;
  tokens: number;
  latency_ms: number;
  created_at: string;
  response: {
    agent_key: string;
    summary: string;
    findings: Array<{
      kind: string;
      severity: string;
      confidence: number;
      summary: string;
      details: Record<string, unknown>;
    }>;
    suggested_actions: Array<{
      kind: string;
      title: string;
      rationale: string;
      payload: Record<string, unknown>;
      estimated_risk: string;
    }>;
    confidence: number;
    error: string | null;
  };
}

export interface AgencyRecommendation {
  id: number;
  case_id?: number;
  case_type?: string;
  case_summary?: string | null;
  shop_id?: number | null;
  offer_id?: number | null;
  offer_title?: string | null;
  offer_image_url?: string | null;
  offer_sku?: string | null;
  allegro_offer_id?: string | null;
  title: string;
  rationale: string;
  action_kind: string;
  payload: Record<string, unknown> | null;
  risk_level: RiskLevel;
  status: RecommendationStatus;
  impact_estimate_pln?: number | null;
  requires_human?: boolean;
  snoozed_until?: string | null;
  started_at?: string | null;
  created_at?: string;
  approved_at?: string | null;
  implemented_at?: string | null;
  rejection_reason?: string | null;
  column?: KanbanColumn;
  has_conflict?: boolean;
  proposed_changes?: Record<string, unknown> | null;
  execution_result?: Record<string, unknown> | null;
  rollback_available?: boolean;
  execution_error?: string | null;
}

export interface RecommendationProposal {
  supports_proposal?: boolean;
  reason?: string;
  error?: string;
  // edit_title
  old_title?: string;
  new_title?: string;
  all_candidates?: Array<{ title: string; length?: number; rationale?: string }>;
  recommended_index?: number;
  // edit_description
  old_description_preview?: string;
  new_sections?: Array<{ heading: string; content: string }>;
  word_count?: number;
  rationale?: string;
  // edit_parameters
  current_params_count?: number;
  suggestions?: Array<{
    name: string;
    value: string;
    confidence: number;
    source: string;
  }>;
  note?: string;
}

export interface RecommendationExecuteResponse {
  success: boolean;
  id: number;
  status?: string;
  result?: Record<string, unknown>;
  rollback_available?: boolean;
  requires_manual?: boolean;
  reason?: string;
  deep_link?: string;
  error?: string;
}

export interface ExecutorsRegistryResponse {
  executors: string[];
  proposers: string[];
}

export type KanbanColumn =
  | "to_approve"
  | "todo_human"
  | "in_progress_human"
  | "in_progress_ai"
  | "done"
  | "rejected_snoozed";

export type TransitionAction = "start" | "complete" | "reject" | "reset";

export interface RecommendationListResponse {
  items: AgencyRecommendation[];
  count: number;
  column_counts: Record<KanbanColumn, number>;
  conflicts_count: number;
}

export interface RecommendationStats {
  to_approve: number;
  todo_human: number;
  in_progress_human: number;
  in_progress_ai: number;
  done_this_week: number;
  impact_pln_this_week: number;
  acceptance_rate: number | null;
  approved_30d: number;
  rejected_30d: number;
  snoozed: number;
  conflicts_count: number;
}

export interface AgencyCaseDetail {
  case: AgencyCase;
  events: AgencyCaseEvent[];
  agent_runs: AgencyAgentRun[];
  recommendations: AgencyRecommendation[];
}

export interface AgencyFeedbackOutcome {
  id: number;
  recommendation_id: number;
  days_after: number;
  checked_at: string;
  success_score: number | null;
  notes: string | null;
  rec_title: string;
  action_kind: string;
  case_id: number;
}

export type AutonomousMode = "conservative" | "standard" | "autonomous";

export interface AgencyPolicy {
  client_id: number;
  margin_floor_pct: number;
  max_cpc_change_pct: number;
  max_price_change_pct: number;
  max_budget_change_pct: number;
  daily_action_limit: number;
  protected_offer_ids: number[];
  autonomous_mode: AutonomousMode;
  smart_bidding_enabled: boolean;
  target_roas: number;
  min_clicks_threshold: number;
  cpc_increase_step_pct: number;
  cpc_decrease_step_pct: number;
  last_smart_run_at: string | null;
  smart_pricing_enabled: boolean;
  min_margin_pct: number;
  last_pricing_run_at: string | null;
  weekly_digest_enabled: boolean;
  last_digest_sent_at: string | null;
  // AUTO-3c (2026-06-23) — auto-exclude bad performers (default OFF)
  auto_exclude_enabled?: boolean;
  last_auto_exclude_run_at?: string | null;
}

export type AgencyPolicyUpdate = Partial<Omit<AgencyPolicy, "client_id">>;

export const agencyApi = {
  scan: (shopId?: number | null) =>
    api.post("/agency/scan", undefined, {
      params: shopId != null ? { shop_id: shopId } : undefined,
    }),
  // AUTO-7 (2026-06-23) — smart bidding (ROAS targeting) status
  smartBiddingStatus: () =>
    api.get<{
      enabled: boolean;
      target_roas: number;
      today_count: number;
      week_count: number;
      automated_campaigns: {
        total: number;
        in_band: number;
        above_band: number;
        below_band: number;
        not_enough_data: number;
      };
    }>("/agency/smart-bidding-status"),

  // AUTO-5 (2026-06-23) — lifecycle timeline (KPI + auto-akcje)
  lifecycleTimeline: (campaignId: string, days = 30) =>
    api.get<{
      campaign_id: string;
      current_stage: string | null;
      stage_changed_at: string | null;
      connected_at: string | null;
      days: number;
      kpi_series: Array<{
        date: string;
        clicks: number;
        cost: number;
        sales: number;
        impressions: number;
        roas: number;
      }>;
      actions: Array<{
        id: number;
        title: string;
        action_kind: string;
        risk_level: "low" | "medium" | "high";
        status: "auto_approved" | "implemented" | "failed";
        executed_at: string;
        error: string | null;
      }>;
    }>(`/agency/lifecycle-timeline?campaign_id=${encodeURIComponent(campaignId)}&days=${days}`),

  // AUTO-4 (2026-06-23) — auto-pilot stats
  autoStats: () =>
    api.get<{
      today_count: number;
      week_count: number;
      daily_limit: number;
      mode: "conservative" | "standard" | "autonomous";
      last_actions: Array<{
        id: number;
        title: string;
        action_kind: string;
        risk_level: "low" | "medium" | "high";
        status: "auto_approved" | "implemented" | "failed";
        campaign_id: string | null;
        executed_at: string | null;
        error: string | null;
      }>;
    }>("/agency/auto-stats"),
  feedbackCheck: () => api.post("/agency/feedback/check"),
  feedbackList: () =>
    api.get<{
      items: AgencyFeedbackOutcome[];
      count: number;
      average_success_score: number | null;
    }>("/agency/feedback"),
  cases: (status?: string, shopId?: number | null) =>
    api.get<{ cases: AgencyCase[]; count: number }>("/agency/cases", {
      params: {
        status,
        ...(shopId != null ? { shop_id: shopId } : {}),
      },
    }),
  case: (id: number) => api.get<AgencyCaseDetail>(`/agency/cases/${id}`),
  process: (id: number) => api.post(`/agency/cases/${id}/process`),
  queue: (shopId?: number | null) =>
    api.get<{ items: AgencyRecommendation[]; count: number }>("/agency/queue", {
      params: shopId != null ? { shop_id: shopId } : undefined,
    }),
  activityLog: (shopId?: number | null) =>
    api.get<{ items: AgencyRecommendation[]; count: number }>("/agency/activity-log", {
      params: shopId != null ? { shop_id: shopId } : undefined,
    }),
  approve: (id: number) =>
    api.post(`/agency/recommendations/${id}/approve`),
  reject: (id: number, reason?: string) =>
    api.post(`/agency/recommendations/${id}/reject`, { reason }),
  getPolicy: () => api.get<AgencyPolicy>("/agency/policy"),
  updatePolicy: (payload: AgencyPolicyUpdate) =>
    api.put<AgencyPolicy>("/agency/policy", payload),
  updateCaseStatus: (caseId: number, status: string) =>
    api.put<{
      success: boolean;
      id: number;
      status: string;
      previous?: string;
      noop?: boolean;
    }>(`/agency/cases/${caseId}/status`, { status }),
  bulkApprove: (ids: number[]) =>
    api.post<{
      approved: number[];
      skipped: { id: number; reason: string }[];
      dispatched: { id: number; result?: unknown; error?: string }[];
      count: number;
    }>("/agency/recommendations/bulk-approve", { ids }),
  bulkReject: (ids: number[], reason?: string) =>
    api.post<{
      rejected: number[];
      skipped: { id: number; reason: string }[];
      count: number;
    }>("/agency/recommendations/bulk-reject", { ids, reason }),
};

export const recommendationsApi = {
  list: (params?: {
    column?: KanbanColumn;
    case_type?: string;
    action_kind?: string;
    risk_level?: RiskLevel;
    search?: string;
    sort?: "impact" | "created";
  }) =>
    api.get<RecommendationListResponse>("/recommendations", { params }),
  stats: () => api.get<RecommendationStats>("/recommendations/stats"),
  bulkApprove: (ids: number[]) =>
    api.post<{ approved: number[]; skipped: { id: number; reason: string }[]; count: number }>(
      "/recommendations/bulk-approve",
      { ids }
    ),
  bulkReject: (ids: number[], reason?: string) =>
    api.post<{ rejected: number[]; skipped: { id: number; reason: string }[]; count: number }>(
      "/recommendations/bulk-reject",
      { ids, reason }
    ),
  snooze: (id: number, until: string) =>
    api.post(`/recommendations/${id}/snooze`, { until }),
  wake: (id: number) => api.post(`/recommendations/${id}/wake`),
  transition: (id: number, action: TransitionAction) =>
    api.post(`/recommendations/${id}/transition`, { action }),
  proposeChanges: (id: number) =>
    api.post<{ id: number; proposal: RecommendationProposal }>(
      `/recommendations/${id}/propose-changes`
    ),
  execute: (id: number) =>
    api.post<RecommendationExecuteResponse>(`/recommendations/${id}/execute`),
  rollback: (id: number) =>
    api.post<{ success: boolean; id: number; rolled_back: boolean }>(
      `/recommendations/${id}/rollback`
    ),
  executorsRegistry: () =>
    api.get<ExecutorsRegistryResponse>("/recommendations/executors/registry"),
  manualConfirm: (id: number) =>
    api.post<{ success: boolean; id: number; manual?: boolean }>(
      `/recommendations/${id}/manual-confirm`
    ),
};

export interface CreateCampaignPayload {
  name: string;
  max_cpc: number;
  daily_budget: number;
  offers: string[];
  status?: "ACTIVE" | "PAUSED";
  bidding_model?: string;
  created_via?: "manual" | "ai_wizard" | "auto_suggestion";
  user_brief?: string | null;
  ai_options?: AdsAiOptions | null;
}

export interface CreateCampaignResponse {
  success: boolean;
  campaign_id: string;
  allegro_result: unknown;
  note: string | null;
  local_only: boolean;
}

export interface AdsOfferSuggestion {
  allegro_offer_id: string;
  offer_id: number;
  title: string;
  category_name: string | null;
  price: number | null;
  score: number;
  reasons: string[];
  ai_reasoning?: string;
  stock_sold: number;
  stock_available: number | null;
  has_ads: boolean;
}

// === M54 — Centrum kampanii (Senvio-style) ===
export interface AdsKpiSummary {
  active_campaigns: number;
  cost_30d: number;
  sales_amount_30d: number;
  roas: number;
  total_campaigns: number;
}

export interface AdsCampaignKpi {
  cost_30d?: number;
  sales_amount_30d?: number;
  roas?: number;
  impressions_30d?: number;
  clicks_30d?: number;
}

export interface AdsAiOptions {
  optimize_titles?: boolean;
  optimize_prices?: boolean;
  optimize_descriptions?: boolean;
  optimize_attributes?: boolean;
  high_intelligence?: boolean;
  ai_picks_offers?: boolean;
}

export type OptimizationStage = "LEARNING" | "SUGGESTING" | "AUTOMATED";

export interface AdsLocalCampaign {
  campaign_id: string;
  name: string;
  status: string;
  channel: string;
  created_via: "manual" | "ai_wizard" | string;
  kpi: AdsCampaignKpi | null;
  last_synced_at: string | null;
  user_brief: string | null;
  ai_options: AdsAiOptions | null;
  created_at: string | null;
  optimization_stage: OptimizationStage;
  days_under_us: number;
}

export interface AdsLifecycleInfo {
  campaign_id: string;
  name: string;
  stage: OptimizationStage;
  days_under_us: number;
  stage_progress_pct: number;
  baseline: AdsCampaignKpi | Record<string, unknown>;
  current: AdsCampaignKpi | Record<string, unknown>;
  deltas_pct: {
    roas: number | null;
    cost_30d: number | null;
    sales_amount_30d: number | null;
  };
  connected_to_us_at: string | null;
  stage_changed_at: string | null;
}

export interface AdsTimelineItem {
  kind: "recommendation" | "event";
  ts: string | null;
  id: number;
  case_id: number;
  // recommendation
  title?: string;
  rationale?: string | null;
  action_kind?: string;
  risk_level?: string;
  status?: string;
  // event
  actor?: string;
  event_kind?: string;
  payload?: Record<string, unknown> | null;
}

// Top 5 #1 (2026-08-11): kanibalizacja Ads — ta sama oferta w >=2 adgroupach
export interface CannibalizationAdgroup {
  adgroup_id: string;
  adgroup_name: string;
  status: string;
  cost_30d: number;
}
export interface CannibalizationCase {
  offer_id: string;
  offer_title: string;
  offer_main_image_url: string | null;
  offer_price: number | null;
  adgroup_count: number;
  adgroups: CannibalizationAdgroup[];
  total_cost_30d: number;
}
export interface CannibalizationResponse {
  cases: CannibalizationCase[];
  total_cases: number;
  total_offers_affected: number;
  adgroups_scanned: number;
  cached?: boolean;
}

// Top 5 #2 (2026-08-11): CPC waste — oferty z dobrym organic ktore płacą CPC
export interface OrganicWasteAdgroup {
  adgroup_id: string;
  adgroup_name: string;
  cost_30d: number;
}
export interface OrganicWasteCase {
  offer_id: string;
  offer_title: string;
  offer_main_image_url: string | null;
  offer_price: number | null;
  avg_position: number;
  views_30d: number;
  visits_30d: number;
  conversion_rate_30d: number;
  stock_sold_30d: number;
  ads_cost_30d: number;
  ads_clicks_30d: number;
  ads_sales_30d: number;
  adgroups: OrganicWasteAdgroup[];
}
export interface OrganicWasteResponse {
  cases: OrganicWasteCase[];
  total_cases: number;
  total_wasted_30d: number;
  adgroups_scanned: number;
  criteria: {
    max_position: number;
    min_cost: number;
    min_views: number;
    min_stock_sold: number;
  };
  cached?: boolean;
}

// Sprint VIII: POAS + Stock Guard typy
export interface PoasCampaignItem {
  success: boolean;
  error?: string;
  campaign_id: string;
  campaign_name: string;
  status?: string;
  revenue_30d_pln?: number;
  spend_30d_pln?: number;
  roas?: number;
  avg_margin_pct?: number;
  estimated_profit_pln?: number;
  poas?: number;
  health?: "scale" | "maintain" | "optimize" | "pause";
  recommendation?: string;
  quality?: {
    is_estimated: boolean;
    n_offers: number;
    n_with_cost?: number;
    fallback_reason?: string;
  };
  n_offers?: number;
}

export interface PoasAnalysisResponse {
  success: boolean;
  items: PoasCampaignItem[];
  summary: {
    total_campaigns: number;
    total_spend_pln: number;
    total_profit_pln: number;
    weighted_poas: number | null;
    n_scale: number;
    n_maintain: number;
    n_optimize: number;
    n_pause: number;
  };
}

export interface StockGuardOffer {
  allegro_offer_id: string;
  title: string | null;
  stock: number;
  avg_daily_sales: number;
  days_coverage: number | null;
  main_image_url: string | null;
  is_zero_stock: boolean;
  is_below_min: boolean;
}

export interface StockGuardCampaign {
  success: boolean;
  campaign_id: string;
  campaign_name: string;
  status?: string;
  total_offers: number;
  zero_stock_offers: number;
  below_min_offers: number;
  healthy_offers: number;
  recommend_pause: boolean;
  recommendation: string;
  offers: StockGuardOffer[];
}

export interface StockGuardResponse {
  success: boolean;
  at_risk_count: number;
  healthy_count: number;
  recommend_pause_count: number;
  at_risk: StockGuardCampaign[];
  healthy: Array<{
    campaign_id: string;
    campaign_name: string;
    total_offers: number;
  }>;
}

export const adsApi = {
  campaigns: () => api.get("/ads/campaigns"),
  cannibalization: (refresh = false) =>
    api.get<CannibalizationResponse>("/ads/cannibalization", { params: { refresh } }),
  organicWaste: (refresh = false) =>
    api.get<OrganicWasteResponse>("/ads/organic-waste", { params: { refresh } }),
  createCampaign: (payload: CreateCampaignPayload) =>
    api.post<CreateCampaignResponse>("/ads/campaigns/sponsored", payload),
  deleteCampaign: (campaignId: string) =>
    api.delete(`/ads/campaigns/sponsored/${encodeURIComponent(campaignId)}`),
  suggestOffers: (limit = 10) =>
    api.post<{
      suggestions: AdsOfferSuggestion[];
      considered: number;
      llm_cost_usd: number | null;
      llm_tokens: number;
    }>(`/ads/campaigns/suggest-offers?limit=${limit}`),
  campaignDetail: (campaignId: string) =>
    api.get<AdsCampaignDetailResponse>(
      `/ads/campaigns/${encodeURIComponent(campaignId)}/detail`
    ),
  sponsoredStats: (campaignId: string) =>
    api.get(`/ads/campaigns/sponsored/${encodeURIComponent(campaignId)}/stats`),
  billing: (from_date?: string, to_date?: string) =>
    api.get("/ads/billing/ads", { params: { from_date, to_date } }),
  sync: () => api.post<AdsSyncResponse>("/ads/sync"),
  audit: () => api.post<AdsAuditResponse>("/ads/audit"),
  // M54
  localCampaigns: () =>
    api.get<{ campaigns: AdsLocalCampaign[]; count: number }>("/ads/campaigns/local"),
  // Sprint VIII/3: POAS + Stock Guard (Marta pełne Ads)
  poasAnalysis: () => api.get<PoasAnalysisResponse>("/ads/poas-analysis"),
  stockGuard: () => api.get<StockGuardResponse>("/ads/stock-guard"),
  // X2.1 — Smart pricing (always SUGGESTING)
  smartPricingRunNow: () =>
    api.post<{
      ok: boolean;
      offers_scanned: number;
      decisions_made: number;
      decisions_rejected_audit: number;
      recommendations_created: number;
      errors: string[];
      details: Array<{
        offer_id: number;
        allegro_offer_id: string;
        title: string;
        current_price: number;
        proposed_price: number;
        delta_pct: number;
        current_margin_pct: number;
        proposed_margin_pct: number;
        competitor_avg: number;
        competitor_min: number;
        competitor_count: number;
        reason: string;
      }>;
    }>("/ads/smart-pricing/run-now"),
  // Pulpit — executive dashboard summary
  dashboardSummary: () =>
    api.get<{
      generated_at: string;
      inbox: {
        pending_pricing: number;
        pending_bidding: number;
        pending_other: number;
        session_status: "OK" | "EXPIRED" | "NO_CONNECTION";
        session_error_at: string | null;
        ready_to_promote_count: number;
        ready_to_promote_sample: Array<{ campaign_id: string; name: string }>;
        critical_audit: number;
      };
      pulse_7d: {
        has_data: boolean;
        total_cost: number;
        total_sales: number;
        total_net_margin: number | null;
        margin_pct_avg: number | null;
        roas: number | null;
        daily_points: Array<{
          date: string;
          cost: number;
          sales: number;
          net_margin: number | null;
        }>;
        days_with_data: number;
      };
      recent_agent_activity: Array<{
        id: number;
        ts: string;
        actor: string;
        kind: string;
        case_id: number;
        case_summary: string | null;
        campaign_id: string | null;
        offer_id: number | null;
        payload: Record<string, unknown>;
      }>;
      ads_status: {
        total: number;
        stages_count: { LEARNING: number; SUGGESTING: number; AUTOMATED: number };
        statuses_count: { ACTIVE: number; PAUSED: number; OTHER: number };
        top_3_roas: Array<{
          campaign_id: string;
          name: string;
          status: string;
          stage: string;
          roas: number;
          cost_30d: number;
          daily_budget: number;
          budget_used_pct: number;
        }>;
        pacing_alerts: Array<{
          campaign_id: string;
          name: string;
          budget_used_pct: number;
          daily_budget: number;
        }>;
        pacing_alerts_total: number;
      };
    }>("/ads/dashboard/summary"),
  // X2.2 — Weekly digest
  digestPreview: () =>
    api.get<{ ok: boolean; subject?: string; html?: string; reason?: string; note?: string }>(
      "/ads/digest/preview"
    ),
  digestSendNow: () =>
    api.post<{ ok: boolean; status?: string; log_id?: number; reason?: string }>(
      "/ads/digest/send-now"
    ),
  smartPricingPending: () =>
    api.get<{
      items: Array<{
        recommendation_id: number;
        case_id: number;
        offer_id: number | null;
        allegro_offer_id: string | null;
        offer_title: string | null;
        offer_image_url: string | null;
        title: string;
        rationale: string;
        current_price: number | null;
        proposed_price: number | null;
        delta_pct: number | null;
        current_margin_pct: number | null;
        proposed_margin_pct: number | null;
        competitor_avg: number | null;
        competitor_count: number | null;
        created_at: string;
      }>;
      count: number;
    }>("/ads/smart-pricing/pending"),
  // X1.9 — Session status (banner gdy cookies Allegro wygasły)
  sessionStatus: () =>
    api.get<{
      connected: boolean;
      auth_status: "OK" | "EXPIRED" | "NO_CONNECTION";
      has_cookies?: boolean;
      cookies_updated_at?: string | null;
      last_auth_error_at?: string | null;
      last_auth_error_msg?: string | null;
    }>("/ads/extension/session-status"),
  // X2.0.D — Lifecycle / soft landing
  lifecycle: (campaignId: string) =>
    api.get<AdsLifecycleInfo>(
      `/ads/campaigns/${encodeURIComponent(campaignId)}/lifecycle`
    ),
  enableAutomated: (campaignId: string) =>
    api.post<{ ok: boolean; stage: OptimizationStage; error?: string; note?: string }>(
      `/ads/campaigns/${encodeURIComponent(campaignId)}/enable-automated`
    ),
  downgradeStage: (campaignId: string) =>
    api.post<{ ok: boolean; stage: OptimizationStage; reason: string }>(
      `/ads/campaigns/${encodeURIComponent(campaignId)}/downgrade`
    ),
  kpiSummary: () => api.get<AdsKpiSummary>("/ads/kpi-summary"),
  timeline: (campaignId: string) =>
    api.get<{ campaign_id: string; items: AdsTimelineItem[]; count: number }>(
      `/ads/campaigns/${encodeURIComponent(campaignId)}/timeline`
    ),

  // Sprint AUTO-3 (2026-06-22) — auto-exclude bad performers
  badPerformers: (adgroupId: string) =>
    api.get<{
      adgroup_id: string;
      total_offers: number;
      healthy_count: number;
      no_data_count: number;
      bad_performers: Array<{
        offer_id: string;
        name: string;
        image_url: string | null;
        url: string | null;
        price: string;
        currency: string;
        reason: "no_sales_burning_budget" | "high_acos" | "low_ctr";
        severity: "critical" | "high" | "medium";
        label: string;
        spend?: number;
        sales?: number;
        clicks?: number;
        acos?: number;
        ctr?: number;
        impressions?: number;
      }>;
      thresholds: Record<string, number>;
    }>(`/ads/campaigns/${encodeURIComponent(adgroupId)}/bad-performers`),

  excludeOffers: (adgroupId: string, offerIds: string[], reason?: string) =>
    api.post<{
      success: boolean;
      adgroup_id: string;
      removed_offers: string[];
      removed_count: number;
    }>(`/ads/campaigns/${encodeURIComponent(adgroupId)}/exclude-offers`, {
      offer_ids: offerIds,
      reason,
    }),

  // AUTO-1 (2026-06-23) — Orphan offers (aktywne oferty bez kampanii)
  orphanOffers: () =>
    api.get<{
      advertised_count: number;
      active_total: number;
      orphan_total: number;
      orphan: Array<{
        offer_id: string;
        title: string;
        category_id: string | null;
        category_name: string | null;
        brand: string | null;
        price: string | null;
        currency: string | null;
      }>;
      truncated: boolean;
    }>("/ads/orphan-offers"),

  orphanSuggestGrouping: () =>
    api.get<{
      orphan_total: number;
      suggestions: Array<{
        key: string;
        name: string;
        label: string;
        grouped_by: "brand" | "category" | "misc";
        offer_count: number;
        offer_ids: string[];
        sample_offers: Array<{ offer_id: string; title: string; price: string | null }>;
        suggested_daily_budget: number;
        suggested_max_cpc: number;
        avg_offer_price: number;
      }>;
      groups_count?: number;
      reason?: string;
    }>("/ads/orphan-offers/suggest-grouping"),

  // AUTO-2 (2026-06-23) — Pareto split (top 20% → Premium kampania)
  paretoAnalysis: (adgroupId: string) =>
    api.get<{
      adgroup_id: string;
      applicable: boolean;
      reason: string;
      top_offers: Array<{
        offer_id: string;
        name: string;
        image_url: string | null;
        url: string | null;
        price: string;
        currency: string;
        sales_30d: number;
        cost_30d: number;
        clicks_30d: number;
        roas: number | null;
      }>;
      top_count: number;
      total_offers: number;
      total_revenue: number;
      top_revenue_pct: number;
    }>(`/ads/campaigns/${encodeURIComponent(adgroupId)}/pareto-analysis`),

  // AUTO-6 (2026-06-23) — A/B testing
  startABTest: (adgroupId: string, newName?: string) =>
    api.post<{
      success: boolean;
      campaign_a_id: string;
      campaign_b_id: string;
      campaign_b_name: string;
      ends_at: string;
      duration_days: number;
      split: { a_offers: number; b_offers: number };
      remove_error: string | null;
    }>(`/ads/campaigns/${encodeURIComponent(adgroupId)}/start-ab-test`, {
      new_name: newName,
    }),

  listABTests: () =>
    api.get<{
      items: Array<{
        id: number;
        campaign_a_id: string;
        campaign_b_id: string;
        started_at: string;
        ends_at: string | null;
        status: "running" | "finalized";
        winner_id: string | null;
        decision_kind: string | null;
        summary: {
          kpi_a?: { cost: number; sales: number; roas: number };
          kpi_b?: { cost: number; sales: number; roas: number };
          winner?: string;
          decision?: string;
        } | null;
      }>;
      count: number;
    }>("/ads/ab-tests"),

  paretoSplit: (adgroupId: string, payload: {
    offer_ids: string[];
    new_name: string;
    cpc_multiplier?: number;
    daily_budget?: number;
  }) =>
    api.post<{
      success: boolean;
      new_campaign_id: string;
      new_campaign_name: string;
      new_cpc: number;
      new_daily_budget: number;
      original_cpc: number;
      moved_offers_count: number;
      removed_from_original: boolean;
      remove_error: string | null;
    }>(`/ads/campaigns/${encodeURIComponent(adgroupId)}/pareto-split`, payload),

  extensionLastSniff: () =>
    api.get<{
      cookies: { present: boolean; updated_at: string | null };
      campaigns: {
        total: number;
        active: number;
        blocked: number;
        last_synced_at: string | null;
        sources: Record<string, number>;
      };
    }>("/ads/extension/last-sniff"),

  extensionQueueStats: () =>
    api.get<{
      per_campaign: Record<string, {
        related_campaign_id: string;
        pending: number;
        done: number;
        failed: number;
        last_completed_at: string | null;
      }>;
      recent_completed: Array<{
        id: number;
        kind: string;
        status: "done" | "failed";
        related_campaign_id: string | null;
        completed_at: string;
        error: string | null;
      }>;
      totals: { pending: number; done: number; failed: number };
    }>("/ads/extension/queue-stats"),
  agentActivity: (campaignId: string) =>
    api.get<AdsAgentActivity>(
      `/ads/campaigns/${encodeURIComponent(campaignId)}/agent-activity`
    ),
  smartBiddingRunNow: () =>
    api.post<{
      ok: boolean;
      decisions_made: number;
      decisions_applied: number;
      decisions_skipped: number;
      errors: string[];
      details: Array<{
        campaign_id: string;
        campaign_name: string;
        current_cpc: number;
        new_cpc: number;
        current_roas: number;
        target_roas: number;
        delta_pct: number;
        reason: string;
        clicks_30d: number;
      }>;
    }>("/ads/smart-bidding/run-now"),
  rescanCampaign: (campaignId: string) =>
    api.post<{ success: boolean; case_id: number | null }>(
      `/ads/campaigns/${encodeURIComponent(campaignId)}/rescan`
    ),
  updateCampaignBrief: (campaignId: string, payload: { user_brief: string | null; rescan?: boolean }) =>
    api.put<{ success: boolean; case_id: number | null; user_brief: string | null }>(
      `/ads/campaigns/${encodeURIComponent(campaignId)}/brief`,
      payload
    ),
  campaignAction: (
    campaignId: string,
    payload: {
      kind: "pause" | "resume" | "change_cpc" | "change_budget";
      new_cpc?: number;
      new_daily_budget?: number;
    }
  ) =>
    api.post<{ ok: boolean; action_id: number; kind: string; note: string }>(
      `/ads/campaigns/${encodeURIComponent(campaignId)}/action`,
      payload
    ),
};

// AdsAgentActivity types
export interface AdsAgentRun {
  case_id: number;
  agent_key: string;
  model: string | null;
  cost_usd: number | null;
  tokens: number;
  latency_ms: number;
  created_at: string;
  summary: string | null;
  findings_count: number;
  suggested_actions_count: number;
  decision: string | null;
}

export interface AdsAgentCase {
  id: number;
  case_type: string;
  status: string;
  severity: string;
  summary: string | null;
  synthesis: Record<string, unknown> | null;
  signal_payload: Record<string, unknown> | null;
  opened_at: string;
  closed_at: string | null;
}

export interface AdsCampaignRecommendation {
  id: number;
  case_id: number | null;
  title: string;
  rationale: string | null;
  action_kind: string;
  risk_level: string;
  status: string;
  payload: Record<string, unknown> | null;
  created_at: string | null;
  implemented_at: string | null;
}

export interface AdsAgentActivity {
  meta: {
    campaign_id: string;
    name: string;
    status: string;
    channel: string;
    created_via: string;
    user_brief: string | null;
    ai_options: AdsAiOptions | null;
    kpi: AdsCampaignKpi | null;
    last_synced_at: string | null;
    created_at: string | null;
  };
  cases: AdsAgentCase[];
  agent_runs: AdsAgentRun[];
  pending_recommendations: AdsCampaignRecommendation[];
  implemented_recommendations: AdsCampaignRecommendation[];
  llm_cost_total_usd: number | null;
  llm_tokens_total: number;
  agents_used: string[];
}

// === Auth ===
export type AppProfile = {
  id: number;
  email: string;
  name: string | null;
  allegro_login: string | null;
  plan: string;
  created_at: string;
  last_login_at: string | null;
  is_active: boolean;
  is_admin: boolean;
};

export const authApi = {
  changePassword: (data: { current_password: string; new_password: string }) =>
    browserAuth.changePassword(data.current_password, data.new_password),
  me: () => api.get<AppProfile>("/auth/me"),
  logout: () => browserAuth.logout(),
  logoutAll: async () => {
    await refreshSessionOnce();
    return browserAuth.logoutAll();
  },

  // Allegro connect (osobny krok od logowania)
  allegroAuthorize: () =>
    api.get<{ auth_url: string; state: string }>("/auth/allegro/authorize"),
  allegroStatus: () =>
    api.get<{
      connected: boolean;
      allegro_login?: string;
      environment?: string;
      expires_at?: string;
    }>("/auth/allegro/status"),
  allegroDisconnect: () => api.delete("/auth/allegro/disconnect"),
};

// === Offers ===
export interface CalculatorState {
  mode: "forward" | "reverse";
  purchase_price: number;
  purchase_vat_deferred?: boolean;
  target_profit_per_unit: number | null;
  override_sale_price?: number | null;
  override_ads_cost_pct?: number | null;
  override_shipping_cost?: number | null;
  extra_packaging_cost?: number;
  extra_other_costs?: number;
}

export interface CalculatorAuto {
  sale_price: number;
  sale_price_source: string;
  ads_cost_pct: number;
  ads_cost_pct_source: string;
  shipping_cost: number;
  shipping_cost_source: string;
  commission_pct: number;
  commission_max: number | null;
  commission_source: string;
  is_vat_payer: boolean;
  tax_form: string;
  company_source: string;
  company_profile_filled: boolean;
}

export interface CalculatorMerged {
  purchase_price: number;
  sale_price: number;
  ads_cost_pct: number;
  shipping_cost: number;
  packaging_cost: number;
  other_costs: number;
}

export interface ProfitCalcResult {
  selling_price?: number;
  selling_net?: number;
  vat_from_sale?: number;
  purchase_price?: number;
  allegro_commission?: number;
  allegro_commission_percent?: number;
  ads_cost?: number;
  shipping_cost?: number;
  packaging_cost?: number;
  other_costs?: number;
  margin_gross?: number;
  margin_gross_percent?: number;
  gross_profit?: number;
  income_tax?: number;
  net_profit?: number;
  margin_net?: number;
  margin_net_percent?: number;
  roi_percent?: number;
  is_profitable?: boolean;
  break_even_price?: number;
  [key: string]: unknown;
}

export interface CalculatorComputeResponse {
  state: CalculatorState;
  auto: CalculatorAuto;
  merged: CalculatorMerged;
  result: ProfitCalcResult;
  reverse_price: number | null;
  target_met: boolean;
  delta_from_target: number | null;
}

export interface CalculatorLoadResponse {
  state: CalculatorState;
  auto: CalculatorAuto;
  last_calc: ProfitCalcResult | null;
}

export interface CalculatorAiAdvice {
  applicable: boolean;
  advice?: {
    diagnosis: string;
    levers: Array<{
      title: string;
      description: string;
      estimated_impact_per_unit: number;
      risk: "low" | "medium" | "high" | string;
    }>;
    warning?: string;
  } | null;
  competitors?: {
    n: number;
    median: number;
    min: number;
    max: number;
    p25: number | null;
    p75: number | null;
    top_3_cheapest: Array<{ title: string; price: number; seller: string }>;
  } | null;
  message?: string;
  error?: string;
  llm_cost_usd?: number | null;
  llm_tokens?: number;
}

export interface OfferCompetitorsResponse {
  own_price: number;
  summary: {
    n: number;
    median: number;
    min: number;
    max: number;
    p25: number | null;
    p75: number | null;
    top_3_cheapest: Array<{ title: string; price: number; seller: string }>;
  };
  voice: string;
  persona_code: string;
}

export const offersApi = {
  list: (params?: { limit?: number; offset?: number; status?: string }) =>
    api.get("/offers/", { params }),
  competitors: (offerId: string) =>
    api.get<OfferCompetitorsResponse>(`/offers/${offerId}/competitors`),
  sync: () =>
    api.post<{
      success: boolean;
      synced_count: number;
      total_in_db: number;
      with_image: number;
    }>("/offers/sync"),
  get: (id: string) => api.get(`/offers/${id}`),
  full: (id: string) => api.get(`/offers/${id}/full`),
  refreshQuality: (offerId: string) =>
    api.post<{
      allegro_offer_id: string;
      quality: {
        total: number;
        max: number;
        tier: "good" | "ok" | "bad";
        breakdown: Record<
          string,
          { score: number; max: number; hint: string; approximate: boolean }
        >;
      };
      cached_at: string;
    }>(`/offers/quality/refresh/${encodeURIComponent(offerId)}`),
  pushGeneratedImages: (
    offerId: string,
    payload: { image_ids: number[]; mode: "append" | "replace" },
  ) =>
    api.post<{
      success: boolean;
      offer_id: string;
      uploaded: number;
      total_requested: number;
      final_gallery_size: number;
      mode: "append" | "replace";
      errors: Array<{ image_id: number; error: string }>;
    }>(`/offers/${offerId}/push-generated-images`, payload),

  // HEAL (2026-06-23) — Healer Loop endpoints
  rescueStatus: (offerId: string) =>
    api.get<{
      has_rescue: boolean;
      case?: {
        id: number;
        status: string;
        severity: string;
        opened_at: string;
        summary: string;
        campaign_id: string | null;
        signal_payload: {
          allegro_offer_id?: string;
          offer_title?: string;
          source_adgroup_id?: string;
          exclude_reason?: string;
          excluded_at?: string;
        };
        synthesis: Record<string, unknown>;
      };
      recommendations?: Array<{
        id: number;
        title: string;
        rationale: string;
        action_kind: string;
        payload: Record<string, unknown>;
        proposed_changes: Record<string, unknown>;
        risk_level: "low" | "medium" | "high";
        status: string;
        executed_at: string | null;
      }>;
      last_closed?: {
        id: number;
        status: string;
        opened_at: string;
        closed_at: string;
        resolution_kind: string;
        summary: string;
      } | null;
    }>(`/offers/${encodeURIComponent(offerId)}/rescue`),

  startRecovery: (offerId: string) =>
    api.post<{
      success: boolean;
      campaign_id: string;
      action: "created" | "added";
      ends_at: string;
      title?: string;
    }>(`/offers/${encodeURIComponent(offerId)}/rescue/start-recovery`),

  rescueList: () =>
    api.get<{
      count: number;
      items: Array<{
        case_id: number;
        allegro_offer_id: string;
        offer_id: number;
        title: string;
        price: string | null;
        currency: string | null;
        severity: string;
        summary: string;
        opened_at: string;
      }>;
    }>("/offers/rescue/list"),
  getCalculator: (id: string) =>
    api.get<CalculatorLoadResponse>(`/offers/${id}/calculator`),
  computeCalculator: (id: string, state: CalculatorState) =>
    api.post<CalculatorComputeResponse>(`/offers/${id}/calculator`, state),
  calculatorAiAdvice: (
    id: string,
    payload: { state: CalculatorState; last_result: ProfitCalcResult }
  ) =>
    api.post<CalculatorAiAdvice>(`/offers/${id}/calculator/ai-advice`, payload),
  stats: (id: string, days = 30) =>
    api.get<OfferStatsResponse>(`/offers/${id}/stats`, { params: { days } }),
  forecast: (id: string, days = 30, project = 14) =>
    api.get<OfferForecastResponse>(`/offers/${id}/forecast`, {
      params: { days, project },
    }),
  checkDegradation: (id: string) =>
    api.post<OfferDegradationResponse>(`/offers/${id}/check-degradation`),
  cases: (id: string) =>
    api.get<{ cases: OfferCaseRow[] }>(`/offers/${id}/cases`),
  auditNow: (id: string) =>
    api.post<{
      success: boolean;
      case_id: number;
      case_url: string;
      agent_runs_count: number;
      recommendations_count: number;
      auto_approved_count: number;
    }>(`/offers/${id}/audit-now`),
  uploadImage: (id: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api.post<{
      success: boolean;
      url: string;
      filename: string;
      size_bytes: number;
    }>(`/offers/${id}/upload-image`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120_000,
    });
  },
  patch: (
    id: string,
    payload: {
      title?: string;
      description?: { sections: Array<unknown> };
      parameters?: Array<{
        id?: string;
        name?: string;
        values?: string[];
        rangeValue?: unknown;
        valuesIds?: string[];
      }>;
      images?: string[];
      /** ID nowej kategorii Allegro — wymusza zmianę kategorii oferty. */
      category_id?: string;
      // === Etap 1: Sprzedaż ===
      price?: {
        amount: number | string;
        currency?: string;
        format?: "BUY_NOW" | "AUCTION" | "ADVERTISEMENT";
        startingPrice?: { amount: number | string; currency?: string };
        minimalPrice?: { amount: number | string; currency?: string };
        netPrice?: { amount: number | string; currency?: string };
      };
      stock?: { available?: number; unit?: string };
      publication?: {
        status?: "ACTIVE" | "INACTIVE" | "ENDED";
        duration?: string; // ISO-8601 np. "PT72H", "P30D"
        republish?: boolean;
        startingAt?: string; // ISO-8601 datetime
      };
      external_id?: string;
      // === Etap 2: Dostawa, płatność, obsługa posprzedażowa ===
      delivery?: {
        shippingRates?: { id: string } | string;
        handlingTime?: string;
        additionalInfo?: string;
        shipmentDate?: string;
      };
      location?: {
        countryCode?: string;
        province?: string;
        city?: string;
        postCode?: string;
      };
      payments?: {
        invoice?: "VAT" | "VAT_MARGIN" | "WITHOUT_VAT" | "NO_INVOICE";
      };
      tax?: {
        id?: string;
        rate?: string;
        subject?: string;
        exemption?: string;
      };
      after_sales_services?: {
        returnPolicy?: { id: string } | null;
        impliedWarranty?: { id: string } | null;
        warranty?: { id: string } | null;
      };
      // === Etap 3: GPSR + Załączniki ===
      responsible_producer_id?: string;
      responsible_person_id?: string;
      safety_information?: {
        type: "TEXT" | "ATTACHMENTS";
        text?: string;
        attachments?: Array<{ id: string }>;
      };
      attachments?: Array<{ id: string; type?: string }>;
      // === Etap 4: Multi-rynek + B2B + język ===
      language?: string;
      additional_marketplaces?: Record<
        string,
        {
          publication?: { status?: "ACTIVE" | "INACTIVE" | "ENDED" };
          sellingMode?: { price?: { amount: number | string; currency?: string } };
        }
      >;
      b2b?: { buyableOnlyByBusiness?: boolean };
      // === Etap 5: Promocje i automatyzacja ===
      discounts?: { wholesalePriceList?: { id: string } | null };
      additional_services?: { id?: string | null };
      // === Etap 6: Specjalne + UX ===
      size_table?: { id?: string | null };
      compatibility_list?: {
        type?: "MANUAL" | "PRODUCT_BASED";
        id?: string;
        items?: Array<{ text?: string; id?: string }>;
      };
      fundraising_campaign?: { id?: string | null };
      message_to_seller_settings?: {
        mode?: "OPTIONAL" | "REQUIRED" | "OFF";
        hint?: string;
      };
    }
  ) =>
    api.patch<{
      success: boolean;
      offer_id: string;
      updated_fields: string[];
      allegro_response: Record<string, unknown>;
    }>(`/offers/${id}`, payload),
  // === Sale options (dropdowny dla edytora) ===
  // KO6 (2026-08-07): shopId param → backend zwraca opcje z konkretnego shopa.
  // Bez tego endpointy uzywaja X-Shop-Id z localStorage (moze nie pasowac
  // do wybranego target w wizardzie kopiowania).
  shippingRates: (shopId?: number | null) =>
    api.get<{ shippingRates?: Array<{ id: string; name: string; marketplace?: { id?: string } }> }>(
      `/offers/options/shipping-rates`,
      shopId ? { headers: { "X-Shop-Id": String(shopId) } } : undefined,
    ),
  returnPolicies: (shopId?: number | null) =>
    api.get<{ returnPolicies?: Array<{ id: string; name: string }> }>(
      `/offers/options/return-policies`,
      shopId ? { headers: { "X-Shop-Id": String(shopId) } } : undefined,
    ),
  impliedWarranties: (shopId?: number | null) =>
    api.get<{ impliedWarranties?: Array<{ id: string; name: string }> }>(
      `/offers/options/implied-warranties`,
      shopId ? { headers: { "X-Shop-Id": String(shopId) } } : undefined,
    ),
  warranties: (shopId?: number | null) =>
    api.get<{ warranties?: Array<{ id: string; name: string }> }>(
      `/offers/options/warranties`,
      shopId ? { headers: { "X-Shop-Id": String(shopId) } } : undefined,
    ),
  taxSettings: (categoryId: string, shopId?: number | null) =>
    api.get<{
      taxSettings?: Array<{
        id?: string;
        rates?: Array<{ rate: string }>;
        subject?: string;
        exemptions?: Array<{ value: string; description?: string }>;
      }>;
    }>(`/offers/options/tax-settings`, {
      params: { category_id: categoryId },
      ...(shopId ? { headers: { "X-Shop-Id": String(shopId) } } : {}),
    }),
  // === Etap 3: GPSR ===
  responsibleProducers: (shopId?: number | null) =>
    api.get<{
      responsibleProducers?: Array<{ id: string; name: string }>;
    }>(
      `/offers/options/responsible-producers`,
      shopId ? { headers: { "X-Shop-Id": String(shopId) } } : undefined,
    ),
  responsiblePersons: (shopId?: number | null) =>
    api.get<{
      responsiblePersons?: Array<{ id: string; name: string }>;
    }>(
      `/offers/options/responsible-persons`,
      shopId ? { headers: { "X-Shop-Id": String(shopId) } } : undefined,
    ),
  /**
   * Upload PDF do Allegro CDN. Akceptuje File z dysku LUB Blob (np. wygenerowany
   * lokalnie PDF kartę produktu). Filename opcjonalny gdy Blob bez nazwy.
   * Zwraca {success, id, type, name} — id idzie potem do PATCH offer.attachments[].
   */
  uploadAttachment: (
    id: string,
    file: File | Blob,
    attachmentType: string,
    filename?: string
  ) => {
    const form = new FormData();
    const f =
      file instanceof File
        ? file
        : new File([file], filename || "karta-produktu.pdf", {
            type: "application/pdf",
          });
    form.append("file", f);
    return api.post<{
      success: boolean;
      id: string;
      type: string;
      name: string;
      size_bytes: number;
    }>(`/offers/${id}/upload-attachment`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      params: { attachment_type: attachmentType },
      timeout: 180_000,
    });
  },
  /**
   * Generuje PDF kartę produktu i zwraca BINARKĘ (blob).
   * Nie wgrywa do Allegro — frontend trzyma jako draft attachment, upload
   * dzieje się przy zapisie oferty przez `uploadAttachment`.
   */
  generateCardPdf: (offer_id: string) =>
    api.post<Blob>(
      `/offers/${encodeURIComponent(offer_id)}/generate-card-pdf`,
      null,
      { responseType: "blob" }
    ),
  generateAttachmentPdf: (
    id: string,
    attachmentType: string = "PRODUCT_INFORMATION_SHEET"
  ) =>
    api.post<{
      success: boolean;
      id: string;
      type: string;
      name: string;
      size_bytes: number;
      ai_tokens?: number;
      ai_cost_usd?: number | null;
    }>(`/offers/${id}/generate-attachment-pdf`, null, {
      params: { attachment_type: attachmentType },
      timeout: 180_000,
    }),
  // === Etap 4: Multi-rynek + Tłumaczenia ===
  marketplaces: () =>
    api.get<{
      marketplaces?: Array<{
        id: string;
        name?: string;
        currency?: { code?: string };
        country?: { code?: string };
      }>;
    }>(`/offers/options/marketplaces`),
  getTranslations: (id: string) =>
    api.get<{
      translations?: Array<{
        language: string;
        // Allegro może zwrócić `title` jako string LUB obiekt {translation, type}.
        title?: string | { translation?: string; type?: string };
        description?: { sections?: Array<unknown> };
        parameters?: Array<unknown>;
      }>;
    }>(`/offers/${id}/translations`),
  patchTranslation: (
    id: string,
    language: string,
    body: {
      title?: string;
      description?: { sections: Array<unknown> };
      parameters?: Array<{ id?: string; name?: string; values?: string[] }>;
    }
  ) => api.patch(`/offers/${id}/translations/${language}`, body),
  deleteTranslation: (id: string, language: string) =>
    api.delete(`/offers/${id}/translations/${language}`),
  // === Etap 5: Promocje i automatyzacja ===
  promotionPackages: () =>
    api.get<{
      packages?: Array<{ id: string; name: string; description?: string }>;
    }>(`/offers/options/promotion-packages`),
  badgeCampaigns: () =>
    api.get<{
      campaigns?: Array<{
        id: string;
        name: string;
        type?: string;
        applicationStartTime?: string;
        applicationEndTime?: string;
      }>;
    }>(`/offers/options/badge-campaigns`),
  loyaltyPromotions: () =>
    api.get<{
      promotions?: Array<{ id: string; name: string }>;
    }>(`/offers/options/loyalty-promotions`),
  additionalServicesGroups: () =>
    api.get<{
      groups?: Array<{ id: string; name: string }>;
    }>(`/offers/options/additional-services`),
  priceAutomationRules: () =>
    api.get<{
      rules?: Array<{ id: string; name: string; strategy?: string }>;
    }>(`/offers/options/price-automation-rules`),
  // === Per-offer GET-y ===
  getOfferPromoOptions: (id: string) =>
    api.get<{
      package?: { id?: string; name?: string };
      campaigns?: Array<{ id?: string; name?: string }>;
    }>(`/offers/${id}/promo-options`),
  getOfferBadges: (id: string) =>
    api.get<{
      badges?: Array<{ campaign?: { id?: string; name?: string }; status?: string }>;
    }>(`/offers/${id}/badges`),
  getAllegroPricesConsent: (id: string) =>
    api.get<{ eligible?: boolean; reason?: string }>(
      `/offers/${id}/allegro-prices-consent`
    ),
  putAllegroPricesConsent: (id: string, eligible: boolean) =>
    api.put<{ eligible?: boolean }>(`/offers/${id}/allegro-prices-consent`, {
      eligible,
    }),
  getOfferPriceAutomation: (id: string) =>
    api.get<{
      rules?: Array<{ id?: string; name?: string; strategy?: string }>;
    }>(`/offers/${id}/price-automation`),
  // === Etap 6: Specjalne + UX ===
  sizeTables: () =>
    api.get<{
      sizeTables?: Array<{ id: string; name: string }>;
    }>(`/offers/options/size-tables`),
  compatibilityCategories: () =>
    api.get<{
      categories?: Array<{ id: string; name: string }>;
    }>(`/offers/options/compatibility-categories`),
  searchCompatibleProducts: (type: string, phrase: string) =>
    api.get<{
      compatibleProducts?: Array<{ id: string; text?: string; name?: string }>;
    }>(`/offers/options/compatible-products`, { params: { type, phrase } }),
  charityCampaigns: (phrase: string = "") =>
    api.get<{
      fundraisingCampaigns?: Array<{
        id: string;
        name: string;
        description?: string;
      }>;
    }>(`/offers/options/charity-campaigns`, { params: { phrase } }),
  offerTags: () =>
    api.get<{
      tags?: Array<{ id: string; name: string }>;
    }>(`/offers/options/offer-tags`),
  getOfferAssignedTags: (id: string) =>
    api.get<{
      tags?: Array<{ id: string; name: string }>;
    }>(`/offers/${id}/tags`),
  putOfferTags: (id: string, tagIds: string[]) =>
    api.put<{ success?: boolean }>(`/offers/${id}/tags`, { tag_ids: tagIds }),
  getOfferSmart: (id: string) =>
    api.get<{
      status?: string;
      eligibility?: { eligible?: boolean };
      criteria?: Array<{ name?: string; status?: string }>;
    }>(`/offers/${id}/smart`),
};

export interface OfferForecastResponse {
  history: Array<{ date: string; units: number }>;
  projection: Array<{ date: string; predicted_units: number }>;
  trend: {
    slope: number;
    intercept: number;
    r_squared: number;
    label: "growing" | "stable" | "declining" | "no_data" | string;
    severity: "info" | "warning" | "critical" | string;
    relative_change_per_day: number;
  };
  summary: {
    avg_daily: number;
    history_total: number;
    projected_total: number;
    projected_days: number;
    history_days: number;
  };
}

export interface OfferDegradationResponse {
  degradation_detected: boolean;
  case_id?: number;
  case_created?: boolean;
  severity?: "warning" | "critical" | string;
  summary?: string;
  ratio?: number;
  last_7_avg?: number;
  prev_30_avg?: number;
  reason?: string;
  message?: string;
}

export interface OfferCaseRow {
  id: number;
  case_type: string;
  status: string;
  severity: string;
  opened_by: string;
  opened_at: string;
  closed_at: string | null;
  summary: string | null;
}

export interface OfferKpi {
  value: number;
  delta?: number | null;
  delta_pct?: number | null;
  trend?: "up" | "down" | "flat";
}

export interface OfferRecentOrder {
  order_id: string;
  allegro_order_id: string | null;
  created_at: string;
  status: string;
  buyer_login: string | null;
  units: number;
  amount: number;
}

export interface OfferStatsResponse {
  period: { days: number; start: string; end: string };
  kpis: {
    units_sold: OfferKpi;
    revenue: OfferKpi;
    avg_price: OfferKpi;
    orders_count: OfferKpi;
  };
  daily_sales: Array<{ date: string; units: number }>;
  recent_orders: OfferRecentOrder[];
  competitors: {
    n: number;
    median: number;
    min: number;
    max: number;
    p25: number | null;
    p75: number | null;
    top_3_cheapest: Array<{ title: string; price: number; seller: string }>;
  } | null;
  offer: {
    title: string | null;
    current_price: number | null;
    stock_sold_total: number | null;
  };
}

// === Audit ===
export const auditApi = {
  latest: () => api.get("/audit/latest"),
  findings: (severity?: string) =>
    api.get("/audit/findings", { params: { severity: severity || "all" } }),
  run: () => api.post("/audit/run"),
};

// === Audit v2 (YAML rules + Allegro) ===
export type AuditV2Severity = "critical" | "high" | "medium" | "low" | "info";
export type AuditV2Status =
  | "pass"
  | "warning"
  | "fail"
  | "manual_required"
  | "not_applicable"
  | "error";
export type AuditV2Scope = "account" | "offer" | "full";
export type AuditV2HealthLevel = "critical" | "warning" | "ok" | "excellent";

// Sprint TR (2026-06-19): blocker classification dla manual_required.
// Backend kategoryzuje dlaczego dana reguła nie została sprawdzona — frontend pokazuje
// badge z opisem zamiast generycznego "❓".
export type AuditV2BlockerType =
  | "needs_re_oauth"
  | "needs_oauth_scope"
  | "needs_partner"
  | "needs_extension"
  | "not_in_allegro_api"
  | "needs_external"
  | "needs_user_input"
  | "needs_data_sync"
  | "needs_brand_setup"
  | "needs_marketplace_expansion";

export interface AuditV2Blocker {
  type: AuditV2BlockerType;
  label: string;
  description: string;
  action_hint?: string;
  severity: "info" | "warning";
}

export interface AuditV2RuleResult {
  rule_id: string;
  rule_name: string;
  section: string;
  scope: AuditV2Scope;
  status: AuditV2Status;
  severity: AuditV2Severity;
  score_impact: number;
  evidence: Record<string, unknown>;
  recommendation: string;
  rewrite_suggestion: string | null;
  rag_reference: string | null;
  rag_context: string | null;
  auto_fix_available: boolean;
  confidence: number | null;
  executed_at: string | null;
  execution_time_ms: number | null;
  // Linki do automatyzacji rekomendacji
  recommendation_id?: number | null;
  recommendation_status?: RecommendationStatus | null;
  recommendation_action_kind?: string | null;
  executor_available?: boolean;
  proposer_available?: boolean;
  proposed_changes?: Record<string, unknown> | null;
  execution_result?: Record<string, unknown> | null;
  rollback_available?: boolean;
  execution_error?: string | null;
  manual_required?: boolean;
}

export interface AuditV2PriorityAction {
  rule_id: string;
  rule_name?: string;
  severity: AuditV2Severity;
  score_impact: number;
  recommendation: string;
  auto_fix_available: boolean;
  rag_reference: string | null;
}

export interface AuditV2Summary {
  total_score: number;
  max_score: number;
  score_pct: number;
  health_level: AuditV2HealthLevel;
  counts: Record<string, number>;
  critical_issues_count: number;
  auto_fix_available_count: number;
}

export interface AuditV2Report {
  report_id: string;
  client_id: number;
  audit_type: AuditV2Scope;
  offer_id: number | null;
  executed_at: string;
  execution_duration_ms: number;
  target: Record<string, unknown>;
  summary: AuditV2Summary;
  priority_actions: AuditV2PriorityAction[];
  diagnostic_patterns: unknown[];
  metadata: Record<string, unknown>;
  results: AuditV2RuleResult[];
}

export interface AuditV2RunResponse {
  report_id: string;
  summary: AuditV2Summary & { bonus_points?: number; raw_penalty?: number };
  priority_actions_count: number;
  rules_executed: number;
  duration_ms: number;
}

export const auditV2Api = {
  runAccount: (seed = false) =>
    api.post<AuditV2RunResponse>(`/audit/v2/account/run`, null, {
      params: { seed },
    }),
  runOffer: (allegroOfferId: string, seed = false) =>
    api.post<AuditV2RunResponse>(
      `/audit/v2/offer/${allegroOfferId}/run`,
      null,
      { params: { seed } }
    ),
  latestReport: (audit_type?: AuditV2Scope) =>
    api.get<{ report: AuditV2Report | null; message?: string }>(
      "/audit/v2/reports/latest",
      { params: { audit_type } }
    ),
  getReport: (reportId: string) =>
    api.get<AuditV2Report>(`/audit/v2/reports/${reportId}`),
  reseed: () => api.post("/audit/v2/rules/reseed"),
  listRules: (scope?: AuditV2Scope, severity?: AuditV2Severity) =>
    api.get("/audit/v2/rules", { params: { scope, severity } }),
};

// === Categories ===
export interface CategoryMetric {
  category_id: string;
  commission: null | {
    fee_amount: number;
    rate_percent: number;
    sample_price: number;
    currency: string;
    sample_source: "median" | "user" | "default" | null;
  };
  competition: null | {
    total_count: number | null;
    median_price: number | null;
    sample_size: number;
    currency: string;
  };
  errors: string[];
  fetched_at: string;
}

export const categoriesApi = {
  tree: (parent_id?: string) =>
    api.get("/categories/", { params: { parent_id } }),
  match: (name: string) => api.get("/categories/match", { params: { name } }),
  parameters: (id: string) => api.get(`/categories/${id}/parameters`),
  path: (id: string) => api.get(`/categories/${id}/path`),
  metrics: (
    category_ids: string[],
    phrase: string,
    options?: { sample_price?: number | null; force_refresh?: boolean }
  ) =>
    api.post<{
      phrase: string;
      phrase_hash: string;
      metrics: CategoryMetric[];
      cache_misses: number;
    }>("/categories/metrics", {
      category_ids,
      phrase,
      sample_price: options?.sample_price ?? null,
      force_refresh: options?.force_refresh ?? false,
    }),
};

// === Products / Create Mode ===
export const productsApi = {
  lookup: (ean: string) => api.get("/products/lookup", { params: { ean } }),
  search: (phrase: string, limit = 10) =>
    api.get("/products/search", { params: { phrase, limit } }),
  suggestCategory: (product_name: string) =>
    api.get("/products/suggest-category", { params: { product_name } }),
};

// === Templates ===
export interface TemplateSection {
  type: string;
  section_name: string;
  image_slots: number[];
  ai_prompt?: string;
  content_template?: string;
  content_type?: string;
}

export interface TemplatePreview {
  template_id: number;
  name: string;
  description: string | null;
  is_system: boolean;
  dynamic: boolean;
  image_count: number;
  image_slots_used: number;
  sections: TemplateSection[];
  blueprint: unknown;
}

export interface TemplateSectionInput {
  // 2026-07-24: 5 typów jak vSprint (`mixed` zostaje dla back-compat = image_left_text_right)
  // 2026-07-27: dodane `two_texts` (2 kolumny tekstu — Allegro supported layout)
  type:
    | "text"
    | "image"
    | "mixed"
    | "image_left_text_right"
    | "text_left_image_right"
    | "two_images"
    | "two_texts";
  name?: string;
  content_type?: string;
  instructions?: string;
  heading_level?: number;
  image_slots?: unknown[];
}

export const templatesApi = {
  system: () => api.get("/templates/system"),
  user: () => api.get("/templates/"),
  get: (id: number) => api.get(`/templates/${id}`),
  preview: (id: number, image_count: number) =>
    api.get<TemplatePreview>(`/templates/${id}/preview`, {
      params: { image_count },
    }),

  // Faza 4 (2026-07-24) — CRUD własnych szablonów
  create: (payload: {
    name: string;
    description?: string;
    ai_instructions?: string;
    sections: TemplateSectionInput[];
  }) => api.post<{ template_id: number; name: string }>("/templates/", payload),

  update: (
    id: number,
    payload: Partial<{
      name: string;
      description: string;
      ai_instructions: string;
      sections: TemplateSectionInput[];
    }>,
  ) =>
    api.put<{ template_id: number; updated: boolean }>(
      `/templates/${id}`,
      payload,
    ),

  duplicate: (id: number) =>
    api.post<{ template_id: number; source_id: number }>(
      `/templates/${id}/duplicate`,
    ),

  delete: (id: number) =>
    api.delete<{ deleted: boolean }>(`/templates/${id}`),

  // Sprint D (2026-07-24) — import struktury z istniejącej oferty Allegro
  importFromAllegro: (allegroOfferId: string) =>
    api.post<{
      sections: TemplateSectionInput[];
      source_offer: { allegro_offer_id: string; name: string };
      n_sections: number;
    }>(`/templates/import-from-allegro/${allegroOfferId}`),
};

// === Drafts (CREATE MODE) ===
// EAN-first flow (Porcja #2 sprintu kreator-ean-gate)
export interface LookupEanExistingOffer {
  id: number;
  title: string;
  image_url: string | null;
}

export interface LookupEanResponse {
  ean: string;
  found_in_catalog: boolean;
  catalog_data: Record<string, unknown> | null;
  catalog_candidates: Record<string, unknown>[];
  my_existing_offers: LookupEanExistingOffer[];
}

export const draftsApi = {
  list: () => api.get("/drafts/"),
  create: (
    product_name: string,
    ean?: string | null,
    short_description?: string | null
  ) => api.post("/drafts/", { product_name, ean, short_description }),
  get: (id: number) => api.get(`/drafts/${id}`),
  update: (id: number, fields: Record<string, unknown>) =>
    api.patch(`/drafts/${id}`, { fields }),
  delete: (id: number) => api.delete(`/drafts/${id}`),
  publish: (id: number) => api.post(`/drafts/${id}/publish`),
  lookupEan: (ean: string) =>
    api.post<LookupEanResponse>("/drafts/lookup-ean", { ean }),
};

// === Image Folders — biblioteka zdjęć (R2 sprint) ===
export interface ImageFolder {
  id: number;
  name: string;
  description: string | null;
  match_ean: string | null;
  match_sku: string | null;
  match_product_name: string | null;
  is_auto: boolean;
  is_system: boolean;
  cover_image_id: number | null;
  sort_order: number;
  image_count: number;
  created_at: string;
  updated_at: string;
}

export interface ImageFoldersListResponse {
  folders: ImageFolder[];
  uncategorized_count: number;
}

export interface LibraryImage {
  id: number;
  filename: string | null;
  width: number | null;
  height: number | null;
  file_size: number | null;
  mime_type: string | null;
  ean: string | null;
  sku: string | null;
  product_name: string | null;
  created_at: string;
  // Sprint IV/1: AI Act — flaga i metadane grafik AI (Agata)
  is_ai_generated?: boolean;
  ai_source?: string | null;
  ai_model?: string | null;
  ai_labeled_at?: string | null;
}

export interface FolderImagesResponse {
  folder_id: number;
  total: number;
  limit: number;
  offset: number;
  images: LibraryImage[];
}

export const imageFoldersApi = {
  list: () => api.get<ImageFoldersListResponse>("/image-folders"),
  create: (payload: {
    name: string;
    description?: string;
    match_ean?: string;
    match_sku?: string;
    match_product_name?: string;
    is_auto?: boolean;
  }) => api.post<ImageFolder>("/image-folders", payload),
  update: (
    id: number,
    payload: Partial<{
      name: string;
      description: string | null;
      match_ean: string | null;
      match_sku: string | null;
      match_product_name: string | null;
      is_auto: boolean;
      cover_image_id: number | null;
      sort_order: number;
    }>,
  ) => api.patch<ImageFolder>(`/image-folders/${id}`, payload),
  delete: (id: number) => api.delete(`/image-folders/${id}`),
  images: (id: number, params?: { limit?: number; offset?: number }) =>
    api.get<FolderImagesResponse>(`/image-folders/${id}/images`, { params }),
  uncategorizedImages: (params?: { limit?: number; offset?: number }) =>
    api.get<FolderImagesResponse>(`/image-folders/0/images`, { params }),
  moveImages: (image_ids: number[], folder_id: number | null) =>
    api.post<{ moved: number; folder_id: number | null }>(
      "/image-folders/_move-images",
      { image_ids, folder_id },
    ),
};

// === Images (Create Mode) ===
export const imagesApi = {
  upload: (formData: FormData) =>
    api.post("/images/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  importFromUrls: (draftId: number, urls: string[]) =>
    api.post<{
      success: boolean;
      imported: number;
      total: number;
      errors: Array<{ url: string; error: string }>;
    }>("/images/import-from-urls", { draft_id: draftId, urls }),
  draftImages: (draftId: number) => api.get(`/images/draft/${draftId}`),
  copyFromLibrary: (image_ids: number[], draft_id: number) =>
    api.post<{
      success: boolean;
      copied: number;
      new_image_ids: number[];
      draft_id: number;
    }>("/images/copy-from-library", { image_ids, draft_id }),
  sessionImages: (sessionId: string) =>
    api.get(`/images/session/${sessionId}`),
  importFromOffer: (sessionId: string, offer_id: string) =>
    api.post<{
      success: boolean;
      offer_id: string;
      imported: number;
      total: number;
      source: string;
      errors: Array<{ url: string; error: string }>;
    }>(`/images/session/${sessionId}/import-from-offer`, { offer_id }),
  reorderDraftImages: (draftId: number, ordered_ids: number[]) =>
    api.post(`/images/draft/${draftId}/reorder`, { ordered_ids }),
  recommendations: (currentCount: number) =>
    api.get("/images/recommendations", { params: { current_count: currentCount } }),
  delete: (id: number) => api.delete(`/images/${id}`),
  fileBlob: async (
    id: number,
    variant: "thumb" | "original" = "thumb"
  ): Promise<string> => {
    const res = await api.get<Blob>(`/images/${id}/file`, {
      params: { variant },
      responseType: "blob",
    });
    return URL.createObjectURL(res.data);
  },
  aiProcess: (
    id: number,
    payload: {
      mode: "white_bg" | "lifestyle";
      scene?: string;
      custom_prompt?: string;
      style_reference_image_id?: number | null;
      apply_brand_frame?: boolean | null;
      apply_palette_anchor?: boolean | null;
    }
  ) =>
    api.post<{
      image_id: number;
      processing_mode: string;
      source_image_id: number;
      ai_cost_usd: number;
      width: number;
      height: number;
    }>(`/images/${id}/ai-process`, payload),
  aiScenes: (category_path?: string) =>
    api.get<{
      scenes: Array<{
        id: string;
        label: string;
        description: string;
        recommended?: boolean;
      }>;
      recommended: string[];
      category_path?: string | null;
    }>("/images/ai-process/scenes", {
      params: category_path ? { category_path } : undefined,
    }),
  polishScene: (user_idea: string) =>
    api.post<{
      polished_prompt: string;
      polish_cost_usd: number;
      model: string;
    }>("/images/ai-process/polish-scene", { user_idea }),
  getSessionBrief: (sessionId: string) =>
    api.get<{
      brief: null | {
        title?: string;
        category?: string;
        description?: string;
        features?: string;
      };
      analysis: null | {
        product_class?: string;
        size_class?: string;
        allowed_scenes?: Array<{ label: string; prompt: string }>;
        forbidden_scenes?: string[];
        detail_shots?: Array<{ label: string; focus: string }>;
        lighting_mood?: string;
        reasoning?: string;
      };
      last_analyzed_at: string | null;
    }>(`/images/session/${sessionId}/brief`),
  putSessionBrief: (
    sessionId: string,
    brief: {
      title?: string;
      category?: string;
      description?: string;
      features?: string;
    }
  ) => api.put(`/images/session/${sessionId}/brief`, brief),
  analyzeBrief: (
    sessionId: string,
    brief: {
      title?: string;
      category?: string;
      description?: string;
      features?: string;
    }
  ) =>
    api.post<{
      success: boolean;
      analysis: {
        product_class?: string;
        size_class?: string;
        allowed_scenes?: Array<{ label: string; prompt: string }>;
        forbidden_scenes?: string[];
        detail_shots?: Array<{ label: string; focus: string }>;
        lighting_mood?: string;
        reasoning?: string;
      };
      cost_usd: number;
    }>(`/images/session/${sessionId}/analyze-brief`, brief),
  smartGallery: (
    sessionId: string,
    brief: {
      title?: string;
      category?: string;
      description?: string;
      features?: string;
      target_count?: number;
    }
  ) =>
    api.post<{
      success: boolean;
      generated: number;
      requested: number;
      total_cost_usd: number;
      errors: string[];
      analysis: unknown;
    }>(`/images/session/${sessionId}/smart-gallery`, brief),
  thumbnailLayouts: () =>
    api.get<{
      layouts: Array<{
        id: string;
        label: string;
        description: string;
        min_images: number;
        ideal_images: number;
      }>;
    }>("/images/thumbnail-composite/layouts"),
  thumbnailComposite: (
    sessionId: string,
    payload: {
      layout_id: string;
      variant_count?: number;
      props_text?: string;
      user_notes?: string;
      image_ids?: number[];
      visual_companions?: Array<{ label?: string; prompt_en?: string }>;
      surface_effects?: string[];
      forbidden_companions?: string[];
    }
  ) =>
    api.post<{
      success: boolean;
      requested: number;
      generated: number;
      winner_image_id: number | null;
      total_cost_usd: number;
      variants: Array<{
        success: boolean;
        rank?: number;
        image?: {
          image_id: number;
          compliance?: { score?: number; summary?: string };
        };
        quality?: {
          overall?: number;
          frame_fill_score?: number;
          composition_score?: number;
          hallucination_risk?: number;
          white_bg_purity?: number;
          sharpness?: number;
          issues?: string[];
        };
        error?: string;
      }>;
    }>(`/images/session/${sessionId}/thumbnail-composite`, payload),
  thumbnailAllegroTemplates: () =>
    api.get<{
      templates: Array<{
        id: string;
        label: string;
        ref: string;
        description: string;
        type: "A" | "B";
        total_shots: number;
        style_class: string[];
        extras: string[];
        min_variants: number;
        ideal_variants: number;
        preview_url: string;
      }>;
    }>("/images/thumbnail-allegro/templates"),
  thumbnailAllegro: (
    sessionId: string,
    payload: {
      variant_count?: number;
      apply_light_unify?: boolean;
      image_ids?: number[];
      seed?: number;
      template_id?: string;
      total_shots?: number;
      style_class?: string;
    }
  ) =>
    api.post<{
      success: boolean;
      template_id: string;
      bg_category: string;
      light_unified: boolean;
      total_cost_usd: number;
      profile?: {
        thermal: string;
        is_liquid: boolean;
        emits_light: boolean;
        key_details: string[];
        dominant_color: number[];
        aspect: string;
        style: string;
        bg_categories: string[];
        action_modes: string[];
      };
      variants_generated: Array<{ kind: string; label: string }>;
      image: {
        image_id: number;
        compliance?: { score?: number; summary?: string };
      };
    }>(`/images/session/${sessionId}/thumbnail-allegro`, payload),
  // --- Mockup Engine MVP (Sesja M) ---
  thumbnailMockupCatalog: () =>
    api.get<{
      mockups: Array<{
        id: string;
        label: string;
        description: string;
        category_tags: string[];
        style: "clean" | "dramatic" | "lifestyle" | "tech";
        min_photos: number;
        ideal_photos: number;
        slot_count: number;
        preview_url: string | null;
      }>;
    }>("/images/thumbnail-mockup/mockups"),
  thumbnailMockupEffects: () =>
    api.get<{
      effects: Array<{
        id: string;
        label: string;
        blend_mode: string;
        default_opacity: number;
        category_compat: string[];
        masked_to_bbox: boolean;
      }>;
    }>("/images/thumbnail-mockup/effects"),
  validateThumbnail: (
    imageId: number,
    payload: { category?: string; include_ai?: boolean } = {},
  ) =>
    api.post<{
      verdict: "pass" | "warning" | "fail";
      overall_score: number;
      checks: Array<{
        group: "A" | "B" | "C";
        rule: string;
        status: "pass" | "warn" | "fail";
        message: string;
        details?: Record<string, unknown>;
      }>;
      meta: {
        image_id: number;
        category?: string | null;
        white_bg_required: boolean;
      };
      ai_compliance?: unknown;
      ai_compliance_error?: string;
    }>(`/images/${imageId}/validate-thumbnail`, payload),
  thumbnailMockup: (
    sessionId: string,
    payload: {
      image_ids?: number[];
      category_hint?: string;
      mockup_id?: string;
      effect_id?: string;
      apply_suggested_effect?: boolean;
      hero_image_id?: number;
    }
  ) =>
    api.post<{
      success: boolean;
      mockup_id: string;
      category: string;
      ranking: Array<{ mockup_id: string; score: number; manual?: boolean; fallback?: boolean }>;
      assignments: Record<string, string>;
      vision_result?: {
        category: string;
        category_confidence: number;
        product_features: {
          emits_light: boolean;
          thermal: string;
          has_display: boolean;
          has_accessories_visible: boolean;
        };
        suggested_effect_id?: string;
        photos: Array<{
          index: number;
          photo_type: string;
          perspective_deg: number;
          quality_score: number;
          dominant_color: number[];
          aspect: string;
          best_fit_slot_role: string;
        }>;
      };
      effect_applied: Record<string, string>;
      ai_effects_rendered: number[];
      total_cost_usd: number;
      image: {
        image_id: number;
        compliance?: { score?: number; summary?: string };
      };
    }>(`/images/session/${sessionId}/thumbnail-mockup`, payload),
  pixelTemplates: () =>
    api.get<{
      templates: Array<{
        id: string;
        label: string;
        description: string;
        min_inputs: number;
        ideal_inputs: number;
      }>;
    }>("/images/pixel-thumbnail/templates"),
  pixelThumbnail: (
    sessionId: string,
    payload: { template_id: string; image_ids?: number[] }
  ) =>
    api.post<{
      success: boolean;
      template_id: string;
      template_label: string;
      frame_fill_pct: number;
      input_count: number;
      overlay_count: number;
      total_cost_usd: number;
      image: {
        image_id: number;
        compliance?: { score?: number; summary?: string };
      };
    }>(`/images/session/${sessionId}/pixel-thumbnail`, payload),
  aiFullSet: (
    id: number,
    payload: {
      category_path?: string | null;
      lifestyle_count?: number;
      apply_brand_frame?: boolean | null;
      apply_palette_anchor?: boolean | null;
    }
  ) =>
    api.post<{
      success: boolean;
      generated: number;
      requested: number;
      total_cost_usd: number;
      errors: string[];
    }>(`/images/${id}/ai-full-set`, payload),
  // M39: AI suggests dimensions from photo (Gemini vision)
  suggestDimensions: (id: number) =>
    api.post<{
      suggestions: Array<{
        label_hint: string;
        type: "linear" | "diameter" | "radius" | "callout";
        point_a: [number, number];
        point_b: [number, number];
        confidence: number;
      }>;
    }>(`/images/${id}/suggest-dimensions`, {}),
  // M29: dimension annotations (strzałki z wymiarami)
  annotateDimensions: (
    id: number,
    payload: {
      dimensions: Array<{
        point_a: [number, number];
        point_b: [number, number];
        label: string;
        flip_offset?: boolean;
        no_offset?: boolean;
        type?: "linear" | "diameter" | "radius" | "callout";
      }>;
      style?: {
        color?: [number, number, number];
        line_width_scale?: number;
        font_size_scale?: number;
        offset_distance_pct?: number;
        auto_offset?: boolean;
        label_style?: "boxed" | "gap" | "bubble";
        arrow_style?: "arrow" | "tbar";
        line_style?: "solid" | "dashed" | "dotted";
        legend_mode?: boolean;
        legend_position?: "tl" | "tr" | "bl" | "br";
        legend_title?: string;
        legend_scale?: number;
      };
    }
  ) =>
    api.post<{
      image_id: number;
      processing_mode: string;
      source_image_id: number;
      width: number;
      height: number;
    }>(`/images/${id}/annotate-dimensions`, payload),
  // M26: nałóż brand frame na istniejące zdjęcie (bez generacji AI)
  applyBrandFrame: (id: number) =>
    api.post<{
      image_id: number;
      processing_mode: string;
      source_image_id: number;
      width: number;
      height: number;
    }>(`/images/${id}/apply-brand-frame`, {}),
  complianceScan: (id: number) =>
    api.post<{
      image_id: number;
      score: number;
      summary: string;
      risks: Array<{
        type: string;
        severity: "critical" | "warning" | "info";
        description: string;
      }>;
      cost_usd: number;
      model: string;
    }>(`/images/${id}/compliance-scan`),
};

// === Profit / Margin Calculator ===
export interface ProfitResult {
  // sprzedaż
  selling_price: number;
  selling_net: number;
  vat_from_sale: number;
  vat_rate: number;
  // zakup
  purchase_price: number;
  purchase_price_gross: number;
  vat_to_deduct: number;
  // koszty (gross + net dla vatowca)
  allegro_commission: number;            // = gross (alias)
  allegro_commission_gross: number;
  allegro_commission_net: number;
  allegro_commission_percent: number;
  ads_cost: number;                       // = gross (alias)
  ads_cost_gross: number;
  ads_cost_net: number;
  ads_percent: number;
  shipping_cost: number;                  // = gross (alias)
  shipping_cost_gross: number;
  shipping_cost_net: number;
  packaging_cost: number;
  returns_cost: number;
  other_costs: number;
  // wyniki
  margin_gross: number;
  margin_gross_percent: number;
  gross_profit: number;
  income_tax: number;
  net_profit: number;
  margin_net: number;                     // = net_profit (alias)
  margin_net_percent: number;
  roi_percent: number;
  is_profitable: boolean;
  profitability_status: "profitable" | "low_margin" | "loss";
  break_even_price: number;
  // meta
  is_vat_payer: boolean;
  tax_form: string;
  tax_rate_used: number;
  tax_basis: "income" | "revenue";
  category?: string | null;
  offer_id?: number | null;
  breakdown?: Record<string, unknown>;
}

export interface ProfitTaxForm {
  key: string;
  rate: number;
  basis: "income" | "revenue";
  label: string;
}

export interface ProfitSmartShipping {
  key: string;
  amount: number;
}

export interface ProfitInput {
  id: number;
  offer_id: number;
  allegro_offer_id?: string | null;
  title?: string | null;
  selling_price?: number | null;
  category?: string | null;
  purchase_price: number;
  shipping_cost: number;
  packaging_cost: number;
  other_costs: number;
  notes?: string | null;
  updated_at?: string | null;
  offer_status?: string | null;
}

export interface ProfitSummary {
  total_offers: number;
  profitable_count: number;
  low_margin_count: number;
  loss_count: number;
  total_margin_net: number;
  avg_margin_percent: number;
  best_offer_id?: number | null;
  worst_offer_id?: number | null;
}

export interface ProfitCategory {
  key: string;
  rate_percent: number;
  max_commission?: number | null;
}

export const profitApi = {
  categories: () => api.get<ProfitCategory[]>("/profit/categories"),
  taxForms: () => api.get<ProfitTaxForm[]>("/profit/tax-forms"),
  smartShipping: () => api.get<ProfitSmartShipping[]>("/profit/smart-shipping"),
  calculate: (data: {
    purchase_price: number;
    selling_price: number;
    purchase_price_gross?: number;
    vat_rate?: number;
    is_vat_payer?: boolean;
    purchase_vat_deferred?: boolean;
    category?: string | null;
    manual_commission_rate?: number | null;
    commission_max_override?: number | null;
    commission_on_shipping?: boolean;
    shipping_cost?: number;
    shipping_preset?: string | null;
    ads_percent?: number;
    packaging_materials?: number;
    packaging_time_min?: number;
    hourly_rate?: number;
    returns_percent?: number;
    return_cost?: number;
    ads_cost?: number;
    packaging_cost?: number;
    other_costs?: number;
    tax_form?: string;
    custom_tax_rate?: number | null;
    custom_tax_basis?: string | null;
    offer_id?: number | null;
  }) => api.post<ProfitResult>("/profit/calculate", data),
  listInputs: () => api.get<{ inputs: ProfitInput[] }>("/profit/inputs"),
  saveInputs: (
    allegroOfferId: string,
    data: {
      purchase_price: number;
      shipping_cost?: number;
      packaging_cost?: number;
      other_costs?: number;
      notes?: string | null;
      selling_price?: number;
      title?: string | null;
      category?: string | null;
    }
  ) =>
    api.put<ProfitResult>(
      `/profit/inputs/${encodeURIComponent(allegroOfferId)}`,
      data
    ),
  deleteInputs: (allegroOfferId: string) =>
    api.delete<{ deleted: number }>(
      `/profit/inputs/${encodeURIComponent(allegroOfferId)}`
    ),
  summary: () => api.get<ProfitSummary>("/profit/summary"),

  // Quick purchase price (moje-oferty inline edit) — 2026-07-01
  setQuickPurchasePrice: (
    allegroOfferId: string,
    data: { purchase_price: number; title?: string; selling_price?: number },
  ) =>
    api.patch<{ purchase_price: number; margin: number; margin_pct: number }>(
      `/profit/inputs/${encodeURIComponent(allegroOfferId)}/purchase-price`,
      data,
    ),
  quickLookup: (allegroOfferIds: string[]) =>
    api.get<
      Record<
        string,
        {
          purchase_price: number;
          margin: number;
          margin_pct: number;
          commission_pct?: number;
        }
      >
    >("/profit/quick-lookup", {
      params: { allegro_offer_ids: allegroOfferIds.join(",") },
    }),
  refreshCommissions: (allegro_offer_ids: string[], force = false) =>
    api.post<{
      refreshed: number;
      skipped_fresh: number;
      failed: number;
      errors: string[];
    }>("/profit/refresh-commissions", { allegro_offer_ids, force }),
};

// Etap B (2026-07-01): Koszty Allegro (billing entries) na pulpicie + raportach
export interface AllegroCostsBreakdown {
  commission: { label: string; amount: number; count: number; pct: number };
  ads: { label: string; amount: number; count: number; pct: number };
  shipping: { label: string; amount: number; count: number; pct: number };
  other: { label: string; amount: number; count: number; pct: number };
}
export interface AllegroCostsDaily {
  date: string; // YYYY-MM-DD
  amount: number;
}
export interface AllegroCostsTopOffer {
  allegro_offer_id: string;
  title: string;
  total: number;
  commission: number;
  ads: number;
  shipping: number;
  other: number;
}
export interface AllegroCostsResponse {
  period_days: number;
  from_date: string;
  to_date: string;
  total: number;
  breakdown: AllegroCostsBreakdown;
  entries_count: number;
  daily_series?: AllegroCostsDaily[];
  top_offers?: AllegroCostsTopOffer[];
}

export const allegroCostsApi = {
  summary: (days = 30) =>
    api.get<AllegroCostsResponse>("/reports/allegro-costs", { params: { days } }),
};

// Etap E (2026-07-01): sync zamówień — auto w tle, endpoint status + fallback manual
export const ordersSyncApi = {
  status: () =>
    api.get<{ last_synced_at: string | null; orders_in_db: number }>(
      "/reports/sync-status",
    ),
  sync: (days = 90) =>
    api.post<{
      new: number;
      updated: number;
      total: number;
      pages: number;
      errors: string[];
    }>("/reports/sync-orders", null, { params: { days } }),
};

// === Company profile (Settings → Profil firmy) ===
export type LegalForm = "jdg" | "sp_zoo" | "sa" | "sp_jawna" | "inna";
export type TaxFormKey =
  | "liniowy_19"
  | "skala"
  | "ryczalt"
  | "cit_9"
  | "cit_19"
  | "none";

export interface CompanyProfile {
  name?: string | null;
  nip?: string | null;
  regon?: string | null;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  legal_form?: LegalForm | null;
  is_vat_payer?: boolean;
  vat_rate?: 0 | 5 | 8 | 23 | null;
  deduct_vat_from_allegro?: boolean;
  tax_form?: TaxFormKey;
  ryczalt_rate?: number | null;
  bank_account?: string | null;
  bank_swift?: string | null;
  bank_name?: string | null;
  logo_path?: string | null;
  brand_color?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface CompanyProfileResponse extends CompanyProfile {
  client_id: number;
  updated_at?: string | null;
  completeness: number;
}

export const companyProfileApi = {
  get: () => api.get<CompanyProfileResponse>("/company-profile"),
  upsert: (profile: CompanyProfile) =>
    api.put<CompanyProfileResponse>("/company-profile", profile),
  clear: () => api.delete("/company-profile"),
};

/** NIP, telefon i e-mail wymagane przy konfiguracji konta (#836). */
export interface ContactProfile {
  name: string | null;
  nip: string | null;
  phone: string | null;
  email: string | null;
  missing: ("nip" | "phone" | "email")[];
  required: boolean;
}

export const contactProfileApi = {
  get: () => api.get<ContactProfile>("/company-profile/contact"),
  save: (body: { nip: string; phone: string; email: string; name: string | null }) =>
    api.put<ContactProfile>("/company-profile/contact", body),
};

// === Operating costs (Profil firmy → Koszty stałe) ===
export type OperatingCostType =
  | "fixed_monthly"
  | "fixed_quarterly"
  | "fixed_yearly"
  | "one_time"
  | "recurring_period";

export type OperatingCostCategory =
  | "taxes"
  | "office"
  | "marketing"
  | "logistics"
  | "tools"
  | "other";

export interface OperatingCost {
  id: number;
  client_id: number;
  name: string;
  amount: number;
  cost_type: OperatingCostType;
  category: OperatingCostCategory | null;
  start_date: string | null;
  end_date: string | null;
  one_time_date: string | null;
  notes: string | null;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export interface OperatingCostPreset {
  key: string;
  name: string;
  amount: number;
  cost_type: OperatingCostType;
  category: OperatingCostCategory;
  description: string;
}

export interface OperatingCostsListResponse {
  costs: OperatingCost[];
  monthly_total: number;
  presets_available: OperatingCostPreset[];
}

export const operatingCostsApi = {
  list: (only_active = false) =>
    api.get<OperatingCostsListResponse>("/operating-costs", {
      params: { only_active },
    }),
  presets: () => api.get<OperatingCostPreset[]>("/operating-costs/presets"),
  create: (payload: {
    name: string;
    amount: number;
    cost_type: OperatingCostType;
    category?: OperatingCostCategory | null;
    start_date?: string | null;
    end_date?: string | null;
    one_time_date?: string | null;
    notes?: string | null;
  }) => api.post<OperatingCost>("/operating-costs", payload),
  update: (
    id: number,
    payload: Partial<{
      name: string;
      amount: number;
      cost_type: OperatingCostType;
      category: OperatingCostCategory;
      start_date: string | null;
      end_date: string | null;
      one_time_date: string | null;
      notes: string | null;
      is_active: boolean;
    }>
  ) => api.put<OperatingCost>(`/operating-costs/${id}`, payload),
  delete: (id: number) =>
    api.delete<{ deleted: number }>(`/operating-costs/${id}`),
  applyPresets: (preset_keys: string[]) =>
    api.post<{ inserted: number; skipped: number }>(
      "/operating-costs/apply-presets",
      { preset_keys }
    ),
};

// === Reports (Raporty → Przegląd) ===
export type PlanName = "starter" | "pro" | "enterprise";

export interface ReportsKPI {
  id: string;
  label: string;
  icon: string;
  value: number | null;
  format: "currency" | "number" | "percent" | "score";
  trend_pct: number | null;
  secondary?: string;
  alert?: boolean;
  link?: string;
}

export interface ReportsActionable {
  priority: "critical" | "high" | "medium" | "opportunity";
  title: string;
  impact: string;
  action_label: string;
  action_href: string;
}

export interface ReportsTimelinePoint {
  date: string;
  gmv: number;
  orders: number;
  items: number;
}

export interface ReportsInsight {
  priority: "critical" | "high" | "opportunity";
  title: string;
  body: string;
  action_label: string;
  action_href: string;
}

export interface DashboardResponse {
  range: {
    key: string;
    from: string;
    to: string;
    prev_from: string;
    prev_to: string;
  };
  kpis: ReportsKPI[];
  timeline: ReportsTimelinePoint[];
  actionable: ReportsActionable[];
  widgets: {
    heatmap: {
      matrix: number[][];
      peak: { day: string; hour: number; count: number; label: string } | null;
    };
    top_products: Array<{
      id: number;
      allegro_offer_id: string;
      title: string;
      price: number;
      sold: number;
      revenue: number;
      image: string | null;
    }>;
    top_combo: { left: string; right: string; count: number; label: string } | null;
  };
  insights: ReportsInsight[];
  company_profile: { configured: boolean; completeness: number };
  data_status: { orders_synced: boolean; offers_count: number };
  generated_at: string;
  meta: {
    plan: PlanName;
    features: Record<string, boolean>;
    cached: boolean;
    ttl_seconds: number;
  };
}

// ===========================================================================
// Dashboard v2 (Sprint 1) — nowe endpointy dla redesignu pulpitu.
// Todo (3 akcje merged z rec/audit/ads) + krytyczne alerty.
// North Star (sprzedaz 7d) uzywa istniejacego reportsApi.dashboard()
// ===========================================================================

export interface TodoItem {
  source: "recommendation" | "audit" | "ads";
  title: string;
  description: string;
  action_url: string;
  action_label: string;
  priority: number;
  impact_pln?: number;
  count?: number;
}

export interface CriticalAlert {
  kind: string;
  severity: "critical" | "warning";
  title: string;
  description: string;
  action_url: string;
  action_label: string;
}

// Top 5 #3 (2026-08-11): Underpriced offers vs mediana konkurencji
export interface UnderpricedCase {
  offer_id: string;
  offer_title: string;
  offer_main_image_url: string | null;
  ean: string | null;
  my_price: number;
  median_competitor_price: number;
  min_competitor_price: number;
  max_competitor_price: number;
  savings_per_unit: number;
  savings_pct: number;
  competitors_count: number;
  stock_sold_30d: number;
  views_30d: number;
  potential_monthly_impact: number;
}
export interface UnderpricedResponse {
  cases: UnderpricedCase[];
  total_cases: number;
  total_potential_impact: number;
  threshold_pct: number;
  offers_scanned: number;
  cached?: boolean;
}

// Top 5 #4 (2026-08-11): Monthly agency report — deterministyczny long-form
export interface MonthlyReportResponse {
  month: string;
  month_label: string;
  period: { from: string; to: string };
  sales: {
    gmv: number;
    orders: number;
    gmv_prev: number;
    orders_prev: number;
    gmv_delta_pct: number | null;
    orders_delta_pct: number | null;
  };
  healsy_actions: {
    recommendations_executed: number;
    audits_run: number;
    campaigns_updated: number;
    impact_pln: number;
  };
  detected_issues: {
    cannibalization_offers: number;
    cannibalization_cost_30d: number;
    organic_waste_offers: number;
    organic_waste_cost_30d: number;
    underpriced_offers: number;
    underpriced_potential_pln: number;
  };
  top_products: Array<{
    offer_id: string;
    title: string;
    main_image_url: string | null;
    orders: number;
    gmv: number;
  }>;
  priorities: Array<{
    title: string;
    why: string;
    action_url: string;
  }>;
  generated_at: string;
  cached?: boolean;
}

// Top 5 #5 (2026-08-11): Quality forecast — regresja audit_runs.global_score
export interface QualityForecastResponse {
  has_data: boolean;
  current_score: number | null;
  trend_per_day: number | null;
  days_to_threshold: number | null;
  threshold: number;
  history: Array<{ date: string; score: number }>;
  status: "good" | "warning" | "critical" | "unknown";
  message: string;
  audits_count?: number;
}

// F0-6 (2026-08-12): status synchronizacji — widget "kiedy patrzysz na aktualne dane"
export interface SyncStatusResponse {
  offers: { last_sync_at: string | null; seconds_ago: number | null; count: number; source: string };
  orders: { last_sync_at: string | null; seconds_ago: number | null; count: number; source: string };
  billing: { last_sync_at: string | null; seconds_ago: number | null; count: number; source: string };
  oauth: { status: string; consecutive_failures: number; error_msg: string | null };
  extension: { status: string };
  overall_health: "ok" | "warning" | "error";
  warnings: string[];
  errors: string[];
  checked_at: string;
}

// Pulpit 2.0 (#599): jedno źródło liczb kafli dla wybranego okresu. null = brak danych.
export type DashboardPeriod = "7d" | "30d" | "90d" | "this_month";

export interface DashboardOverviewMetric {
  value: number | null;
  previous: number | null;
  change_pct: number | null;
}

export interface DashboardOverviewResponse {
  period: { key: DashboardPeriod; from: string; to: string; prev_from: string; prev_to: string };
  data: { orders_synced: boolean; billing_synced: boolean };
  sales: DashboardOverviewMetric & { series: number[] };
  orders: DashboardOverviewMetric & { avg_basket: number | null; series: number[] };
  after_fees: DashboardOverviewMetric & {
    share_pct: number | null;
    fees_total: number | null;
    fees_breakdown: Array<{ key: string; label: string; amount: number }>;
  };
}

/** Karta celu na pulpicie (#624). Brak danych to null, nigdy 0. */
export interface DashboardGoalCard {
  goal: {
    id: number;
    name: string | null;
    goal_type: "revenue" | "orders" | "profit_net";
    period_type: "month" | "quarter" | "year" | "custom";
    period_start: string;
    period_end: string;
    target: number;
  } | null;
  today: string;
  forecast_from_day: number;
  achieved?: number | null;
  percentage?: number | null;
  forecast?: number | null;
  forecast_reason?: "too_early" | "short_history" | "no_data" | "not_supported" | "period_ended" | null;
  gap?: number | null;
  hint?: { decision_id: number; title: string; persona_code: string | null; impact_pln: number } | null;
  /** Sprzedaż dzienna (#873): wczoraj i dziś do teraz; null bez historii zamówień. */
  recent_days?: { yesterday: DashboardSalesDay; today: DashboardSalesDay } | null;
}

export interface DashboardSalesDay {
  date: string;
  sales: number;
  orders: number;
}

export type SinceVisitItem =
  | { kind: "orders"; count: number; amount: number; href: string }
  | { kind: "messages"; count: number; href: string }
  | { kind: "proposals"; count: number; href: string }
  | { kind: "audit"; count: number | null; href: string };

export interface DashboardVisit {
  since: string | null;
  items: SinceVisitItem[];
  heartbeat_seconds: number;
}

/** Dolny rząd pulpitu (#629): Opłaty, Okazje, Nadchodzi. Brak danych to null. */
export interface DashboardFeesPart {
  key: string;
  label: string;
  amount: number;
  pct: number;
  includes?: string[];
}

export type DashboardOpportunity =
  | { kind: "ads"; amount: number; share_of_sales_pct: number | null; href: string }
  | { kind: "competition"; count: number; days: number; href: string }
  | {
      kind: "credits_low" | "credits_renewal";
      balance: number;
      allowance: number;
      reset_at: string | null;
      href: string;
    };

export interface DashboardUpcomingEvent {
  date: string;
  days: number;
  title: string;
  hint: string | null;
  href: string | null;
  source: "calendar" | "account";
}

export interface DashboardExtras {
  period: DashboardOverviewResponse["period"];
  fees: { total: number; share_of_sales_pct: number | null; parts: DashboardFeesPart[] } | null;
  opportunities: DashboardOpportunity[];
  upcoming: DashboardUpcomingEvent[];
}

/** Układ pulpitu (#629): strefy w kolejności, `on` = widoczna. */
export type PulpitZoneId =
  | "kpi"
  | "todo"
  | "team"
  | "extras"
  | "finanse"
  | "ads_campaigns"
  | "pulse_7d"
  | "value_counter"
  | "credits"
  | "ai_inbox";

export type PulpitRole = "owner" | "service" | "agency";

export interface DashboardLayout {
  zones: Array<{ id: PulpitZoneId; on: boolean }>;
  is_default: boolean;
  role: PulpitRole | null;
  can_edit: boolean;
}

export const dashboardApi = {
  overview: (period: DashboardPeriod) =>
    api.get<DashboardOverviewResponse>("/dashboard/overview", { params: { period } }),
  goal: () => api.get<DashboardGoalCard>("/dashboard/goal"),
  extras: (period: DashboardPeriod) =>
    api.get<DashboardExtras>("/dashboard/extras", { params: { period } }),
  /** Z limitem czasu: bez odpowiedzi pulpit rusza z układem awaryjnym. */
  layout: () => api.get<DashboardLayout>("/dashboard/layout", { timeout: 5000 }),
  saveLayout: (zones: DashboardLayout["zones"]) => api.put<DashboardLayout>("/dashboard/layout", { zones }),
  resetLayout: () => api.post<DashboardLayout>("/dashboard/layout/reset"),
  /** Sygnał obecności na pulpicie; `changes: false` bez liczenia zmian. */
  visit: (changes = true) =>
    api.post<DashboardVisit>("/dashboard/visit", null, { params: { changes } }),
  todo: () => api.get<{ items: TodoItem[]; total_available: number }>("/dashboard/todo"),
  alerts: () => api.get<{ alerts: CriticalAlert[]; count: number }>("/dashboard/alerts"),
  underpriced: (refresh = false) =>
    api.get<UnderpricedResponse>("/dashboard/underpriced", { params: { refresh } }),
  monthlyReport: (month?: string) =>
    api.get<MonthlyReportResponse>("/dashboard/monthly-report", { params: month ? { month } : {} }),
  qualityForecast: (threshold = 60) =>
    api.get<QualityForecastResponse>("/dashboard/quality-forecast", { params: { threshold } }),
  syncStatus: () => api.get<SyncStatusResponse>("/dashboard/sync-status"),
  // Sprint VI/1: scoreboard 400 pkt (od 27.07.2026) z owner_persona per miara.
  qualityScoreboard: () =>
    api.get<QualityScoreboard>("/dashboard/quality/scoreboard"),
};

export interface QualityScoreboardMetric {
  code: string;
  label: string;
  owner_persona_code: string;
  co_owner_persona_code: string | null;
  hint: string;
  action_href: string;
  score: number;
  max_score: number;
  pct: number | null;
  health: "good" | "warn" | "critical";
}

export interface QualityScoreboard {
  source: "live" | "unavailable";
  reason?: string;
  total_score?: number;
  max_score: number;
  grade?: string;
  score_pct?: number;
  days_with_data?: number;
  latest_day?: string;
  metrics: QualityScoreboardMetric[];
}

export const reportsApi = {
  meta: () =>
    api.get<{
      plan_raw: string;
      plan: PlanName;
      features: Record<string, boolean>;
      has_orders: boolean;
    }>("/reports/meta"),
  dashboard: (params?: {
    range?: "1d" | "7d" | "30d" | "90d" | "custom";
    date_from?: string;
    date_to?: string;
    refresh?: boolean;
  }) => api.get<DashboardResponse>("/reports/dashboard", { params }),
  invalidate: () => api.post("/reports/dashboard/invalidate"),
  salesTime: (params?: {
    range?: "7d" | "30d" | "90d" | "custom";
    date_from?: string;
    date_to?: string;
    metric?: "orders" | "gmv" | "items";
    refresh?: boolean;
  }) => api.get<SalesTimeResponse>("/reports/sales-time", { params }),
  ordersReport: (params?: {
    range?: "1d" | "7d" | "30d" | "90d" | "this_month" | "custom";
    date_from?: string;
    date_to?: string;
    status?: "all" | "new" | "shipped" | "delivered" | "cancelled" | "returns";
    metric?: "orders" | "gmv" | "items";
    basket?: string;
    search?: string;
    page?: number;
    page_size?: number;
    sort?: string;
    refresh?: boolean;
  }) => api.get<OrdersReportResponse>("/reports/orders", { params }),
  orderDetail: (orderRef: string) =>
    api.get<OrderDetailResponse>(`/reports/orders/${encodeURIComponent(orderRef)}`),
  channels: (params?: {
    range?: "7d" | "30d" | "90d" | "custom";
    date_from?: string;
    date_to?: string;
    metric?: "gmv" | "orders" | "items" | "margin";
    refresh?: boolean;
  }) => api.get<ChannelsReportResponse>("/reports/channels", { params }),
  finance: (params?: {
    range?: "1d" | "7d" | "30d" | "90d" | "this_month" | "custom";
    date_from?: string;
    date_to?: string;
    market?: "all" | "PL" | "CZ" | "HU" | "EU" | "US" | "UK";
  }) =>
    // 202 = synchronizacja jeszcze trwa, a treść to `{detail}`, nie raport.
    // Axios traktuje 202 jak sukces, więc zamieniamy ją na błąd ze statusem
    // 202, który strony już obsługują („Synchronizacja z Allegro trwa”, #613).
    api.get<FinanceReportResponse>("/reports/finance", { params }).then((r) => {
      if (r.status === 202) {
        throw Object.assign(new Error("setup_not_ready"), { response: r });
      }
      return r;
    }),
  // === Sesja 3 ===
  margin: (params?: {
    range?: "7d" | "30d" | "90d" | "custom";
    date_from?: string;
    date_to?: string;
    only_losing?: boolean;
    only_low?: boolean;
    only_missing_pp?: boolean;
    only_top?: boolean;
    search?: string;
    sort?: string;
    refresh?: boolean;
  }) => api.get<MarginReportResponse>("/reports/margin", { params }),
  setPurchasePrice: (offerId: number, payload: PurchasePricePayload) =>
    api.put<{ offer_id: number; purchase_price: number; saved: boolean }>(
      `/reports/margin/${offerId}/purchase-price`,
      payload
    ),
  csvImportPurchasePrices: (csvText: string) =>
    api.post<{ imported: number; skipped: number; errors: string[] }>(
      "/reports/margin/csv-import",
      { csv_text: csvText }
    ),
  products: (params?: {
    range?: "7d" | "30d" | "90d" | "custom";
    date_from?: string;
    date_to?: string;
    chip?: string;
    search?: string;
    sort?: string;
    top10_metric?: "revenue" | "qty" | "margin";
    refresh?: boolean;
  }) => api.get<ProductsReportResponse>("/reports/products", { params }),
  categories: (params?: {
    range?: "7d" | "30d" | "90d" | "custom";
    date_from?: string;
    date_to?: string;
    refresh?: boolean;
  }) => api.get<CategoriesReportResponse>("/reports/categories", { params }),
  trends: (params?: {
    range?: "30d" | "90d" | "180d" | "custom";
    date_from?: string;
    date_to?: string;
    forecast_days?: number;
    refresh?: boolean;
  }) => api.get<TrendsReportResponse>("/reports/trends", { params }),
  bundles: (params?: {
    range?: "30d" | "90d" | "180d" | "custom";
    date_from?: string;
    date_to?: string;
    min_count?: number;
    refresh?: boolean;
  }) => api.get<BundlesReportResponse>("/reports/bundles", { params }),
  generateBundleDescription: (left_name: string, right_name: string) =>
    api.post<{
      title: string;
      description_html: string;
      tags: string[];
      generated_by: string;
      note: string;
    }>("/reports/bundles/generate-description", { left_name, right_name }),
  // === Goals (Sesja 5) ===
  listGoals: (only_active = false) =>
    api.get<{ goals: Goal[]; active_goal: Goal | null }>("/reports/goals", {
      params: { only_active },
    }),
  suggestTargets: () =>
    api.get<GoalSuggestTargets>("/reports/goals/suggest-targets"),
  createGoal: (payload: GoalCreatePayload) =>
    api.post<Goal>("/reports/goals", payload),
  getGoal: (id: number) => api.get<Goal>(`/reports/goals/${id}`),
  updateGoal: (id: number, payload: Partial<GoalCreatePayload>) =>
    api.put<Goal>(`/reports/goals/${id}`, payload),
  deleteGoal: (id: number) =>
    api.delete<{ deleted: number }>(`/reports/goals/${id}`),
  goalProgress: (id: number) =>
    api.get<GoalProgressResponse>(`/reports/goals/${id}/progress`),
  goalWhatNeeded: (id: number) =>
    api.get<GoalWhatNeededResponse>(`/reports/goals/${id}/what-needed`),
  goalChangesLog: (id: number) =>
    api.get<
      Array<{
        id: number;
        field: string;
        old_value: string | null;
        new_value: string | null;
        changed_at: string;
      }>
    >(`/reports/goals/${id}/changes-log`),
  // Margin enhancement: include operating costs toggle
  marginWithCosts: (params?: {
    range?: "7d" | "30d" | "90d" | "custom";
    date_from?: string;
    date_to?: string;
    only_losing?: boolean;
    only_low?: boolean;
    only_missing_pp?: boolean;
    only_top?: boolean;
    search?: string;
    sort?: string;
    include_operating_costs?: boolean;
    refresh?: boolean;
  }) => api.get<MarginReportResponse>("/reports/margin", { params }),

  // === Inventory (2026-07-07) — /raporty/magazyn ===
  inventory: (params?: {
    commission_period_days?: number;
    refresh?: boolean;
  }) => api.get<InventoryReportResponse>("/reports/inventory", { params }),

  setRealStock: (allegro_offer_id: string, payload: { real_stock: number; note?: string | null }) =>
    api.patch<{ allegro_offer_id: string; real_stock: number; note: string | null }>(
      `/reports/inventory/offer/${encodeURIComponent(allegro_offer_id)}/real-stock`,
      payload,
    ),

  setOfferVatRate: (offer_id: number, vat_rate: number | null) =>
    api.patch<{ offer_id: number; vat_rate: number | null }>(
      `/reports/inventory/offer/${offer_id}/vat-rate`,
      { vat_rate },
    ),

  inventorySnapshots: (params?: { limit?: number }) =>
    api.get<{ snapshots: InventorySnapshot[]; count: number }>(
      "/reports/inventory/snapshots",
      { params },
    ),

  createInventorySnapshot: (period?: string) =>
    api.post<{ period: string; summary: InventorySummary }>(
      "/reports/inventory/snapshots/create",
      null,
      { params: period ? { period } : undefined },
    ),

  reorderSuggestions: (params?: {
    lead_time_days?: number;
    safety_stock_days?: number;
  }) =>
    api.get<{
      suggestions: ReorderSuggestion[];
      count: number;
      lead_time_days: number;
      safety_stock_days: number;
    }>("/reports/inventory/reorder-suggestions", { params }),

  // === Stock groups (2026-07-08) — łączenie aukcji w jeden wspólny stock ===
  listStockGroups: () =>
    api.get<{ groups: StockGroup[]; count: number }>(
      "/reports/inventory/groups",
    ),

  createStockGroup: (payload: StockGroupCreatePayload) =>
    api.post<StockGroup>("/reports/inventory/groups", payload),

  updateStockGroup: (group_id: number, patch: Partial<StockGroupUpdatePayload>) =>
    api.patch<StockGroup>(`/reports/inventory/groups/${group_id}`, patch),

  deleteStockGroup: (group_id: number) =>
    api.delete<{ deleted: true; group_id: number }>(
      `/reports/inventory/groups/${group_id}`,
    ),

  addStockGroupMember: (
    group_id: number,
    payload: { offer_id: string; multiplier: number; is_display_master?: boolean },
  ) =>
    api.post<StockGroup>(
      `/reports/inventory/groups/${group_id}/members`,
      payload,
    ),

  removeStockGroupMember: (group_id: number, offer_id: string) =>
    api.delete<StockGroup | { deleted: true; group_id: number }>(
      `/reports/inventory/groups/${group_id}/members/${encodeURIComponent(offer_id)}`,
    ),

  suggestStockGroups: () =>
    api.get<{
      suggestions: StockGroupSuggestion[];
      count: number;
    }>("/reports/inventory/groups/suggest"),

  syncOfferImages: () =>
    api.post<{
      checked: number;
      updated: number;
      still_missing: number;
    }>("/reports/inventory/sync-offer-images"),
};

// === Inventory types ===
export interface InventoryRow {
  offer_id: number;
  allegro_offer_id: string;
  title: string;
  category_id: string | null;
  category_name: string | null;
  main_image_url: string | null;
  status: string | null;
  price_gross: number;
  allegro_stock: number;
  real_stock: number | null;
  effective_stock: number;
  stock_source: "override" | "allegro" | "group";
  in_group: boolean;
  group_id: number | null;
  group_name: string | null;
  group_multiplier: number | null;
  purchase_price: number | null;
  vat_rate: number;
  vat_source: "override" | "company_default";
  value_gross: number;
  value_net: number;
  value_vat: number;
  value_purchase: number;
  potential_profit: number;
  potential_profit_after_commission: number;
  avg_commission_pct: number;
  commission_source: "real" | "quote_cache" | "category" | "default";
  break_even_price: number | null;
  sales_30d_qty: number;
  sales_30d_revenue: number;
  velocity_per_day: number;
  days_of_supply: number | null;
  last_sale_at: string | null;
  is_dead_stock: boolean;
  needs_purchase_price: boolean;
  needs_real_stock: boolean;
}

export interface InventorySummary {
  offers_count: number;
  groups_count: number;
  offers_with_purchase_price: number;
  offers_with_real_stock: number;
  coverage_pp_pct: number;
  stock_units_total: number;
  cash_tied_up: number;
  total_value_gross: number;
  total_potential_profit: number;
  total_potential_profit_after_commission: number;
  dead_stock_count: number;
  dead_stock_value: number;
}

export interface StockGroupMember {
  offer_id: string;
  multiplier: number;
  is_display_master: boolean;
  title: string | null;
  main_image_url: string | null;
  price_amount: number;
  allegro_stock: number;
  status: string | null;
  sellable_via_this_offer: number;
}

export interface StockGroup {
  id: number;
  name: string;
  real_stock: number;
  purchase_cost_pln: number | null;
  vat_rate: number | null;
  vat_rate_effective?: number;
  note: string | null;
  value_purchase: number | null;
  value_gross?: number;
  value_net?: number;
  value_vat?: number;
  potential_profit?: number;
  unit_price_estimate?: number;
  needs_purchase_price?: boolean;
  display_master_offer_id: string | null;
  display_title: string | null;
  display_image_url: string | null;
  members: StockGroupMember[];
  member_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface StockGroupCreatePayload {
  name: string;
  real_stock: number;
  purchase_cost_pln?: number | null;
  vat_rate?: number | null;
  note?: string | null;
  members: Array<{
    offer_id: string;
    multiplier: number;
    is_display_master?: boolean;
  }>;
}

export interface StockGroupUpdatePayload {
  name: string;
  real_stock: number;
  purchase_cost_pln: number | null;
  vat_rate: number | null;
  note: string | null;
}

export interface StockGroupSuggestion {
  ean: string;
  offers: Array<{
    offer_id: string;
    title: string;
    main_image_url: string | null;
    price_amount: number;
    allegro_stock: number;
  }>;
}

export interface InventoryReportResponse {
  rows: InventoryRow[];
  groups: StockGroup[];
  summary: InventorySummary;
  meta: {
    commission_period_days: number;
    dead_stock_threshold_days: number;
    default_commission_pct: number;
    generated_at: string;
    vat_rate_default?: number;
    is_vat_payer?: boolean;
  };
}

export interface InventorySnapshot {
  period: string;
  stock_units_total: number;
  total_value_gross: number;
  cash_tied_up: number;
  total_potential_profit: number;
  total_potential_profit_after_commission: number;
  dead_stock_count: number;
  dead_stock_value: number;
  offers_count: number;
  created_at: string | null;
}

export interface ReorderSuggestion {
  offer_id: number;
  allegro_offer_id: string;
  title: string;
  current_stock: number;
  velocity_per_day: number;
  days_of_supply: number | null;
  suggested_reorder_qty: number;
  estimated_cost: number | null;
}

// === Goals types ===
export type GoalType = "profit_net" | "revenue" | "orders";
export type PeriodType = "month" | "quarter" | "year" | "custom";

export interface GoalTier {
  name: string;
  amount: number;
  icon?: string;
  unlocked?: boolean;
  progress_pct?: number;
}

export interface Goal {
  id: number;
  client_id: number;
  name: string | null;
  period_type: PeriodType;
  period_start: string;
  period_end: string;
  goal_type: GoalType;
  target_amount: number;
  tiers: GoalTier[];
  tiers_json: string;
  include_operating_costs: number;
  motivation_note: string | null;
  is_active: number;
  achieved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface GoalCreatePayload {
  target_amount: number;
  period_type?: PeriodType;
  custom_start?: string;
  custom_end?: string;
  goal_type?: GoalType;
  name?: string | null;
  motivation_note?: string | null;
  include_operating_costs?: boolean;
  tiers?: GoalTier[];
}

export interface GoalProgressData {
  achieved: number;
  target: number;
  percentage: number;
  operating_profit: number;
  operating_costs_total: number;
  operating_costs_breakdown: Array<{
    id: number;
    name: string;
    category: string | null;
    cost_type: string;
    monthly_equivalent: number;
    contribution: number;
  }>;
  operating_costs_by_category: Record<string, number>;
  monthly_costs_estimate: number;
  revenue: number;
  orders: number;
  tiers: GoalTier[];
  current_tier_index: number;
  current_tier: GoalTier | null;
  next_tier:
    | (GoalTier & { from: number; remaining: number; tier_progress_pct: number })
    | null;
  streak_days: number;
  daily_contributions: Array<{ date: string; value: number }>;
  include_operating_costs: boolean;
  goal_type: GoalType;
}

export interface GoalPaceData {
  status: "ahead" | "on_track" | "behind";
  label: string;
  days_total: number;
  days_passed: number;
  days_remaining: number;
  expected_at_today: number;
  delta: number;
  delta_pct: number;
  avg_per_day: number;
  needed_per_day: number;
  days_to_target_at_pace: number | null;
}

export interface GoalProgressResponse {
  goal: Goal;
  progress: GoalProgressData;
  pace: GoalPaceData;
}

export interface GoalWhatNeededResponse {
  needed_operating_profit: number;
  needed_revenue: number;
  needed_orders: number;
  current_orders_per_day: number;
  needed_orders_per_day: number;
  current_aov: number;
  current_margin_pct: number;
  monthly_costs: number;
  levers: Array<{
    type: string;
    title: string;
    estimate_pln: number;
    description: string;
    href: string;
  }>;
}

export interface GoalSuggestTargets {
  monthly_avg_profit: number;
  monthly_avg_after_costs: number;
  monthly_costs: number;
  presets: Array<{
    label: string;
    amount: number;
    growth_pct: number;
    color: string;
    description: string;
  }>;
}

// === Margin response ===
export interface PurchasePricePayload {
  purchase_price: number;
  shipping_cost?: number;
  packaging_cost?: number;
  other_costs?: number;
  notes?: string | null;
}

export interface MarginRow {
  offer_id: number;
  allegro_offer_id: string;
  title: string;
  category: string | null;
  image: string | null;
  sell_price_gross: number;
  sell_price_net?: number;
  purchase_price: number | null;
  shipping_cost?: number;
  packaging_cost?: number;
  other_costs?: number;
  allegro_commission?: number;
  allegro_commission_gross?: number;
  vat_to_remit?: number;
  income_tax?: number;
  margin_pct: number | null;
  profit_per_unit: number | null;
  profit_per_unit_pre_tax?: number;
  sales_30d_qty: number;
  profit_30d: number | null;
  color: "green" | "yellow" | "orange" | "red" | "gray";
  is_vat_payer?: boolean;
  needs_purchase_price: boolean;
}

export interface MarginKPI {
  id: string;
  label: string;
  value: number | string | null;
  format: "percent" | "currency" | "number" | "text";
  secondary?: string;
  alert?: boolean;
  vat_relevant?: boolean;
}

export interface MarginReportResponse {
  range: { key: string; from: string; to: string };
  hero: {
    margin_net_pct: number | null;
    profit_net_total: number | null;
    revenue_total: number;
    losing_count: number;
    losing_loss_per_month: number;
    trend_pp: number | null;
    profile_complete: boolean;
    is_vat_payer: boolean;
    tax_form: string;
    with_operating_costs?: {
      operating_costs_total: number;
      profit_net_after_costs: number;
      margin_pct_after_costs: number | null;
    };
  };
  kpis: MarginKPI[];
  histogram: Array<{ label: string; count: number; share_pct: number; color: string }>;
  trend: {
    series: Array<{
      date: string;
      margin_pct: number | null;
      revenue: number;
      profit: number;
    }>;
  };
  table: MarginRow[];
  table_total: number;
  insights: SalesTimeInsight[];
  profile: {
    is_vat_payer: boolean;
    vat_rate: number;
    tax_form: string;
    income_tax_rate: number | null;
    completeness: number;
  };
  data_status: {
    offers_total: number;
    offers_with_purchase_price: number;
    offers_with_sales: number;
  };
  operating_costs: {
    total: number;
    by_category: Record<string, number>;
    breakdown: Array<{
      id: number;
      name: string;
      category: string | null;
      cost_type: string;
      monthly_equivalent: number;
      contribution: number;
    }>;
  } | null;
  generated_at: string;
  meta: {
    plan: PlanName;
    features: Record<string, boolean>;
    cached: boolean;
    ttl_seconds: number;
  };
}

// === Products response ===
export interface ProductRow {
  id: number;
  allegro_offer_id: string;
  title: string;
  category: string | null;
  price: number;
  image: string | null;
  status: string;
  smart_active: boolean;
  stock_available: number | null;
  images_count: number;
  views_30d: number;
  visits_30d: number;
  conversion_rate_30d: number;
  sales_30d_qty: number;
  sales_30d_revenue: number;
  margin_pct: number | null;
  profit_per_unit: number | null;
  profit_30d: number | null;
  /** 2026-07-04: prowizja z billing_entries (real) sumowana w okresie. */
  allegro_commission_30d: number;
  allegro_ads_30d: number;
  /** "billing_entries" | "quotes" | "fallback_8pct" — do znacznika w UI. */
  commission_source: "billing_entries" | "quotes" | "fallback_8pct" | null;
  purchase_price: number | null;
  audit_score: number | null;
  trend_pct: number | null;
  color: "green" | "yellow" | "orange" | "red" | "blue";
  days_since_publication: number | null;
  needs_purchase_price: boolean;
}

export interface ProductsReportResponse {
  range: { key: string; from: string; to: string };
  hero: {
    offer_id: number;
    title: string;
    image: string | null;
    category: string | null;
    sales_30d_qty: number;
    sales_30d_revenue: number;
    share_pct: number;
    audit_score: number | null;
    margin_pct: number | null;
  } | null;
  kpis: Array<{
    id: string;
    label: string;
    value: string | number;
    format: string;
    secondary?: string;
    alert?: boolean;
  }>;
  table: ProductRow[];
  table_total: number;
  table_filtered: number;
  scatter: Array<{
    id: number;
    title: string;
    x: number;
    y: number;
    revenue: number;
    color: string;
  }>;
  top10: Array<{
    id: number;
    title: string;
    value: number | null;
    image: string | null;
    color: string;
  }>;
  top10_metric: string;
  insights: SalesTimeInsight[];
  data_status: { offers_total: number; offers_with_sales: number };
  generated_at: string;
  meta: {
    plan: PlanName;
    features: Record<string, boolean>;
    cached: boolean;
    ttl_seconds: number;
  };
}

// === Categories response ===
export interface CategoryRow {
  key: string;
  label: string;
  offers_count: number;
  active_count: number;
  smart_count: number;
  audited_count: number;
  audit_score_avg: number | null;
  sales_qty: number;
  sales_revenue: number;
  profit_total: number;
  margin_avg: number | null;
  view_total: number;
  conversion_rate_avg: number | null;
  trend_pct: number | null;
  color: "green" | "yellow" | "orange" | "red" | "gray";
}

export interface CategoriesReportResponse {
  range: { key: string; from: string; to: string };
  categories: CategoryRow[];
  total_revenue: number;
  total_categories: number;
  hero: {
    category: string;
    share_pct: number;
    revenue: number;
    offers_count: number;
    margin_avg: number | null;
    trend_pct: number | null;
    audit_score_avg: number | null;
  } | null;
  kpis: Array<{
    id: string;
    label: string;
    value: string | number;
    format: string;
    secondary?: string;
    alert?: boolean;
  }>;
  treemap: Array<{
    key: string;
    label: string;
    value: number;
    offers_count: number;
    margin_avg: number | null;
    color: string;
    trend_pct: number | null;
  }>;
  bars: {
    trend: Array<{ label: string; value: number; color: string }>;
    margin: Array<{ label: string; value: number; color: string }>;
  };
  insights: SalesTimeInsight[];
  data_status: { categories_count: number; categories_with_sales: number };
  generated_at: string;
  meta: {
    plan: PlanName;
    features: Record<string, boolean>;
    cached: boolean;
    ttl_seconds: number;
  };
}

// === Sales-time response ===
export interface SalesTimeCard {
  id: string;
  label: string;
  value: string | number;
  secondary?: string;
  delta?: string | null;
  icon: string;
}

export interface SalesTimeInsight {
  priority: "high" | "medium" | "info";
  title: string;
  body: string;
  action_label: string;
  action_href: string;
}

export interface SalesTimeResponse {
  range: { key: string; from: string; to: string };
  metric: "orders" | "gmv" | "items";
  matrix: number[][];
  matrix_total: number;
  matrix_max: number;
  cards: SalesTimeCard[];
  insights: SalesTimeInsight[];
  axes: { days: string[]; days_short: string[]; hours: number[] };
  data_status: { orders_synced: boolean };
  generated_at: string;
  meta: {
    plan: PlanName;
    features: Record<string, boolean>;
    cached: boolean;
    ttl_seconds: number;
  };
}

// === Orders response ===
export interface OrdersKPI {
  id: string;
  label: string;
  value: string | number;
  format: "number" | "currency" | "percent" | "text";
  trend_pct: number | null;
  secondary?: string;
  alert?: boolean;
}

export interface OrderListItem {
  id: number;
  order_id: string;
  allegro_order_id: string;
  buyer_pseudo: string;
  status: string;
  payment_status: string;
  total: number;
  currency: string;
  items_count: number;
  items_preview: string[];
  delivery_method: string | null;
  created_at: string;
  paid_at: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
}

export interface OrdersTimelinePoint {
  bucket: string;
  value: number;
}

export interface OrdersTimelineAnnotation {
  type: "peak" | "drop" | "spike";
  bucket: string;
  value: number;
  label: string;
}

export interface OrdersReportResponse {
  range: { key: string; from: string; to: string };
  kpis: OrdersKPI[];
  timeline: {
    granularity: "day" | "week" | "month";
    metric: "orders" | "gmv" | "items";
    current: OrdersTimelinePoint[];
    previous: OrdersTimelinePoint[];
    annotations: OrdersTimelineAnnotation[];
  };
  list: { items: OrderListItem[]; total: number; limit: number; offset: number };
  basket_histogram: Array<{
    label: string;
    count: number;
    share_pct: number;
    gmv: number;
  }>;
  frequent_pairs: Array<{ left: string; right: string; count: number }>;
  status_breakdown: {
    breakdown: Array<{ status: string; label: string; count: number; share_pct: number }>;
    total: number;
    hours_to_ship: number | null;
    days_to_deliver: number | null;
  };
  insights: SalesTimeInsight[];
  data_status: { orders_count: number };
  generated_at: string;
  meta: {
    plan: PlanName;
    features: Record<string, boolean>;
    cached: boolean;
    ttl_seconds: number;
  };
}

export interface OrderDetailResponse {
  id: number;
  order_id: string;
  allegro_order_id: string;
  buyer: { pseudo: string; voivodeship: string | null; is_returning: boolean };
  status: string;
  payment_status: string;
  fulfillment_status: string | null;
  items: Array<{ name: string; price: number; quantity: number }>;
  totals: {
    gross: number;
    currency: string;
    items_count: number;
    delivery_cost: number;
    allegro_commission_estimate: number;
    vat_estimate: number;
    net_estimate: number;
  };
  delivery: {
    method: string | null;
    address: { voivodeship?: string; postcode?: string };
  };
  timeline: {
    created_at: string;
    paid_at: string | null;
    shipped_at: string | null;
    delivered_at: string | null;
  };
  sub_channel: string | null;
}

// === Channels response ===
export interface ChannelRow {
  key: "smart" | "standard" | "ads" | "promotions";
  label: string;
  color: string;
  icon: string;
  level: number;
  platform: string;
  orders: number;
  gmv: number;
  items: number;
  share_orders_pct: number;
  share_gmv_pct: number;
  margin_pct: number;
  trend_pct: number | null;
}

export interface FinanceReportResponse {
  range: { from: string; to: string; label: string };
  market_filter: string;
  sales: {
    total_pln: number;
    by_market: Array<{
      currency: string;
      market: string;
      orders_count: number;
      value_native: number;
      value_pln: number;
    }>;
  };
  fees: {
    total_pln: number;
    breakdown: Array<{ key: string; label: string; amount: number; count: number }>;
  };
  refunds: {
    total_pln: number;
    breakdown: Array<{ key: string; label: string; amount: number; count: number }>;
  };
  netto_pln: number;
  margin_pct: number;
}

export interface ChannelsReportResponse {
  range: { key: string; from: string; to: string };
  metric: "gmv" | "orders" | "items" | "margin";
  platform: string;
  channels: ChannelRow[];
  hero: {
    channel_key: string;
    label: string;
    color: string;
    share_pct: number;
    gmv: number;
    trend_pct: number | null;
    margin_pct: number;
    headline: string;
  } | null;
  bar_chart: {
    labels: string[];
    keys: string[];
    colors: string[];
    gmv: number[];
    margin_pct: number[];
    orders: number[];
  };
  stacked_area: {
    granularity: "day" | "week";
    buckets: string[];
    series: Record<string, number[]>;
  };
  insights: SalesTimeInsight[];
  v2_placeholder: { label: string; available_in: string };
  total_gmv: number;
  classification_accuracy: string;
  data_status: { orders_count: number };
  generated_at: string;
  meta: {
    plan: PlanName;
    features: Record<string, boolean>;
    cached: boolean;
    ttl_seconds: number;
  };
}

// === Trends response ===
export interface TrendsForecastPoint {
  date: string;
  value: number;
}

export interface TrendsAnomaly {
  date: string;
  value: number;
  moving_avg: number;
  change_pct: number;
  type: "spike" | "drop";
  orders: number;
}

export interface TrendsReportResponse {
  range: { key: string; from: string; to: string };
  hero: {
    total_history: number;
    avg_daily: number;
    forecast_30d: number | null;
    trend_direction: "up" | "down" | "flat";
    trend_strength_pct: number;
    anomalies_count: number;
    alerts_count: number;
  };
  daily: Array<{ date: string; value: number; orders: number }>;
  moving_avg: Array<{ date: string; value: number | null }>;
  forecast: {
    available: boolean;
    reason?: string;
    series: TrendsForecastPoint[];
    total: number;
    days_ahead: number;
    trend_direction: "up" | "down" | "flat";
    trend_strength_pct: number;
    weekend_factor?: number;
    model?: string;
  };
  anomalies: TrendsAnomaly[];
  alerts: Array<{
    type: "drop" | "spike";
    title: string;
    body: string;
    priority: "high" | "info";
  }>;
  category_trends: Array<{
    category: string;
    current: number;
    previous: number;
    change_pct: number | null;
  }>;
  seasonality: {
    dow_avg: Array<{ day: string; value: number }>;
    weekly: Array<{ week_start: string; value: number }>;
  };
  insights: SalesTimeInsight[];
  data_status: { days_count: number; has_forecast: boolean };
  generated_at: string;
  meta: {
    plan: PlanName;
    features: Record<string, boolean>;
    cached: boolean;
    ttl_seconds: number;
  };
}

// === Bundles response ===
export interface BundlePair {
  left: {
    name: string;
    offer_id: number | null;
    allegro_offer_id: string | null;
    category: string | null;
    price: number;
  };
  right: {
    name: string;
    offer_id: number | null;
    allegro_offer_id: string | null;
    category: string | null;
    price: number;
  };
  count: number;
  avg_order_revenue: number;
  bundle_price: number;
  lift: number;
  estimated_roi_30d: number;
}

export interface BundlesReportResponse {
  range: { key: string; from: string; to: string };
  hero: {
    left: string;
    right: string;
    count: number;
    bundle_price: number;
    lift: number;
    estimated_roi_30d: number;
    headline: string;
  } | null;
  kpis: Array<{
    id: string;
    label: string;
    value: string | number;
    format: string;
    secondary?: string;
    alert?: boolean;
  }>;
  pairs: BundlePair[];
  network: {
    nodes: Array<{
      id: string;
      label: string;
      category: string | null;
      price: number;
      weight: number;
    }>;
    edges: Array<{ source: string; target: string; weight: number; lift: number }>;
  };
  insights: SalesTimeInsight[];
  data_status: {
    orders_count: number;
    multi_item_orders: number;
    pairs_found: number;
  };
  generated_at: string;
  meta: {
    plan: PlanName;
    features: Record<string, boolean>;
    cached: boolean;
    ttl_seconds: number;
  };
}

// === AI ===
export interface AiTitleProposal {
  title: string;
  length?: number;
  structure?: string | null;
  keywords_used?: string[];
  reasoning?: string | null;
  confidence?: number;
  seo_score?: { score: number; grade: string; breakdown?: Record<string, number> } | null;
  validation?: {
    valid?: boolean;
    passed?: boolean;
    // Backend zwraca obiekty `{type, msg}` (scripts/ai_content_generator.py
    // _validate_title_minimal), ale starsze ścieżki mogą zwrócić surowe stringi.
    issues?: Array<string | { type?: string; msg?: string }>;
    warnings?: Array<string | { type?: string; msg?: string }>;
  } | null;
}

export function validationMessage(
  item: string | { type?: string; msg?: string } | null | undefined
): string {
  if (item === null || item === undefined) return "";
  if (typeof item === "string") return item;
  if (typeof item === "object") return item.msg ?? item.type ?? "";
  return String(item);
}

export interface TitleGenerationResponse {
  success: boolean;
  proposals?: AiTitleProposal[];
  cost_usd?: number | null;
  tokens_used?: number;
  model?: string;
  error?: string;
  source_offer?: {
    allegro_offer_id: string;
    current_title: string;
    category_path?: string | null;
    category_id?: string | null;
  };
}

export interface AiDescriptionSection {
  index: number;
  name: string;
  type: string;
  content_type?: string | null;
  content: string;
  confidence: number;
  tokens?: number;
}

export interface OriginalDescription {
  sections: Array<{
    index: number;
    content_html: string;
    image_urls: string[];
  }>;
  full_html: string;
  plain_text: string;
  char_count: number;
}

export interface DescriptionGenerationResponse {
  success: boolean;
  sections?: AiDescriptionSection[];
  overall_confidence?: number;
  total_tokens?: number;
  total_cost_usd?: number | null;
  model?: string;
  error?: string;
  credits_charged?: number;
  /** Finalny HTML w stylu Allegro (obrazy z oferty + teksty AI, 2-column
   * layout). Renderowany po backend'ie przez template_engine.render_description_html
   * — do podglądu Desktop / Mobile w Kroku 4. */
  rendered_html?: string | null;
  /** Allegro-format sekcje `[{items: [{type: "TEXT"|"IMAGE", content|url}]}]`
   * gotowe do PATCH /offers/{id}. Wypełniane w Kroku 5 „Wyślij". */
  allegro_sections?: Array<{ items: Array<{ type: string; content?: string; url?: string }> }> | null;
  source_offer?: {
    allegro_offer_id: string;
    current_title: string;
    category_path?: string | null;
    category_id?: string | null;
    /** „Przed vs Po" — obecny opis oferty z Allegro API (Sprint A' 2026-07-24). */
    original_description?: OriginalDescription;
  };
}

export interface SmartFormExtras {
  usp?: string;
  target?: string;
  use_cases?: string[];
  condition?: string;
  brand?: string;
  warranty_months?: number;
  in_box?: string;
  avoid?: string;
}

export interface SmartProductPayload {
  product_name: string;
  ean?: string | null;
  category_id?: string | null;
  category_path?: string | null;
  parameters?: Record<string, string>;
  extras?: SmartFormExtras;
}

export interface ListingGenerationResponse {
  success: boolean;
  title?: TitleGenerationResponse;
  description?: DescriptionGenerationResponse | null;
  error?: string;
}

export interface CategorySuggestion {
  category_id?: string | null;
  name: string;
  confidence: number;
  source?: string;
  reasoning?: string | null;
  count_in_search?: number | null;
  ai_verified?: boolean | null;
  ai_warning?: string | null;
}

/**
 * Warunek widoczności / obowiązkowości parametru — rodzic + dozwolone
 * wartości rodzica (po ID). Niewypełnione `valueIds` oznacza "wystarczy że
 * rodzic ma jakąkolwiek wartość".
 */
export interface AllegroParameterCondition {
  parameterId?: string;
  valueIds?: string[];
}

/**
 * Format Allegro dla `displayedIf` / `requiredIf` na top-level parametru.
 * AND między elementami list. OR wewnątrz `oneOfValueIds`.
 *  - `parametersWithValue`: każdy rodzic musi mieć jedną z `oneOfValueIds`.
 *  - `parametersWithoutValue`: każdy rodzic musi być pusty.
 */
export interface AllegroDisplayCondition {
  parametersWithValue?: Array<{ id: string; oneOfValueIds: string[] }>;
  parametersWithoutValue?: Array<{ id: string }>;
}

export interface AllegroCategoryParameter {
  id: string;
  name: string;
  type: "string" | "integer" | "float" | "dictionary" | "date" | "boolean" | string;
  /** Statycznie zadeklarowana obowiązkowość. Wartość efektywna jest
   * wyliczana w `parameter-engine` z uwzględnieniem `requiredIf` (top-level)
   * lub legacy `options.requiredCondition`. */
  required?: boolean;
  /** Top-level — Allegro wskazuje tu warunek widoczności parametru. Brak =
   * zawsze widoczny (chyba że jest jeszcze `options.dependsOnParameterId`). */
  displayedIf?: AllegroDisplayCondition | null;
  /** Top-level — warunek obowiązkowości. Gdy istnieje, ma pierwszeństwo nad
   * legacy `options.requiredCondition` i nad statycznym `required`. */
  requiredIf?: AllegroDisplayCondition | null;
  /** Czy parametr jest obowiązkowy przy tworzeniu nowego produktu w katalogu
   * Allegro (osobno od obowiązkowości w ofercie). */
  requiredForProduct?: boolean;
  unit?: string | null;
  description?: string | null;
  restrictions?: {
    multipleChoices?: boolean;
    range?: boolean;
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    /** Maksymalna liczba wartości jakie można wybrać (np. 3 alergeny). */
    allowedNumberOfValues?: number;
  } | null;
  dictionary?: Array<{
    value: string;
    id?: string;
    /** Per wartość słownika — ta opcja jest dostępna do wyboru tylko gdy
     * rodzic parametru ma jedną z `dependsOnValueIds` wartości. Brak pola =
     * opcja zawsze dostępna. */
    dependsOnValueIds?: string[];
  }> | null;
  options?: {
    /** ID parametru rodzica — widoczność zależy od jego wartości. */
    dependsOnParameterId?: string | null;
    /** Dozwolone wartości rodzica które włączają widoczność. Brak = każda wartość. */
    dependsOnValueIds?: string[] | null;
    /** Warunkowa obowiązkowość — parametr jest required tylko gdy spełniony. */
    requiredCondition?: AllegroParameterCondition | null;
    /** ID specjalnej wartości "Inne" w słowniku (gdy istnieje). */
    ambiguousValueId?: string | null;
    /** Czy można wpisać własną wartość gdy wybrano ambiguousValueId. */
    customValuesEnabled?: boolean;
    /** Czy parametr opisuje produkt (productSet[].product.parameters)
     * vs ofertę (offer.parameters). */
    describesProduct?: boolean;
    /** Czy parametr jest required przy propozycji nowego produktu. */
    requiredForProduct?: boolean;
    /** Czy parametr może być wariantem (kolor/rozmiar). */
    variantsAllowed?: boolean;
    variantsEqual?: boolean;
    [key: string]: unknown;
  } | null;
}

export const aiApi = {
  generateTitle: (draft_id: number) =>
    api.post<TitleGenerationResponse>("/ai/generate-title", { draft_id }),
  generateTitleFromInput: (payload: {
    product_name: string;
    category_path?: string | null;
    category_id?: string | null;
    parameters?: Record<string, string>;
    extras?: SmartFormExtras;
  }) => api.post<TitleGenerationResponse>("/ai/generate-title-from-input", payload),
  generateTitleForOffer: (allegro_offer_id: string) =>
    api.post<TitleGenerationResponse>(
      `/ai/generate-title-for-offer/${encodeURIComponent(allegro_offer_id)}`
    ),
  generateDescription: (draft_id: number, template_id: number) =>
    api.post<DescriptionGenerationResponse>("/ai/generate-description", { draft_id, template_id }),
  generateDescriptionFromInput: (payload: {
    template_id: number;
    product_name: string;
    category_path?: string | null;
    category_id?: string | null;
    parameters?: Record<string, string>;
    extras?: SmartFormExtras;
  }) =>
    api.post<DescriptionGenerationResponse>(
      "/ai/generate-description-from-input",
      payload
    ),
  generateListing: (payload: SmartProductPayload & { template_id?: number | null }) =>
    api.post<ListingGenerationResponse>("/ai/generate-listing", payload),

  expressGenerate: (payload: {
    product_name: string;
    short_description: string;
    price: number;
    currency?: string;
    stock?: number;
    condition?: string;
    image_ids?: number[];
    session_id?: string;
    ean?: string | null;
    template_id?: number | null;
    gallery_slots?: number;
  }) =>
    api.post<{
      draft_id: number;
      needs_category_choice: boolean;
      category_choices: Array<{
        category_id: string | null;
        category_path: string | null;
        confidence: number;
        source?: string | null;
      }> | null;
      category: { id: string; path: string | null; confidence: number } | null;
      title: string | null;
      title_alternatives_count: number;
      description_sections_count: number;
      parameters_filled: number;
      parameters_required: number;
      images_count: number;
      current_step: number;
    }>("/ai/express/generate", payload),

  expressFinalizeCategory: (payload: {
    draft_id: number;
    category_id: string;
    category_path?: string | null;
  }) =>
    api.post<{
      draft_id: number;
      category: { id: string; path: string | null; confidence: number };
      parameters_filled: number;
      parameters_required: number;
    }>("/ai/express/finalize-category", payload),
  generateDescriptionForOffer: (allegro_offer_id: string, template_id: number) =>
    api.post<DescriptionGenerationResponse>(
      `/ai/generate-description-for-offer/${encodeURIComponent(allegro_offer_id)}`,
      { template_id }
    ),
  regenerateSection: (
    draft_id: number,
    section_index: number,
    custom_instructions?: string
  ) =>
    api.post<{
      success: boolean;
      updated_section?: AiDescriptionSection;
      cost_usd?: number | null;
      model?: string | null;
      error?: string;
    }>("/ai/regenerate-section", {
      draft_id,
      section_index,
      custom_instructions,
    }),
  generateSectionForOffer: (
    allegro_offer_id: string,
    payload: {
      instructions: string;
      content_type?: "paragraph" | "list" | "bullet_list" | "numbered_list";
      section_name?: string;
      existing_content?: string;
      heading_level?: "h1" | "h2" | null;
      allowed_emojis?: string[];
    }
  ) =>
    api.post<{
      success: boolean;
      content?: string;
      content_type?: string;
      section_name?: string;
      confidence?: number;
      tokens?: number;
      total_cost_usd?: number | null;
      model?: string;
      error?: string;
    }>(
      `/ai/generate-section-for-offer/${encodeURIComponent(allegro_offer_id)}`,
      payload
    ),
  validate: (data: { title?: string; description_html?: string }) =>
    api.post("/ai/validate", data),
};

// === M44: Graphic Orders ===

export type GraphicOrderTier = "multi" | "pro";
export type GraphicOrderStatus =
  | "new"
  | "in_progress"
  | "delivered"
  | "revision_requested"
  | "delivered_revision"
  | "accepted"
  | "cancelled";

export type GraphicOrderTierInfo = {
  id: GraphicOrderTier;
  name: string;
  description: string;
  price_net: number;
  price_gross: number;
  vat_rate: number;
  delivery_hours: number;
  min_images: number;
  max_images: number;
  features: string[];
};

export type GraphicOrder = {
  id: number;
  client_id: number;
  tier: GraphicOrderTier;
  status: GraphicOrderStatus;
  price_net: number;
  price_gross: number;
  vat_rate: number;
  source_image_ids: number[];
  delivery_image_ids: number[] | null;
  offer_title: string | null;
  notes: string | null;
  use_brand_layer: number;
  brand_snapshot: Record<string, unknown> | null;
  designer_notes: string | null;
  revision_used: number;
  revision_note: string | null;
  sla_deadline: string | null;
  created_at: string;
  started_at: string | null;
  delivered_at: string | null;
  accepted_at: string | null;
  cancelled_at: string | null;
  invoice_issued: number;
  invoice_issued_at: string | null;
};

export const graphicOrdersApi = {
  // Pricing info (po zalogowaniu)
  tiers: () =>
    api.get<{
      tiers: GraphicOrderTierInfo[];
      vat_rate: number;
      currency: string;
    }>("/graphic-orders/tiers"),

  // === Klient ===
  create: (payload: {
    tier: GraphicOrderTier;
    source_image_ids: number[];
    offer_title?: string;
    notes?: string;
    use_brand_layer?: boolean;
  }) => api.post<GraphicOrder>("/graphic-orders", payload),

  listMine: () =>
    api.get<{ orders: GraphicOrder[] }>("/graphic-orders/mine"),

  get: (id: number) => api.get<GraphicOrder>(`/graphic-orders/${id}`),

  cancel: (id: number) =>
    api.post<GraphicOrder>(`/graphic-orders/${id}/cancel`),

  accept: (id: number) =>
    api.post<GraphicOrder>(`/graphic-orders/${id}/accept`),

  requestRevision: (id: number, revision_note: string) =>
    api.post<GraphicOrder>(`/graphic-orders/${id}/request-revision`, {
      revision_note,
    }),

  // === Admin (grafik) ===
  checkAdmin: () =>
    api.get<{ is_admin: boolean; client_id: number }>(
      "/graphic-orders/admin/me"
    ),

  adminQueue: (status?: GraphicOrderStatus) =>
    api.get<{ orders: GraphicOrder[] }>("/graphic-orders/admin/queue", {
      params: status ? { status } : undefined,
    }),

  adminStart: (id: number) =>
    api.post<GraphicOrder>(`/graphic-orders/admin/${id}/start`),

  adminMarkDelivered: (
    id: number,
    payload: { delivery_image_ids: number[]; designer_notes?: string }
  ) =>
    api.post<GraphicOrder>(
      `/graphic-orders/admin/${id}/mark-delivered`,
      payload
    ),
};

// === M45: Admin Panel (RBAC + KPI + CRM + audit + impersonation) ===

export type AdminRole =
  | "owner"
  | "super_admin"
  | "cs_manager"
  | "sales"
  | "graphic"
  | "accountant"
  | "support"
  | "viewer"
  | "user";

export type AdminMeResponse = {
  role: AdminRole;
  permissions: string[];
  is_admin: boolean;
  is_owner: boolean;
  active_impersonation: {
    id: number;
    admin_client_id: number;
    target_client_id: number;
    target_email: string;
    target_name: string | null;
    reason: string;
    started_at: string;
  } | null;
};

export type AdminClient = {
  id: number;
  email: string;
  name: string | null;
  plan: string;
  role: AdminRole;
  is_active: number;
  is_admin: number;
  must_change_password?: number;
  created_at: string;
  last_login_at: string | null;
  stats?: {
    orders_total: number;
    orders_active: number;
    lifetime_value_net: number;
  };
  last_impersonation?: {
    id: number;
    admin_client_id: number;
    started_at: string;
    ended_at: string | null;
    reason: string;
  } | null;
};

export type AdminActivityItem = {
  id: number;
  actor_client_id: number;
  actor_email: string | null;
  actor_name: string | null;
  actor_role: AdminRole | null;
  action: string;
  action_label: string;
  target_type: string | null;
  target_id: string | null;
  metadata: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
};

export interface FirstRunSettings {
  step_seconds: Record<FirstRunStepKey, number>;
  goal_multipliers: number[];
  goal_rounding: number;
  audit_wait_minutes: number;
  active_since: string | null;
}

/** Ustawienia pulpitu w panelu admina (#624). */
export interface PulpitSettings {
  forecast_min_day: number;
  forecast_lookback_days: number;
  forecast_min_history_days: number;
  visit_session_gap_minutes: number;
  visit_min_session_seconds: number;
  since_visit_max_items: number;
  fees_merge_pct: number;
  competition_days: number;
  competition_drop_pct: number;
  upcoming_horizon_days: number;
  upcoming_max_items: number;
  credits_reminder_days: number;
  calendar_events: PulpitCalendarEvent[];
  layout_defaults: Record<PulpitRole, PulpitZoneId[]>;
  layout_zones: PulpitZoneId[];
  limits: Record<PulpitNumericSetting, { min: number; max: number }>;
}

export type PulpitNumericSetting = Exclude<
  keyof PulpitSettings,
  "limits" | "calendar_events" | "layout_defaults" | "layout_zones"
>;

/**
 * Synchronizacja Allegro w panelu admina (#683, #690): interwały w minutach
 * oraz limity jednego przebiegu wiadomości od kupujących i spraw (dyskusje,
 * reklamacje).
 */
export interface SyncSettings {
  orders_incremental_minutes: number;
  offers_minutes: number;
  billing_incremental_minutes: number;
  /** Co ile minut pobierać wiadomości od kupujących (#690). */
  messages_minutes: number;
  /** Twardy limit stron listy wątków (po 19) i wiadomości jednego wątku w przebiegu. */
  messages_max_list_pages: number;
  /** Ile wątków na sklep pobrać w jednym przebiegu automatycznym. */
  messages_max_threads: number;
  /** Ile wątków na sklep pobrać przy ręcznym „Synchronizuj”. */
  messages_manual_max_threads: number;
  /** Przerwa (min) między ręcznymi synchronizacjami sklepu; 0 wyłącza. */
  messages_manual_cooldown_minutes: number;
  /** Ile nowych wiadomości sklasyfikować w jednym przebiegu (0 wyłącza). */
  messages_classify_max: number;
  /** Co ile minut pobierać dyskusje i reklamacje (#690). */
  issues_minutes: number;
  /** Limit stron listy spraw (po 100) i czatu jednej sprawy w przebiegu. */
  issues_max_list_pages: number;
  /** Ile zmienionych spraw na sklep pobrać z Allegro w jednym przebiegu. */
  issues_max_fetches: number;
  limits: Record<SyncIntervalSetting, { min: number; max: number }>;
  defaults: Record<SyncIntervalSetting, number>;
  /** Rola wdrożenia API to TEST (#683). */
  test_role: boolean;
  /** TEST z przełącznikiem roli HEALSY_TEST_ALLEGRO_SYNC: scheduler Allegro może działać. */
  test_sync_available: boolean;
  /** Przełącznik panelu „Synchronizacja Allegro na TEST”, domyślnie wyłączony. */
  test_allegro_enabled: boolean;
}

export type SyncIntervalSetting = Exclude<
  keyof SyncSettings,
  "limits" | "defaults" | "test_role" | "test_sync_available" | "test_allegro_enabled"
>;

export interface PulpitCalendarEvent {
  date: string;
  title: string;
  hint: string | null;
  yearly: boolean;
}

export const adminApi = {
  me: () => api.get<AdminMeResponse>("/admin/me"),

  dashboardKpi: () =>
    api.get<{
      clients: {
        total: number;
        active_30d: number;
        trial: number;
        paid: number;
        new_this_month: number;
        conversion_rate_pct: number;
      };
      graphic_orders: {
        active: number;
        sla_breach: number;
        accepted_this_month: number;
        revenue_this_month: number;
        revenue_total: number;
      };
      finance: {
        mrr_estimate: number;
        arr_estimate: number;
        outstanding: number;
        currency: string;
        note: string;
      };
      generated_at: string;
    }>("/admin/dashboard/kpi"),

  // Clients
  listClients: (params?: {
    q?: string;
    plan?: string;
    role?: AdminRole;
    only_inactive?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<{
      total: number;
      items: AdminClient[];
      limit: number;
      offset: number;
    }>("/admin/clients", { params }),

  getClient: (id: number) => api.get<AdminClient>(`/admin/clients/${id}`),

  // Impersonation
  impersonateStart: (clientId: number, reason: string) =>
    api.post<{
      session: {
        session_id: number;
        admin_client_id: number;
        target_client_id: number;
        target_email: string;
        target_name: string | null;
        reason: string;
        started_at: string;
        expires_at: string;
      };
    }>(`/admin/clients/${clientId}/impersonate`, { reason }),

  impersonateEnd: () =>
    api.post<{ ok: boolean; original_admin_id: number }>(
      "/admin/impersonate/end"
    ),

  impersonateActive: () =>
    api.get<{ active: AdminMeResponse["active_impersonation"] }>(
      "/admin/impersonate/active"
    ),

  impersonateHistory: (params?: { days_back?: number; limit?: number }) =>
    api.get<{ sessions: Array<Record<string, unknown>> }>(
      "/admin/impersonate/history",
      { params }
    ),

  // Activity log
  activityLog: (params?: {
    actor_client_id?: number;
    action?: string;
    target_type?: string;
    target_id?: string;
    days_back?: number;
    limit?: number;
  }) =>
    api.get<{ items: AdminActivityItem[]; count: number }>("/admin/activity", {
      params,
    }),

  // Team & Roles
  listRoles: () =>
    api.get<{
      roles: Array<{ id: AdminRole; label: string; description: string }>;
      permissions: Array<{ key: string; label: string; category: string }>;
      role_permissions: Record<AdminRole, string[]>;
    }>("/admin/roles"),

  listTeam: () =>
    api.get<{ members: AdminClient[] }>("/admin/team"),

  assignRole: (clientId: number, role: AdminRole) =>
    api.post<{ ok: boolean; role: AdminRole }>(`/admin/users/${clientId}/role`, {
      role,
    }),

  // Team management
  inviteMember: (payload: {
    email: string;
    role: AdminRole;
    name?: string;
  }) =>
    api.post<{
      ok: boolean;
      client_id: number;
      email: string;
      name: string;
      role: AdminRole;
      activation_sent: boolean;
      note: string;
    }>("/admin/team/invite", payload),

  suspendMember: (clientId: number, suspend: boolean) =>
    api.post<{ ok: boolean; is_active: number; email: string }>(
      `/admin/team/${clientId}/suspend`,
      { suspend }
    ),

  // Work stats (mój personal graphic dashboard)
  statsWork: (params?: { target_client_id?: number; days_back?: number }) =>
    api.get<{
      target_client_id: number;
      days_back: number;
      actions: { orders_started: number; orders_delivered: number };
      orders: {
        touched: number;
        accepted: number;
        revenue_net: number;
        revisions: number;
        revision_rate_pct: number;
      };
      sla: { compliant: number; total: number; compliance_pct: number };
      avg_duration_hours: number;
      daily_activity: Array<{ day: string; count: number }>;
    }>("/admin/stats/work", { params }),

  // System settings
  getPricingSettings: () =>
    api.get<{
      vat_rate: number;
      graphic: {
        multi_net: number;
        multi_gross: number;
        pro_net: number;
        pro_gross: number;
      };
      sla_graphic_hours: number;
      free_revisions: number;
      annual_discount_pct: number;
    }>("/admin/settings/pricing"),

  getPulpitSettings: () => api.get<PulpitSettings>("/admin/settings/pulpit"),
  updatePulpitSettings: (payload: Partial<Omit<PulpitSettings, "limits" | "layout_zones">>) =>
    api.post<{ ok: boolean; changes: Array<{ key: string; old: unknown; new: unknown }>; settings: PulpitSettings }>(
      "/admin/settings/pulpit",
      payload,
    ),

  getSyncSettings: () => api.get<SyncSettings>("/admin/settings/sync"),
  updateSyncSettings: (payload: Partial<Pick<SyncSettings, SyncIntervalSetting | "test_allegro_enabled">>) =>
    api.post<{ ok: boolean; changes: Array<{ key: string; old: unknown; new: unknown }>; settings: SyncSettings }>(
      "/admin/settings/sync",
      payload,
    ),

  getFirstRunSettings: () =>
    api.get<FirstRunSettings>("/admin/settings/first-run"),
  updateFirstRunSettings: (payload: Partial<FirstRunSettings>) =>
    api.post<{ ok: boolean; changes: Array<{ key: string; old: unknown; new: unknown }> }>(
      "/admin/settings/first-run",
      payload,
    ),

  updatePricingSettings: (payload: {
    multi_net?: number;
    pro_net?: number;
    vat_rate?: number;
    sla_graphic_hours?: number;
    free_revisions?: number;
    annual_discount_pct?: number;
  }) =>
    api.post<{ ok: boolean; changes: Array<{ key: string; old: unknown; new: unknown }> }>(
      "/admin/settings/pricing",
      payload
    ),
};

// === M46b: Billing / Subscriptions / Modules ===

export type SubscriptionPlan = {
  key: string;
  name: string;
  label: string | null;
  price_monthly_net: number;
  included_modules: string[];
  max_extra_modules: number;
  max_offers: number | null;
  max_graphic_orders_per_month: number;
  is_active: number;
  is_recommended: number;
  sort_order: number;
};

export type SubscriptionModuleDef = {
  key: string;
  name: string;
  category: string;
  price_monthly_net: number;
  description: string | null;
  icon: string | null;
  is_active: number;
  sort_order: number;
};

export type ClientSubscription = {
  id: number;
  client_id: number;
  plan_key: string;
  billing_cycle: "monthly" | "annual";
  status: "trial" | "active" | "canceled" | "past_due";
  started_at: string;
  trial_ends_at: string | null;
  current_period_start: string;
  current_period_end: string | null;
  canceled_at: string | null;
  cancel_reason: string | null;
};

export type Payment = {
  id: number;
  client_id: number;
  client_email: string | null;
  client_name: string | null;
  type: "subscription" | "graphic_order" | "module_addon";
  amount_net: number;
  amount_gross: number;
  vat_rate: number;
  currency: string;
  status: "pending" | "paid" | "failed" | "refunded" | "canceled";
  description: string | null;
  related_type: string | null;
  related_id: number | null;
  payu_order_id: string | null;
  payu_redirect_url: string | null;
  invoice_issued: number;
  invoice_issued_at: string | null;
  created_at: string;
  paid_at: string | null;
  refunded_at: string | null;
  failed_at: string | null;
  failure_reason: string | null;
  metadata: Record<string, unknown> | null;
};

export type BillingQuote = {
  plan_key: string;
  plan_net: number;
  addons_count: number;
  addons_net: number;
  monthly_net: number;
  monthly_gross: number;
  billing_cycle: "monthly" | "annual";
  period_net: number;
  period_gross: number;
  annual_discount_pct: number;
  vat_rate: number;
};

export type PublicPricing = {
  plans: SubscriptionPlan[];
  modules: SubscriptionModuleDef[];
  currency: string;
  annual_discount_pct: number;
  graphic_prices_net: Record<string, number>;
  vat_rate: number;
};

// ────────────────────────────────────────────────────────────────────
// Credits — jednolita waluta AI (od 2026-07-21)
// ────────────────────────────────────────────────────────────────────
export interface CreditsSummary {
  balance: number;
  monthly_allowance: number;
  next_reset_at: string | null;
  usage_this_period: number;
  low: boolean;
}

export interface CreditsLedgerEntry {
  id: number;
  delta: number;
  reason: string;
  action_key: string | null;
  ref_type: string | null;
  ref_id: string | null;
  expired_at: string | null;
  occurred_at: string;
  metadata_json: string | null;
}

/** GET /me/billing/overview — zakładka Rozliczenia (#663). */
export interface CreditsUsageByAction {
  action_key: string;
  label: string;
  count: number;
  credits: number;
}

export interface BillingOverview {
  subscription: ClientSubscription | null;
  plan: SubscriptionPlan | null;
  active_modules: string[];
  credits: {
    balance: number;
    monthly_allowance: number;
    monthly_left: number;
    extra: number;
    used_in_period: number;
    period_start: string | null;
    /** Początek okna kafelków i limitów: okres albo ostatnie 30 dni planu rocznego. */
    usage_since: string | null;
    next_reset_at: string | null;
    months_left: number | null;
    usage_by_action: CreditsUsageByAction[];
  };
  usage: { offers: number; graphic_orders: number | null };
  plan_credits: Record<string, number>;
  next_payment: { date: string | null; amount_gross: number; amount_net: number } | null;
}

export interface CreditCost {
  action_key: string;
  cost: number;
  description: string | null;
  is_active: number;
}

export interface CreditPlan {
  plan_key: string;
  monthly_credits: number;
  welcome_bonus_credits: number;
}

// ────────────────────────────────────────────────────────────────────
// Bulk AI — batch generacja z SSE progress (Faza 3, 2026-07-24)
// ────────────────────────────────────────────────────────────────────
export const bulkAiApi = {
  startDescriptions: (
    template_id: number,
    allegro_offer_ids: string[],
    session_prompt?: string,
  ) =>
    api.post<{ job_id: string }>("/ai/bulk-generate-descriptions", {
      template_id,
      allegro_offer_ids,
      ...(session_prompt ? { session_prompt } : {}),
    }),
  status: (job_id: string) =>
    api.get<{
      job_id: string;
      total: number;
      done: number;
      failed: number;
      finished: boolean;
      created_at: number;
    }>(`/ai/bulk-jobs/${job_id}/status`),

  /**
   * Przelicza rendered_html + allegro_sections po edycji użytkownika w Kroku 4
   * (tryb „Edytuj"). Klient wysyła edytowane teksty per sekcja, dostaje z
   * powrotem świeży podgląd + payload gotowy do PATCH /offers/{id}.
   */
  reRenderDescription: (
    template_id: number,
    allegro_offer_id: string,
    section_texts: Record<string, string>,
  ) =>
    api.post<{
      rendered_html: string | null;
      allegro_sections: Array<{
        items: Array<{ type: string; content?: string; url?: string }>;
      }>;
    }>("/ai/description/re-render", {
      template_id,
      allegro_offer_id,
      section_texts,
    }),

  /**
   * Subskrybuje SSE stream jobu bulkowego. Zwraca AbortController do przerwania.
   * Nie używa EventSource — potrzebujemy pełnej kontroli nad readerem.
   *
   * Wywoływane onEvent(event, data) dla każdego payloadu. Kończy się po
   * eventach `done`/`error` albo abort'em.
   */
  streamJob: async (
    job_id: string,
    onEvent: (event: string, data: Record<string, unknown>) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    return _sseStreamJob(`/ai/bulk-jobs/${job_id}/stream`, onEvent, signal);
  },

  // ─── Tytułomat 2.0 — bulk generacja tytułów ─────────────────────────

  startTitles: (
    allegro_offer_ids: string[],
    opts?: {
      session_prompt?: string;
      include_parameters?: boolean;
      include_description?: boolean;
    },
  ) =>
    api.post<{ job_id: string }>("/ai/bulk-generate-titles", {
      allegro_offer_ids,
      session_prompt: opts?.session_prompt,
      include_parameters: opts?.include_parameters ?? true,
      include_description: opts?.include_description ?? true,
    }),

  streamTitleJob: async (
    job_id: string,
    onEvent: (event: string, data: Record<string, unknown>) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    return _sseStreamJob(`/ai/bulk-title-jobs/${job_id}/stream`, onEvent, signal);
  },

  cancelTitleJob: (job_id: string) =>
    api.post<{ cancelled: boolean }>(`/ai/bulk-title-jobs/${job_id}/cancel`),

  // KO2: status polling dla tytulow (identyczny format jak bulk-jobs)
  titleJobStatus: (job_id: string) =>
    api.get<{
      job_id: string;
      total: number;
      done: number;
      failed: number;
      finished: boolean;
      created_at: number;
    }>(`/ai/bulk-title-jobs/${job_id}/status`),

  // Duplikuj ofertę z nowym tytułem (Krok 3 Tytułomat → „Duplikuj A/B").
  cloneOfferWithTitle: (
    allegro_offer_id: string,
    new_title: string,
    publish: boolean = false,
  ) =>
    api.post<{
      success: boolean;
      new_offer_id: string | null;
      new_title: string;
      source_offer_id: string;
      status: string;
    }>("/offers/clone-with-title", {
      allegro_offer_id,
      new_title,
      publish,
    }),
};

// ─── SSE helper wspólny dla bulk-descriptions i bulk-titles ──────────
async function _sseStreamJob(
  path: string,
  onEvent: (event: string, data: Record<string, unknown>) => void,
  signal?: AbortSignal,
): Promise<void> {
  const resp = await fetch(`/api/v1${path}`, {
    credentials: "same-origin",
    signal,
  });
  if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const lines = raw.split("\n");
      let event = "message";
      let data = "";
      for (const line of lines) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data = line.slice(5).trim();
      }
      if (data) {
        try {
          onEvent(event, JSON.parse(data));
        } catch {
          onEvent(event, { raw: data });
        }
      }
      if (event === "done" || event === "error") return;
    }
  }
}

// ─── AI Inbox (skrzynka wyników bulk generacji, Stage A 2026-07-30) ──

export interface InboxJob {
  id: number;
  // KO12 1d (2026-08-07): dodane "listing" — kreator smart-form
  job_type: "description" | "title" | "image" | "parameters" | "clone" | "listing";
  template_id?: number | null;
  session_prompt?: string | null;
  context_flags?: Record<string, unknown>;
  status:
    | "running"
    | "done"
    | "reviewed_pushed"
    | "reviewed_discarded"
    | "cancelled";
  n_offers: number;
  n_success: number;
  n_failed: number;
  started_at: string;
  completed_at?: string | null;
  reviewed_at?: string | null;
  name?: string | null;
  offers_preview?: Array<{
    allegro_offer_id: string;
    title?: string | null;
    main_image_url?: string | null;
  }>;
}

export interface InboxJobResult {
  id: number;
  allegro_offer_id: string;
  success: boolean;
  result_data?: Record<string, unknown> | null;
  error?: string | null;
  review_action?: "accepted" | "rejected" | "edited" | null;
  reviewed_at?: string | null;
  pushed_at?: string | null;
}

export interface InboxJobDetail extends InboxJob {
  offer_ids: string[];
  results: InboxJobResult[];
}

// ============================================================================
// Parameters API (2026-08-06) — bulk uzupełnianie parametrów AI
// ============================================================================

export interface ParameterSuggestion {
  param_id: string;
  param_name: string;
  suggested_value: string;
  confidence: number;
  reasoning: string;
  is_required: boolean;
  options?: string[] | null;
  type?: string | null;
  auto_check: boolean;
}

export interface ParameterCurrentParam {
  id: string;
  name: string;
  values?: string[];
  values_labels?: string[];
}

export interface ParameterMissingParam {
  id: string;
  name: string;
  required: boolean;
  options?: string[] | null;
  type?: string | null;
}

export interface ParameterOfferResult {
  success: boolean;
  offer_id: string;
  title: string;
  main_image_url?: string | null;
  description_plain: string;
  category_id?: string | null;
  category_name?: string | null;
  current_parameters: ParameterCurrentParam[];
  missing_parameters: ParameterMissingParam[];
  suggestions: ParameterSuggestion[];
  stats: {
    total: number;
    filled: number;
    missing: number;
    ai_filled: number;
    high_confidence: number;
    required_filled: number;
    required_missing: number;
  };
  error?: string;
}

// ============================================================================
// Offer Clone API (2026-08-06) — bulk kopiowanie ofert między kontami
// ============================================================================

export interface OfferCloneResult {
  success: boolean;
  source_offer_id: string;
  target_shop_id?: string | number | null;
  new_offer_id?: string;
  new_title?: string;
  mode?: string;
  status?: string;
  alt_category_suggestion?: {
    id: string;
    name: string;
    confidence?: number;
  } | null;
  warnings?: string[];
  error?: string;
}

export const offerCloneApi = {
  single: (payload: {
    source_offer_id: string;
    source_shop_id?: string | number | null;
    target_shop_id?: string | number | null;
    mode: "exact" | "regenerate";
    new_ean?: string;
    regenerate_title?: boolean;
    regenerate_description?: boolean;
    regenerate_category?: boolean;
    publish?: boolean;
  }) => api.post<OfferCloneResult>("/offer-clone/single", payload),

  batch: (payload: {
    source_offer_ids: string[];
    source_shop_id?: string | number | null;
    target_shop_id?: string | number | null;
    mode: "exact" | "regenerate";
    regenerate_title?: boolean;
    regenerate_description?: boolean;
    regenerate_category?: boolean;
    ean_map?: Record<string, string>;
    template_id?: number | null;
    // KO6 (2026-08-07): Allegro wymaga tych IDs zeby zaakceptowac oferte
    shipping_rate_id?: string | null;
    responsible_person_id?: string | null;
    responsible_producer_id?: string | null;
    // KO7 (2026-08-07): dodatkowe ustawienia
    implied_warranty_id?: string | null;
    warranty_id?: string | null;
    return_policy_id?: string | null;
    price_override?: number | null;
    quantity_override?: number | null;
    copy_attachments?: boolean;
    copy_sku?: boolean;
    // KO8 (2026-08-07): safety info GPSR (null = inherit z source)
    safety_information_text?: string | null;
    publish?: boolean;
  }) =>
    api.post<{
      success: boolean;
      inbox_job_id: number | null;
      // KO2: async generacja tytulow/opisow po sync clone (osobne joby
      // trackowalne w Skrzynce AI albo przez /ai/bulk-jobs/{id}/status)
      title_job_id?: string | null;
      desc_job_id?: string | null;
      n_success: number;
      n_failed: number;
      total: number;
      results?: Array<{
        source_offer_id: string;
        success: boolean;
        new_offer_id?: string | null;
        new_title?: string | null;
        error?: string | null;
        warnings?: string[];
      }>;
    }>("/offer-clone/batch", payload),

  // 2026-08-06: Publikacja draftow (DRAFT → ACTIVE na Allegro)
  publish: (payload: {
    allegro_offer_ids: string[];
    target_shop_id?: number | null;
  }) =>
    api.post<{
      success: boolean;
      n_success: number;
      n_failed: number;
      total: number;
      results: Array<{
        allegro_offer_id: string;
        success: boolean;
        status?: string;
        error?: string | null;
      }>;
    }>("/offer-clone/publish", payload),

  // KO8 (2026-08-07): pobierz defaults z pojedynczej source oferty
  // (shipping_rate_id, responsible_person/producer_id, safety_information).
  // Wywolywane po wybraniu 1. oferty w Kroku 1 → wypelnia dropdowny Kroku 3.
  sourceDefaults: (source_offer_id: string, source_shop_id?: number | null) =>
    api.get<{
      source_offer_id: string;
      shipping_rate_id: string | null;
      responsible_person_id: string | null;
      responsible_producer_id: string | null;
      safety_information: {
        type: string;
        text?: string;
        url?: string;
      } | null;
      safety_information_text: string | null;
    }>(`/offer-clone/source-defaults`, {
      params: {
        source_offer_id,
        ...(source_shop_id != null ? { source_shop_id } : {}),
      },
    }),

  // KO4 (2026-08-06): Wyslij preview payloady na Allegro. Uzywane w Kroku 4
  // wizardu — dopiero teraz oferta powstaje na Allegro (i od razu ACTIVE
  // jesli activate=true, default).
  send: (payload: {
    inbox_job_id: number;
    source_offer_ids: string[];
    target_shop_id?: number | null;
    activate?: boolean;
  }) =>
    api.post<{
      success: boolean;
      n_success: number;
      n_failed: number;
      total: number;
      results: Array<{
        source_offer_id: string;
        success: boolean;
        new_offer_id?: string | null;
        status?: string;
        error?: string | null;
      }>;
    }>("/offer-clone/send", payload),
};

export const parametersApi = {
  suggestBatch: (offer_ids: string[]) =>
    api.post<{
      success: boolean;
      inbox_job_id: number | null;
      n_success: number;
      n_failed: number;
      total: number;
    }>("/parameters/suggest-batch", { offer_ids }),
  apply: (
    offer_id: string,
    accepted_params: Array<{ param_id: string; param_name: string; value: string }>,
  ) =>
    api.post<{
      success: boolean;
      offer_id: string;
      applied_count: number;
    }>("/parameters/apply", { offer_id, accepted_params }),
};

export const aiInboxApi = {
  list: (status?: string, limit = 50) =>
    api.get<{ jobs: InboxJob[] }>("/ai-inbox", {
      params: { status, limit },
    }),

  pendingCount: () => api.get<{ count: number }>("/ai-inbox/pending-count"),

  get: (job_id: number) => api.get<InboxJobDetail>(`/ai-inbox/${job_id}`),

  archive: (job_id: number) =>
    api.post<{ ok: boolean }>(`/ai-inbox/${job_id}/archive`),

  markPushed: (job_id: number) =>
    api.post<{ ok: boolean }>(`/ai-inbox/${job_id}/mark-pushed`),

  reviewResult: (
    job_id: number,
    result_id: number,
    action: "accepted" | "rejected" | "edited",
    pushed: boolean = false,
  ) =>
    api.post<{ ok: boolean }>(
      `/ai-inbox/${job_id}/results/${result_id}/review`,
      { action, pushed },
    ),
};

// ─── Snapshots (undo dla bulk operacji, Faza 3 2026-07-30) ──────────

export interface SnapshotBatch {
  batch_id: string;
  operation_type: string;
  n_snapshots: number;
  n_restored: number;
  created_at: string;
  expires_at: string;
}

// ── Sprint IV/1: AI Act rejestr grafik AI (Agata) ─────────────────────────

export interface AiContentEvent {
  id: number;
  client_id: number;
  image_id: number | null;
  action: "generated" | "modified" | "labeled" | "exported";
  ai_source:
    | "mockup_engine"
    | "gallery_frames"
    | "ai_image_generator"
    | "thumbnail_composer_v2"
    | string;
  ai_model: string | null;
  persona_code: string | null;
  prompt: string | null;
  input_hash: string | null;
  export_target: string | null;
  metadata_json: string | null;
  created_at: string;
}

export interface AiContentSummary {
  total_events: number;
  per_source: Array<{ ai_source: string; n: number }>;
  per_action: Array<{ action: string; n: number }>;
  images: {
    total_ai: number;
    labeled: number;
    unlabeled: number;
    compliance_pct: number;
  };
}

export const aiContentRegistryApi = {
  list: (params?: {
    limit?: number;
    offset?: number;
    ai_source?: string;
    action?: string;
  }) =>
    api.get<{
      items: AiContentEvent[];
      total: number;
      limit: number;
      offset: number;
    }>("/ai-content-registry", { params }),
  summary: () =>
    api.get<AiContentSummary>("/ai-content-registry/summary"),
};

export interface RestoreResult {
  restored: number;
  failed: number;
  already_restored?: number;
  errors?: Array<{ allegro_offer_id: string; error: string }>;
  error?: string;
}

export const snapshotsApi = {
  createBatch: (operation_type: string) =>
    api.post<{ batch_id: string; operation_type: string }>(
      "/snapshots/batch/create",
      { operation_type },
    ),

  addSnapshot: (
    batch_id: string,
    allegro_offer_id: string,
    revert_action: { type: "patch"; body: Record<string, unknown> },
    meta?: Record<string, unknown>,
  ) =>
    api.post<{ snapshot_id: number }>(`/snapshots/batch/${batch_id}/snapshot`, {
      allegro_offer_id,
      revert_action,
      ...(meta ? { meta } : {}),
    }),

  restore: (batch_id: string) =>
    api.post<RestoreResult>(`/snapshots/batch/${batch_id}/restore`),

  getBatch: (batch_id: string) =>
    api.get<SnapshotBatch & { snapshots: Array<Record<string, unknown>> }>(
      `/snapshots/batch/${batch_id}`,
    ),

  recent: (limit = 20) =>
    api.get<{ batches: SnapshotBatch[] }>("/snapshots/recent", {
      params: { limit },
    }),
};

export const creditsApi = {
  summary: () => api.get<CreditsSummary>("/me/credits/summary"),
  history: (params: { limit?: number; offset?: number } = {}) =>
    api.get<{ entries: CreditsLedgerEntry[]; limit: number; offset: number }>(
      "/me/credits/history",
      { params },
    ),
  pricing: () =>
    api.get<{ actions: CreditCost[]; plans: CreditPlan[] }>("/credits/pricing"),
};

export const billingApi = {
  publicPricing: () =>
    api.get<PublicPricing>("/billing/public-pricing"),

  // Public catalog
  plans: () =>
    api.get<{
      plans: SubscriptionPlan[];
      modules: SubscriptionModuleDef[];
      currency: string;
    }>("/billing/plans"),

  modules: () => api.get<{ modules: SubscriptionModuleDef[] }>("/billing/modules"),

  quote: (payload: {
    plan_key: string;
    addon_module_keys: string[];
    billing_cycle: "monthly" | "annual";
  }) => api.post<BillingQuote>("/billing/quote", payload),

  // User subscription
  overview: () => api.get<BillingOverview>("/me/billing/overview"),

  paynowCheck: (paymentId: number) =>
    api.get<{ payment_id: number; status: string }>(`/payments/paynow-check/${paymentId}`),

  mySubscription: () =>
    api.get<{
      subscription: ClientSubscription | null;
      plan: SubscriptionPlan | null;
      active_modules: string[];
    }>("/me/subscription"),

  startCheckout: (payload: {
    plan_key: string;
    addon_module_keys: string[];
    billing_cycle: "monthly" | "annual";
  }) =>
    api.post<{
      payment_id: number;
      amount_gross: number;
      redirect_url: string;
      quote: BillingQuote;
    }>("/me/subscription/start-checkout", payload),

  addModule: (module_key: string) =>
    api.post<{ ok: boolean; active_modules: string[] }>(
      "/me/subscription/add-module",
      { module_key }
    ),

  removeModule: (module_key: string) =>
    api.post<{ ok: boolean; active_modules: string[] }>(
      "/me/subscription/remove-module",
      { module_key }
    ),

  cancelMySubscription: (reason?: string) =>
    api.post<{ ok: boolean }>("/me/subscription/cancel", { reason }),

  // Admin
  adminListPlans: () =>
    api.get<{ plans: SubscriptionPlan[]; modules: SubscriptionModuleDef[] }>(
      "/admin/plans"
    ),

  adminUpdatePlan: (key: string, payload: Partial<SubscriptionPlan>) =>
    api.patch<SubscriptionPlan>(`/admin/plans/${key}`, payload),

  adminUpdateModule: (key: string, payload: Partial<SubscriptionModuleDef>) =>
    api.patch<SubscriptionModuleDef>(`/admin/modules/${key}`, payload),

  adminListPayments: (params?: {
    client_id?: number;
    status?: string;
    payment_type?: string;
    days_back?: number;
    limit?: number;
  }) =>
    api.get<{ items: Payment[]; count: number }>("/admin/payments", { params }),

  adminPaymentStats: (params?: { days_back?: number }) =>
    api.get<{
      paid_count: number;
      paid_net: number;
      pending_count: number;
      pending_net: number;
      failed_count: number;
      refunded_count: number;
      refunded_net: number;
      by_type: Record<string, number>;
    }>("/admin/payments/stats", { params }),

  adminRefund: (paymentId: number, reason?: string) =>
    api.post<Payment>(`/admin/payments/${paymentId}/refund`, { reason }),

  adminListSubscriptions: (status?: string) =>
    api.get<{
      items: Array<ClientSubscription & {
        plan_name: string | null;
        plan_price: number | null;
        client_email: string | null;
        client_name: string | null;
      }>;
      mrr: {
        mrr_net: number;
        arr_net: number;
        active_subscriptions: number;
        trial_subscriptions: number;
      };
    }>("/admin/subscriptions", { params: status ? { status } : undefined }),
};

// === M46c: Invoices / Accounting / JPK ===

export type InvoiceItem = {
  description: string;
  qty: number;
  unit_price_net: number;
  vat_rate: number;
  total_net: number;
};

export type Invoice = {
  id: number;
  payment_id: number | null;
  client_id: number;
  invoice_number: string;
  issue_date: string;
  sale_date: string;
  due_date: string;
  status: "issued" | "ksef_pending" | "ksef_sent" | "ksef_failed" | "canceled";
  buyer_name: string | null;
  buyer_nip: string | null;
  buyer_address: string | null;
  buyer_email: string | null;
  seller_name: string | null;
  seller_nip: string | null;
  seller_address: string | null;
  amount_net: number;
  amount_vat: number;
  amount_gross: number;
  vat_rate: number;
  currency: string;
  items: InvoiceItem[];
  pdf_path: string | null;
  fakturownia_invoice_id: string | null;
  ksef_reference_number: string | null;
  ksef_status: string | null;
  ksef_sent_at: string | null;
  ksef_error: string | null;
  paid_at: string | null;
  canceled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
  client_email_current?: string | null;
  client_name_current?: string | null;
  payment_type?: string | null;
};

export type RevenueSummary = {
  year: number;
  month: number | null;
  invoice_count: number;
  total_net: number;
  total_vat: number;
  total_gross: number;
  by_vat_rate: Array<{
    vat_rate: number;
    count: number;
    net: number;
    vat: number;
    gross: number;
  }>;
  by_type: {
    subscription_net: number;
    graphic_order_net: number;
    module_addon_net: number;
  };
};

export type PnLStatement = {
  year: number;
  month: number | null;
  period_label: string;
  revenue: RevenueSummary;
  costs: {
    total: number;
    breakdown: Record<string, number>;
    period_multiplier: number;
    note: string;
  };
  profit: {
    net_profit: number;
    margin_pct: number;
    is_profitable: boolean;
  };
};

export const accountingApi = {
  adminListInvoices: (params?: {
    client_id?: number;
    status?: string;
    year?: number;
    month?: number;
    limit?: number;
  }) =>
    api.get<{ items: Invoice[]; count: number }>("/admin/invoices", { params }),

  adminGetInvoice: (id: number) => api.get<Invoice>(`/admin/invoices/${id}`),

  adminCancelInvoice: (id: number, reason?: string) =>
    api.post<Invoice>(`/admin/invoices/${id}/cancel`, { reason }),

  adminResendKsef: (id: number) =>
    api.post<{
      ok: boolean;
      reference_number?: string;
      status?: string;
    }>(`/admin/invoices/${id}/resend-ksef`),

  adminInvoicePdfUrl: (id: number) => `/api/v1/admin/invoices/${id}/pdf`,

  // User
  myInvoices: () => api.get<{ items: Invoice[]; count: number }>("/me/invoices"),
  myInvoicePdfUrl: (id: number) => `/api/v1/me/invoices/${id}/pdf`,

  // P&L + JPK
  adminPnl: (params?: { year?: number; month?: number }) =>
    api.get<PnLStatement>("/admin/accounting/pnl", { params }),

  adminRevenue: (params?: { year?: number; month?: number }) =>
    api.get<RevenueSummary>("/admin/accounting/revenue", { params }),

  adminJpkUrl: (year: number, month: number) =>
    `/api/v1/admin/accounting/jpk-vat?year=${year}&month=${month}`,

  adminIntegrationStatus: () =>
    api.get<{
      fakturownia: { configured: boolean; instructions: string };
      ksef: { configured: boolean; sandbox: boolean; instructions: string };
    }>("/admin/accounting/integration-status"),
};

// === M46d: 2FA ===
// Klient CRM sprzedaży (leady) jest w frontend/lib/api-sprzedaz.ts (#745).
// Dziennik wysyłek maili: frontend/lib/mailing-api.ts (#755; stary /admin/email-log wycofany w #759).

// Marketing campaigns
export type MarketingCampaign = {
  id: number;
  name: string;
  subject: string;
  html_body: string;
  target_filter: Record<string, unknown>;
  status: "draft" | "scheduled" | "sending" | "sent" | "canceled";
  scheduled_at: string | null;
  sent_at: string | null;
  sent_count: number;
  failed_count: number;
  opened_count: number;
  clicked_count: number;
  created_by: number | null;
  created_at: string;
  updated_at: string;
};

export type CampaignTemplate = {
  id: string;
  name: string;
  subject: string;
  body: string;
  target_filter: Record<string, unknown>;
};

export const campaignsApi = {
  list: (status?: string) =>
    api.get<{ items: MarketingCampaign[] }>("/admin/marketing/campaigns", {
      params: status ? { status } : undefined,
    }),
  get: (id: number) =>
    api.get<MarketingCampaign>(`/admin/marketing/campaigns/${id}`),
  templates: () =>
    api.get<{ templates: CampaignTemplate[] }>("/admin/marketing/campaigns/templates"),
  previewAudience: (target_filter: Record<string, unknown>) =>
    api.post<{
      count: number;
      sample: Array<{ id: number; email: string; name: string | null; plan: string }>;
      by_plan: Record<string, number>;
    }>("/admin/marketing/campaigns/preview-audience", { target_filter }),
  create: (payload: {
    name: string;
    subject: string;
    html_body: string;
    target_filter?: Record<string, unknown>;
  }) => api.post<MarketingCampaign>("/admin/marketing/campaigns", payload),
  update: (id: number, payload: Partial<MarketingCampaign>) =>
    api.patch<MarketingCampaign>(`/admin/marketing/campaigns/${id}`, payload),
  send: (id: number) =>
    api.post<{
      ok: boolean;
      campaign_id: number;
      audience_size: number;
      sent: number;
      failed: number;
    }>(`/admin/marketing/campaigns/${id}/send`),
  delete: (id: number) => api.delete(`/admin/marketing/campaigns/${id}`),
  recipients: (id: number) =>
    api.get<{
      items: Array<{
        id: number;
        campaign_id: number;
        client_id: number;
        email: string;
        status: string;
        sent_at: string | null;
        error: string | null;
        client_email: string | null;
        client_name: string | null;
      }>;
    }>(`/admin/marketing/campaigns/${id}/recipients`),
};

// M47b: Onboarding + Referral
export type OnboardingState = {
  client_id: number;
  current_step: string;
  steps_completed: string[];
  steps_skipped: string[];
  data: Record<string, unknown>;
  started_at: string;
  completed_at: string | null;
  last_updated_at: string;
  all_steps: string[];
  step_labels: Record<string, string>;
  is_complete: boolean;
  progress_pct: number;
};

export type ReferralConversion = {
  id: number;
  referrer_client_id: number;
  referred_client_id: number;
  referral_code: string;
  status: "pending" | "credited" | "paid_out" | "voided";
  reward_pln: number;
  source_payment_id: number | null;
  credited_at: string | null;
  paid_at: string | null;
  voided_at: string | null;
  void_reason: string | null;
  created_at: string;
  referred_email: string | null;
  referred_name: string | null;
  referred_signup_at: string | null;
  referred_plan: string | null;
};

export const onboardingApi = {
  myStatus: () => api.get<OnboardingState>("/me/onboarding"),
  completeStep: (step: string, data?: Record<string, unknown>, skipped = false) =>
    api.post<OnboardingState>("/me/onboarding/complete", { step, data, skipped }),
  reset: () => api.post<OnboardingState>("/me/onboarding/reset"),
};

// Pierwsze uruchomienie pulpitu (#615)
export type FirstRunStepKey = "allegro" | "role" | "twofa" | "goal" | "audit" | "decision";
export type FirstRunRole = "owner" | "service" | "agency";
export interface FirstRunState {
  /** active: wprowadzenie na pulpicie; dismissed: pominięte (karta „Dokończ”);
   *  finished: ukończone; none: konto sprzed wprowadzenia, nieuruchomione. */
  status: "active" | "dismissed" | "finished" | "none";
  /** false dla członka zespołu: wprowadzenie przechodzi właściciel konta. */
  can_run: boolean;
  steps: Array<{
    key: FirstRunStepKey;
    state: "done" | "skipped" | "in_progress" | "todo";
    estimate_seconds: number;
  }>;
  done: number;
  total: number;
  remaining_seconds: number;
  role: FirstRunRole | null;
  goal: { target_amount: number; goal_type: GoalType | null; period_type: PeriodType | null } | null;
}
export type FirstRunAction =
  | { action: "role"; role: FirstRunRole }
  | { action: "skip" | "unskip"; step: Exclude<FirstRunStepKey, "allegro" | "role"> }
  | { action: "start" | "audit_started" | "decision_done" | "dismiss" | "finish" | "restart" };

export const firstRunApi = {
  get: () => api.get<FirstRunState>("/me/first-run"),
  act: (payload: FirstRunAction) => api.post<FirstRunState>("/me/first-run", payload),
  goalSuggestions: () =>
    api.get<{ base_30d: number | null; options: Array<{ multiplier: number; amount: number }> }>(
      "/me/first-run/goal-suggestions",
    ),
};

export const referralApi = {
  myReferral: () =>
    api.get<{
      code: string;
      total_uses: number;
      total_earned_pln: number;
      pending_count: number;
      credited_pln: number;
      paid_out_pln: number;
      credited_minus_paid_out: number;
      conversions: ReferralConversion[];
    }>("/me/referral"),

  checkCode: (code: string) =>
    api.get<{ valid: boolean; code: string }>(
      `/referral/check/${encodeURIComponent(code)}`
    ),

  adminList: () =>
    api.get<{
      items: ReferralConversion[];
      stats: {
        by_status: Record<string, { count: number; total: number }>;
        total_codes: number;
        total_conversions: number;
        total_rewards_pln: number;
      };
    }>("/admin/referrals"),

  adminPayout: (conversionId: number) =>
    api.post<ReferralConversion>(`/admin/referrals/${conversionId}/payout`),

  adminOnboardingStats: () =>
    api.get<{
      total: number;
      completed: number;
      in_progress: number;
      completion_rate_pct: number;
      step_funnel: Record<string, { completed: number; skipped: number }>;
    }>("/admin/onboarding/stats"),
};

// M47c — Integrations status
export type IntegrationStatus = {
  id: string;
  name: string;
  category: string;
  description: string;
  configured: boolean;
  mode: "real" | "mock" | "system" | "missing";
  env_vars: string[];
  current_env: Record<string, string>;
  instructions: string;
  docs_url: string;
  signup_url: string;
  priority: "critical" | "high" | "low";
  blocks_features: string[];
  editable_credentials?: boolean;
};

// #493 — klucze integracji edytowane w panelu admina. Sekret wraca tylko
// jako maska (`preview`), nigdy w całości.
export type IntegrationCredentialField = {
  name: string;
  label: string;
  secret: boolean;
  set: boolean;
  source: "panel" | "env" | "missing";
  stored: boolean;
  preview: string;
};

export type IntegrationCredentials = {
  integration: string;
  fields: IntegrationCredentialField[];
};

// M48 — GDPR + Scheduler
export type DeletionRequest = {
  id: number;
  client_id: number;
  reason: string | null;
  requested_at: string;
  execute_at: string;
  status: "pending" | "cancelled" | "executed";
  cancelled_at: string | null;
  executed_at: string | null;
};

export const gdprApi = {
  exportMyData: () =>
    api.get<Blob>("/me/gdpr/export", { responseType: "blob" }),

  deleteRequest: (reason: string | null, confirm: boolean) =>
    api.post<{
      ok: boolean;
      request: DeletionRequest;
      cooldown_days: number;
      message: string;
    }>("/me/gdpr/delete-request", { reason, confirm }),

  cancelDelete: () =>
    api.post<{
      ok: boolean;
      reactivated: boolean;
      is_active: boolean;
      message: string;
    }>("/me/gdpr/delete-cancel"),

  myDeleteStatus: () =>
    api.get<{ request: DeletionRequest | null }>("/me/gdpr/delete-status"),

  adminPending: () =>
    api.get<{ items: DeletionRequest[]; cooldown_days: number }>(
      "/admin/gdpr/pending"
    ),
};

// M50 — Public API: keys + webhooks
export type ApiKey = {
  id: number;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at: string | null;
  last_ip: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  revoked_reason: string | null;
  created_at: string;
  is_active: boolean;
};

export type ScopeDef = {
  key: string;
  label: string;
  category: string;
};

export type WebhookSubscription = {
  id: number;
  client_id: number;
  url: string;
  events: string[];
  secret: string;
  is_active: number;
  description: string | null;
  last_triggered_at: string | null;
  total_sent: number;
  total_failed: number;
  created_at: string;
};

export type WebhookDelivery = {
  id: number;
  subscription_id: number;
  subscription_url: string;
  event: string;
  payload: string;
  status: "pending" | "delivered" | "failed" | "skipped";
  http_status: number | null;
  response_body: string | null;
  error: string | null;
  attempt_number: number;
  next_retry_at: string | null;
  created_at: string;
  attempted_at: string | null;
  delivered_at: string | null;
};

// M52a — In-app notifications
export type Notification = {
  id: number;
  client_id: number;
  type: string;
  title: string;
  message: string | null;
  link_url: string | null;
  icon: string | null;
  metadata: Record<string, unknown> | null;
  is_read: number;
  expires_at: string | null;
  created_at: string;
  read_at: string | null;
};

export type NotificationPrefs = {
  client_id: number;
  email_lifecycle: number;
  email_marketing: number;
  inapp_lifecycle: number;
  inapp_marketing: number;
  marketing_consent_at: string | null;
  marketing_consent_withdrawn_at: string | null;
  is_default?: boolean;
};

// M52f — Knowledge Base
export type KbArticleCategory = {
  id: number;
  slug: string;
  name: string;
  icon: string | null;
  description: string | null;
  sort_order: number;
  article_count: number;
};

export type KbArticle = {
  id: number;
  slug: string;
  category_id: number | null;
  category_slug: string | null;
  category_name: string | null;
  category_icon: string | null;
  title: string;
  excerpt: string | null;
  content_md?: string;
  views: number;
  helpful_count: number;
  not_helpful_count: number;
  created_at?: string;
  updated_at?: string;
};

export const kbApi = {
  categories: () => api.get<{ items: KbArticleCategory[] }>("/kb/categories"),
  articles: (category?: string) =>
    api.get<{ items: KbArticle[] }>("/kb/articles", {
      params: category ? { category } : undefined,
    }),
  article: (slug: string) => api.get<KbArticle>(`/kb/articles/${slug}`),
  search: (q: string) =>
    api.get<{ items: KbArticle[]; query: string }>("/kb/search", { params: { q } }),
  popular: (limit = 5) =>
    api.get<{ items: KbArticle[] }>("/kb/popular", { params: { limit } }),
  vote: (articleId: number, helpful: boolean) =>
    api.post<{
      id: number;
      helpful_count: number;
      not_helpful_count: number;
      views: number;
    }>(`/kb/articles/${articleId}/vote`, { helpful }),
};

export const notificationPrefsApi = {
  get: () => api.get<NotificationPrefs>("/me/notification-preferences"),
  update: (payload: Partial<NotificationPrefs>) =>
    api.patch<NotificationPrefs>("/me/notification-preferences", payload),
  withdrawMarketing: () =>
    api.post<NotificationPrefs>("/me/notification-preferences/withdraw-marketing"),
};

export const notificationsApi = {
  list: (params?: { unread_only?: boolean; limit?: number; offset?: number }) =>
    api.get<{ items: Notification[]; unread_count: number }>("/me/notifications", {
      params,
    }),
  unreadCount: () => api.get<{ count: number }>("/me/notifications/unread-count"),
  markRead: (id: number) =>
    api.post<{ ok: boolean }>(`/me/notifications/${id}/read`),
  markAllRead: () =>
    api.post<{ ok: boolean; marked: number }>("/me/notifications/mark-all-read"),
  delete: (id: number) => api.delete<{ ok: boolean }>(`/me/notifications/${id}`),
};

// M51 — Allegro Multi-Shop
export type AllegroShop = {
  id: number;
  client_id: number;
  allegro_user_id: string | null;
  allegro_seller_login: string | null;
  label: string | null;
  is_primary: number;
  environment: string;
  connected_at: string;
  last_synced_at: string | null;
  token_expires_at: string | null;
  is_active: number;
};

export const allegroShopsApi = {
  list: () =>
    api.get<{
      items: AllegroShop[];
      active_count: number;
      primary_id: number | null;
    }>("/me/allegro-shops"),

  setPrimary: (shopId: number) =>
    api.post<{ ok: boolean; primary_id: number }>(
      `/me/allegro-shops/${shopId}/set-primary`
    ),

  rename: (shopId: number, label: string) =>
    api.patch<{ ok: boolean; label: string }>(`/me/allegro-shops/${shopId}`, {
      label,
    }),

  disconnect: (shopId: number) =>
    api.delete<{
      disconnected: number;
      promoted_to_primary: number | null;
      remaining_count: number;
    }>(`/me/allegro-shops/${shopId}`),

  authorizeAsNew: (label?: string) =>
    api.get<{ auth_url: string; state: string }>("/auth/allegro/authorize", {
      params: { as_new: true, label },
    }),

  // 2026-08-06: OAuth 2.0 Device Authorization Flow (RFC 8628)
  deviceStart: (label?: string) =>
    api.post<{
      device_code: string;
      user_code: string;
      verification_uri: string;
      verification_uri_complete: string;
      expires_in: number;
      interval: number;
    }>("/auth/allegro/device-start", { label }),

  devicePoll: (device_code: string, label?: string) =>
    api.post<{
      status: "pending" | "success" | "expired" | "error";
      shop_id: number | null;
      shop_label: string | null;
      seller_login: string | null;
      error: string | null;
    }>("/auth/allegro/device-poll", { device_code, label }),
};

// Zakładka Integracje 2.0 (#647): kanały z liczbami z 30 dni i okazja Allegro Ads.
export type IntegrationChannelStatus = "ok" | "stale" | "auth_error";

export interface IntegrationChannel {
  id: number;
  label: string | null;
  seller_login: string | null;
  is_primary: boolean;
  environment: string | null;
  connected_at: string | null;
  last_synced_at: string | null;
  status: IntegrationChannelStatus;
  /** `null` = brak danych albo uprawnienia; 0 to fakt. */
  sales: number | null;
  orders: number | null;
}

export interface IntegrationsOverview {
  period: { key: "30d"; from: string; to: string };
  shops: IntegrationChannel[];
  /** Kanał Allegro Ads (null bez uprawnienia `ads.view`); `spend` tylko gdy są opłaty w rozliczeniach. */
  ads: { spend: number | null; connected: boolean | null; state: string | null } | null;
  /** Akcje na sklepach i łączenie ma tylko właściciel konta. */
  can_manage: boolean;
}

export const integrationsOverviewApi = {
  overview: () => api.get<IntegrationsOverview>("/me/integrations/overview"),
};

export const apiKeysApi = {
  list: () =>
    api.get<{ items: ApiKey[]; available_scopes: ScopeDef[] }>("/me/api-keys"),
  generate: (payload: { name: string; scopes: string[]; expires_in_days?: number }) =>
    api.post<{
      id: number;
      name: string;
      raw_key: string;
      key_prefix: string;
      scopes: string[];
      expires_at: string | null;
      warning: string;
    }>("/me/api-keys/generate", payload),
  revoke: (id: number, reason?: string) =>
    api.delete<{ ok: boolean }>(`/me/api-keys/${id}${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`),
};

export const webhooksApi = {
  list: () =>
    api.get<{ items: WebhookSubscription[]; supported_events: string[] }>("/me/webhooks"),
  create: (payload: { url: string; events: string[]; description?: string }) =>
    api.post<WebhookSubscription>("/me/webhooks", payload),
  update: (id: number, payload: Partial<WebhookSubscription>) =>
    api.patch<WebhookSubscription>(`/me/webhooks/${id}`, payload),
  delete: (id: number) => api.delete(`/me/webhooks/${id}`),
  deliveries: () =>
    api.get<{ items: WebhookDelivery[] }>("/me/webhooks/deliveries"),
  test: (subscription_id: number, event: string = "order.created") =>
    api.post<{ ok: boolean; queued: number }>("/me/webhooks/test", { subscription_id, event }),
};

export const schedulerApi = {
  jobs: () =>
    api.get<{
      jobs: Array<{ id: string; next_run: string | null; trigger: string }>;
      scheduler_running: boolean;
    }>("/admin/scheduler/jobs"),

  triggerJob: (jobId: string) =>
    api.post<{ ok: boolean; job_id?: string; triggered?: boolean; error?: string }>(
      `/admin/scheduler/jobs/${jobId}/trigger`
    ),

  emailSequenceStats: () =>
    api.get<{
      by_sequence: Record<string, Record<string, number>>;
      total: number;
    }>("/admin/email-sequences/stats"),
};

export const integrationsApi = {
  list: () =>
    api.get<{
      integrations: IntegrationStatus[];
      summary: {
        total: number;
        configured: number;
        missing: number;
        critical_missing: string[];
        health_pct: number;
      };
    }>("/admin/integrations"),
  health: () =>
    api.get<{
      total: number;
      configured: number;
      missing: number;
      critical_missing: string[];
      health_pct: number;
    }>("/admin/integrations/health"),

  credentials: (integration: string) =>
    api.get<IntegrationCredentials>(
      `/admin/integrations/credentials/${encodeURIComponent(integration)}`
    ),
  updateCredentials: (integration: string, values: Record<string, string>) =>
    api.put<IntegrationCredentials & { ok: boolean; changed: string[] }>(
      `/admin/integrations/credentials/${encodeURIComponent(integration)}`,
      { values }
    ),
  clearCredentials: (integration: string) =>
    api.delete<IntegrationCredentials & { ok: boolean; removed: string[] }>(
      `/admin/integrations/credentials/${encodeURIComponent(integration)}`
    ),
};

export const twoFaApi = {
  status: () =>
    api.get<{
      enabled: boolean;
      enabled_at?: string | null;
      recovery_codes_remaining: number;
      recovery_codes_used?: number;
      last_used_at?: string | null;
    }>("/me/2fa/status"),

  setupStart: () =>
    api.post<{
      secret: string;
      provisioning_uri: string;
      qr_data_uri: string;
      recovery_codes: string[];
      issuer: string;
    }>("/me/2fa/setup-start"),

  setupVerify: (code: string) =>
    api.post<{ ok: boolean; enabled: boolean }>("/me/2fa/setup-verify", { code }),

  disable: (code: string) =>
    api.post<{ ok: boolean; enabled: boolean }>("/me/2fa/disable", { code }),
};

// =============================================================================
// KB Admin (zarządzanie bazą wiedzy RAG dla Heli)
// =============================================================================

export type KbAdminCategory = {
  slug: string;
  name: string;
  description: string | null;
  default_change_freq: "stable" | "semi_stable" | "dynamic" | "volatile";
  default_authority: "tier_1_official" | "tier_2_curated" | "tier_3_community";
  sort_order: number;
};

export type KbDocument = {
  id: number;
  slug: string;
  title: string;
  category: string;
  source_authority: "tier_1_official" | "tier_2_curated" | "tier_3_community";
  change_freq: "stable" | "semi_stable" | "dynamic" | "volatile";
  source_url: string | null;
  status: string;
  version: number;
  last_verified_at: string | null;
  expires_at: string | null;
  usage_count: number;
  quality_score: number;
  source_hash: string | null;
  updated_at: string;
  content_length?: number;
  days_until_review?: number;
  is_stale?: boolean;
};

export type KbDocumentDetail = KbDocument & {
  content: string;
  verified_by_client_id: number | null;
  owner_id: number | null;
  created_at: string;
};

export type KbReviewLogEntry = {
  id: number;
  action: string;
  reviewer_client_id: number | null;
  from_version: number | null;
  to_version: number | null;
  notes: string | null;
  created_at: string;
};

export type KbReviewQueueItem = KbDocument & {
  days_overdue: number;
  priority_score: number;
};

export const kbAdminApi = {
  categories: () => api.get<{ categories: KbAdminCategory[] }>("/admin/kb/categories"),

  list: (params: {
    category?: string;
    tier?: string;
    freq?: string;
    status?: string;
    search?: string;
    sort?: "category" | "verified" | "usage" | "stale";
    limit?: number;
  }) =>
    api.get<{ documents: KbDocument[]; count: number }>(`/admin/kb/documents`, {
      params,
    }),

  detail: (id: number) =>
    api.get<{
      document: KbDocumentDetail;
      history: KbReviewLogEntry[];
      chunks: Array<{ id: number; idx: number; tokens: number | null; text_length: number }>;
    }>(`/admin/kb/documents/${id}`),

  updateMeta: (
    id: number,
    payload: {
      source_authority?: string;
      change_freq?: string;
      expires_at?: string | null;
      owner_id?: number | null;
      status?: string;
      source_url?: string | null;
    },
  ) => api.patch<{ updated: boolean }>(`/admin/kb/documents/${id}`, payload),

  verify: (id: number, notes?: string) =>
    api.post<{ verified_at: string }>(`/admin/kb/documents/${id}/verify`, {
      notes: notes || null,
    }),

  reembed: (id: number) =>
    api.post<{ reembedded: boolean; chunks: number }>(
      `/admin/kb/documents/${id}/reembed`,
    ),

  archive: (id: number) =>
    api.post<{ archived: boolean }>(`/admin/kb/documents/${id}/archive`),

  reviewQueue: (limit = 50) =>
    api.get<{ items: KbReviewQueueItem[]; total: number }>(
      `/admin/kb/review-queue?limit=${limit}`,
    ),

  stats: () =>
    api.get<{
      totals: { active: number; archived: number; never_verified: number };
      chunks: number;
      by_category: Array<{ category: string; n: number }>;
      by_tier: Array<{ tier: string; n: number }>;
    }>("/admin/kb/stats"),

  exportMd: (id: number) =>
    api.post<{ path: string; action: "created" | "updated"; bytes: number }>(
      `/admin/kb/documents/${id}/export-md`,
    ),

  syncAllToRepo: () =>
    api.post<{
      stats: { created: number; updated: number; skipped: number; failed: number };
      items: Array<{ id: number; slug: string; path?: string; action?: string; error?: string }>;
      kb_root: string;
    }>("/admin/kb/sync-all-to-repo"),
};

// =============================================================================
// KB Ingest Admin — sources + drafts (Faza C1)
// =============================================================================

export type KbSource = {
  id: number;
  source_type: "url" | "youtube" | "pdf" | "manual_md";
  url: string | null;
  title: string;
  suggested_category: string | null;
  suggested_authority: "tier_1_official" | "tier_2_curated" | "tier_3_community";
  suggested_freq: "stable" | "semi_stable" | "dynamic" | "volatile";
  tier: 1 | 2 | 3;
  cadence_days: number | null;
  last_checked_at: string | null;
  last_changed_at: string | null;
  next_check_at: string | null;
  status: "active" | "paused" | "error";
  last_error: string | null;
  error_count: number;
  draft_count?: number;
  created_at?: string;
  updated_at?: string;
};

export type KbDraft = {
  id: number;
  source_id: number | null;
  source_url_snapshot: string | null;
  source_type: string;
  suggested_title: string | null;
  suggested_slug: string | null;
  suggested_category: string | null;
  suggested_authority: string | null;
  suggested_freq: string | null;
  status: "pending_review" | "approved" | "rejected" | "failed";
  published_doc_id: number | null;
  reviewed_at: string | null;
  created_at: string;
  raw_length?: number;
  processed_length?: number;
  source_title?: string | null;
};

export type KbDraftDetail = KbDraft & {
  raw_content: string;
  processed_markdown: string | null;
  processed_at: string | null;
  rejection_reason: string | null;
  failure_reason: string | null;
  reviewer_client_id: number | null;
  reviewer_notes: string | null;
  source_url: string | null;
};

export const kbIngestApi = {
  // Sources
  listSources: (params?: { status?: string; tier?: number }) =>
    api.get<{ items: KbSource[]; count: number }>("/admin/kb/sources", {
      params,
    }),
  createSource: (payload: {
    source_type: "url" | "youtube" | "pdf" | "manual_md";
    url?: string | null;
    title: string;
    suggested_category?: string | null;
    suggested_authority?: string;
    suggested_freq?: string;
    tier?: number;
    cadence_days?: number | null;
  }) => api.post<KbSource>("/admin/kb/sources", payload),
  updateSource: (
    id: number,
    payload: Partial<{
      title: string;
      suggested_category: string | null;
      suggested_authority: string;
      suggested_freq: string;
      tier: number;
      cadence_days: number | null;
      status: string;
    }>,
  ) => api.patch<KbSource>(`/admin/kb/sources/${id}`, payload),
  deleteSource: (id: number) =>
    api.delete<{ deleted: boolean }>(`/admin/kb/sources/${id}`),
  checkNow: (id: number) =>
    api.post<{
      action: "no_change" | "new_draft" | "duplicate_draft" | "out_of_scope" | "error";
      draft_id?: number;
      error?: string;
    }>(`/admin/kb/sources/${id}/check-now`),

  // Drafts
  listDrafts: (params?: { status?: string; limit?: number }) =>
    api.get<{ items: KbDraft[]; count: number; pending_total: number }>(
      "/admin/kb/drafts",
      { params },
    ),
  getDraft: (id: number) => api.get<KbDraftDetail>(`/admin/kb/drafts/${id}`),
  approveDraft: (
    id: number,
    payload: Partial<{
      final_title: string;
      final_slug: string;
      final_category: string;
      final_authority: string;
      final_freq: string;
      final_markdown: string;
      reviewer_notes: string;
    }>,
  ) =>
    api.post<{ draft_id: number; document_id: number; title: string }>(
      `/admin/kb/drafts/${id}/approve`,
      payload,
    ),
  rejectDraft: (id: number, reason: string) =>
    api.post<{ draft_id: number; status: string }>(
      `/admin/kb/drafts/${id}/reject`,
      { reason },
    ),
  createManualDraft: (payload: {
    raw_markdown: string;
    title?: string;
    category?: string;
    authority?: string;
    freq?: string;
  }) => api.post<KbDraftDetail>("/admin/kb/drafts/manual", payload),

  // Faza C3 — cron runs + manual trigger + pending badge
  ingestStatus: () =>
    api.get<{
      runs: Array<{
        id: number;
        started_at: string;
        finished_at: string | null;
        triggered_by: "cron" | "manual" | "startup";
        sources_checked: number;
        drafts_created: number;
        duplicates_skipped: number;
        no_change: number;
        out_of_scope: number;
        errors: number;
      }>;
      summary: {
        runs_7d: number;
        drafts_created_7d: number;
        sources_checked_7d: number;
        errors_7d: number;
        pending_drafts: number;
      };
      last_run: {
        id: number;
        started_at: string;
        finished_at: string | null;
        triggered_by: string;
        sources_checked: number;
        drafts_created: number;
        errors: number;
      } | null;
    }>("/admin/kb/ingest-status"),
  runIngestNow: () =>
    api.post<{
      run_id: number;
      sources_checked: number;
      drafts_created: number;
      duplicates_skipped: number;
      no_change: number;
      out_of_scope: number;
      errors: number;
      duration_sec: number;
    }>("/admin/kb/run-ingest-now"),
  pendingDraftsCount: () =>
    api.get<{ count: number }>("/admin/kb/drafts-pending-count"),
};

// =============================================================================
// Ads review — Warstwa 5 anti-hallucination (human-in-the-loop)
// =============================================================================

export type AdsReviewItem = {
  id: number;
  case_id: number;
  campaign_id: string;
  title: string;
  rationale: string;
  payload: {
    source?: string;
    agent?: string;
    campaign_id?: string;
    current_cpc?: number;
    new_cpc?: number;
    delta_pct?: number;
    rule_trigger?: string;
    rationale_source?: "llm" | "fallback";
    confidence?: number;
  };
  risk_level: string;
  status: string;
  created_at: string;
  client_id: number;
  case_summary?: string | null;
  execution_error?: string | null;
};

export const adsReviewApi = {
  list: () =>
    api.get<{ items: AdsReviewItem[]; count: number }>("/admin/ads-review"),
  pendingCount: () =>
    api.get<{ count: number }>("/admin/ads-review/pending-count"),
  detail: (id: number) => api.get<AdsReviewItem>(`/admin/ads-review/${id}`),
  approve: (id: number) =>
    api.post<{ rec_id: number; status: string; new_cpc: number }>(
      `/admin/ads-review/${id}/approve`,
    ),
  reject: (id: number, reason: string) =>
    api.post<{ rec_id: number; status: string }>(
      `/admin/ads-review/${id}/reject`,
      { reason },
    ),
};

// =============================================================================
// SEO Keywords — Sprint Keywords (Allegro + Google + YouTube + Trends + Competitor)
// =============================================================================

export type SeoKeywordsResult = {
  phrase: string;
  per_source: {
    allegro?: string[];
    google?: string[];
    youtube?: string[];
    competitor?: string[];
    trends_top?: string[];
    trends_rising?: string[];
  };
  unified_top: string[];
  trends?: {
    top_queries: string[];
    rising_queries: string[];
    seasonality: Record<string, number>;
    rate_limited?: boolean;
    error?: string;
  } | null;
};

export const seoKeywordsApi = {
  get: (params: {
    phrase: string;
    category_id?: number;
    sources?: string;
    force_refresh?: boolean;
    include_trends?: boolean;
    include_planner?: boolean;
  }) => api.get<SeoKeywordsResult>("/seo/keywords", { params }),
  trends: (phrase: string) =>
    api.get<{
      top_queries: string[];
      rising_queries: string[];
      seasonality: Record<string, number>;
      rate_limited?: boolean;
      error?: string;
    }>("/seo/keywords/trends", { params: { phrase } }),
  planner: (phrase: string, limit = 20) =>
    api.get<{
      configured: boolean;
      phrase: string;
      avg_monthly_searches?: number;
      competition?: string;
      low_top_of_page_bid_pln?: number;
      high_top_of_page_bid_pln?: number;
      related?: Array<{
        keyword: string;
        volume: number;
        competition: string;
        low_bid_pln?: number;
        high_bid_pln?: number;
      }>;
      error?: string;
    }>("/seo/keywords/planner", { params: { phrase, limit } }),
  plannerStatus: () =>
    api.get<{
      configured: boolean;
      developer_token: boolean;
      client_id: boolean;
      client_secret: boolean;
      refresh_token: boolean;
      mcc_customer_id: boolean;
      setup_guide: string;
    }>("/seo/keywords/planner/status"),
};

export const searchConsoleApi = {
  status: () =>
    api.get<{
      configured: boolean;
      needs_admin_setup: boolean;
      client_connection?: {
        connected: boolean;
        email?: string;
        selected_property?: string | null;
        status?: string;
        connected_at?: string;
        last_used_at?: string | null;
      } | null;
    }>("/me/seo/sc/status"),
  authorize: () => api.get<{ authorize_url: string }>("/me/seo/sc/authorize"),
  properties: () =>
    api.get<{
      items: Array<{ siteUrl: string; permissionLevel: string }>;
      count: number;
    }>("/me/seo/sc/properties"),
  selectProperty: (siteUrl: string) =>
    api.post<{ client_id: number; selected_property: string }>(
      "/me/seo/sc/select-property",
      { site_url: siteUrl },
    ),
  topQueries: (days = 30, limit = 25) =>
    api.get<{
      site_url: string;
      days: number;
      queries: Array<{
        query: string;
        clicks: number;
        impressions: number;
        ctr: number;
        position: number;
      }>;
      count: number;
    }>("/me/seo/sc/top-queries", { params: { days, limit } }),
  disconnect: () => api.post<{ client_id: number; disconnected: boolean }>("/me/seo/sc/disconnect"),
};

// =============================================================================
// Audit rules — Sprint Trust E1 (widoczność reguł)
// =============================================================================

export type AuditRule = {
  id: number;
  code: string;
  area: string | null;
  severity: "critical" | "warning" | "info" | "suggestion";
  points_impact: number | null;
  name: string;
  description: string;
  scope: "offer" | "account" | null;
  type: "automatic" | "ai_soft" | "vision" | "external_api" | "manual" | null;
  section: string | null;
  data_source: string | null;
  validator: string | null;
  validator_params: Record<string, unknown> | null;
  prompt: string | null;
  source_facts: string[];
  fix_suggestion: string | null;
  is_enabled: number;
  version: number;
  // Sprint Trust E3 — data readiness
  data_status?: "ready" | "needs_integration" | "needs_user_input" | "manual" | "unknown";
  required_sources?: Array<{ id: string; label: string; status: string }>;
};

export type AuditCoverageReport = {
  total_rules: number;
  coverage: {
    ready: number;
    blocked: number;
    user_input: number;
    manual: number;
    unknown: number;
  };
  ready_pct: number;
  status_counts: Record<string, number>;
  rules_per_status: Record<string, string[]>;
  points_lost_per_status: Record<string, number>;
  total_points_lost_possible: number;
  sources: Array<{
    id: string;
    label: string;
    description: string | null;
    status: "ready" | "needs_integration" | "needs_user_input" | "manual";
    setup_link: string | null;
    setup_note: string | null;
    rule_count: number;
  }>;
};

export type AuditRuleDetail = AuditRule & {
  fix_module: string | null;
  source_rag_query: string | null;
  created_at: string;
  updated_at: string;
};

export const auditRulesApi = {
  list: (params?: {
    scope?: string;
    type?: string;
    severity?: string;
    section?: string;
    enabled_only?: boolean;
    search?: string;
    limit?: number;
  }) => api.get<{ items: AuditRule[]; count: number }>("/admin/audit-rules", { params }),
  detail: (code: string) =>
    api.get<AuditRuleDetail>(`/admin/audit-rules/${encodeURIComponent(code)}`),
  stats: () =>
    api.get<{
      total: number;
      by_scope: Array<{ scope: string; n: number }>;
      by_type: Array<{ type: string; n: number }>;
      by_severity: Array<{ severity: string; n: number }>;
      trust_metrics: {
        deterministic: number;
        llm_based: number;
        manual: number;
        deterministic_pct: number;
      };
    }>("/admin/audit-rules-stats"),
  meta: () =>
    api.get<{
      validators: string[];
      prompts: string[];
      scopes: string[];
      types: string[];
      severities: string[];
    }>("/admin/audit-rules-meta"),
  update: (
    code: string,
    payload: Partial<{
      name: string;
      description: string;
      severity: string;
      points_impact: number;
      fix_suggestion: string;
      is_enabled: boolean;
      condition: Partial<{
        type: string;
        scope: string;
        section: string;
        data_source: string;
        validator: string;
        validator_params: Record<string, unknown>;
        prompt: string;
      }>;
      change_notes: string;
    }>,
  ) =>
    api.patch<AuditRuleDetail>(`/admin/audit-rules/${encodeURIComponent(code)}`, payload),
  create: (payload: {
    code: string;
    name: string;
    description: string;
    severity: string;
    points_impact: number;
    fix_suggestion?: string;
    condition: {
      type: string;
      scope: string;
      section?: string;
      data_source?: string;
      validator?: string;
      validator_params?: Record<string, unknown>;
      prompt?: string;
    };
    change_notes?: string;
  }) => api.post<AuditRuleDetail>("/admin/audit-rules", payload),
  delete: (code: string) =>
    api.delete<{ code: string; is_enabled: number }>(
      `/admin/audit-rules/${encodeURIComponent(code)}`,
    ),
  history: (code: string) =>
    api.get<{
      items: Array<{
        id: number;
        code: string;
        version: number;
        severity: string;
        points_impact: number;
        name: string;
        modified_by_client_id: number | null;
        change_action: string;
        change_notes: string | null;
        created_at: string;
      }>;
      count: number;
    }>(`/admin/audit-rules/${encodeURIComponent(code)}/history`),
  revert: (code: string, version: number) =>
    api.post<AuditRuleDetail>(
      `/admin/audit-rules/${encodeURIComponent(code)}/revert/${version}`,
    ),
  // Eksport YAML: zwraca blob — w UI używamy window.open na pełny URL
  exportYamlUrl: (scope?: string) => {
    const params = scope ? `?scope=${scope}` : "";
    return `/admin/audit-rules/export-yaml${params}`;
  },
  coverage: () => api.get<AuditCoverageReport>("/admin/audit-rules-coverage"),

  // Public methodology (każdy zalogowany user — nie tylko owner)
  publicList: (params?: {
    scope?: string;
    type?: string;
    severity?: string;
    search?: string;
    limit?: number;
  }) =>
    api.get<{ items: AuditRule[]; count: number }>("/audit/methodology/rules", {
      params,
    }),
  publicDetail: (code: string) =>
    api.get<AuditRule>(`/audit/methodology/rules/${encodeURIComponent(code)}`),
  publicSources: () =>
    api.get<{
      sources: Array<{
        id: string;
        label: string;
        description: string | null;
        status: string;
        rule_count: number;
        setup_note: string | null;
      }>;
      count: number;
    }>("/audit/methodology/sources"),
  publicStats: () =>
    api.get<{
      total_rules: number;
      by_scope: Array<{ scope: string; n: number }>;
      by_type: Array<{ type: string; n: number }>;
      by_severity: Array<{ severity: string; n: number }>;
      trust_metrics: {
        deterministic: number;
        llm_based: number;
        manual: number;
        deterministic_pct: number;
      };
      coverage: {
        ready: number;
        blocked: number;
        user_input: number;
        manual: number;
        unknown: number;
      };
      coverage_pct: number;
    }>("/audit/methodology/stats"),
  executionStats: (code: string, days = 30) =>
    api.get<{
      code: string;
      window_days: number;
      runs_in_window: number;
      triggered_in_window: number;
      trigger_rate: number;
      last_triggered_at: string | null;
      recent_findings: Array<{
        id: number;
        audit_run_id: number;
        client_id: number;
        offer_id: number | null;
        severity: string;
        actual_value: string | null;
        evidence_text: string | null;
        created_at: string;
      }>;
    }>(`/admin/audit-rules/${encodeURIComponent(code)}/execution-stats`, {
      params: { days },
    }),
  test: (code: string, context: Record<string, unknown>) =>
    api.post<{
      code: string;
      name: string;
      type: string;
      input_context_size: number;
      validator?: string;
      params?: Record<string, unknown>;
      prompt?: string;
      status: string;
      evidence?: unknown;
      recommendation?: string;
      confidence?: number;
      triggered?: boolean;
      note?: string;
    }>(`/admin/audit-rules/${encodeURIComponent(code)}/test`, { context }),
};

// =============================================================================
// Audit review — Warstwa 5 anti-hallucination dla audit_account
// =============================================================================

export type AuditReviewItem = {
  id: number;
  audit_run_id: number;
  client_id: number;
  offer_id: number | null;
  rule_code: string;
  area: string;
  severity: "critical" | "warning" | "info" | "suggestion";
  points_impact: number | null;
  actual_value: string | null;
  evidence_text: string | null;
  confidence: number;
  created_at: string;
  rule_name?: string | null;
  rule_description?: string | null;
  fix_suggestion?: string | null;
};

export const auditReviewApi = {
  list: () =>
    api.get<{ items: AuditReviewItem[]; count: number }>("/admin/audit-review"),
  pendingCount: () =>
    api.get<{ count: number }>("/admin/audit-review/pending-count"),
  detail: (id: number) =>
    api.get<AuditReviewItem & { requires_review: boolean; review_action: string | null; reviewer_client_id: number | null; reviewed_at: string | null }>(
      `/admin/audit-review/${id}`,
    ),
  approve: (id: number) =>
    api.post<{ finding_id: number; review_action: string }>(
      `/admin/audit-review/${id}/approve`,
    ),
  reject: (id: number, reason: string) =>
    api.post<{ finding_id: number; review_action: string }>(
      `/admin/audit-review/${id}/reject`,
      { reason },
    ),
};

// =============================================================================
// AI Modules Admin (centralna kontrola wszystkich modułów AI)
// =============================================================================

export type AiModuleSummary = {
  module_key: string;
  label: string;
  description: string | null;
  model: string;
  llm_model_id: string | null;
  effective_model: string;
  provider_type: LlmProvider;
  required_capabilities: string[];
  runtime_supported: boolean;
  temperature: number;
  rag_top_k: number;
  rag_max_distance: number;
  enabled: boolean;
  version: number;
  n_categories: number;
  prompt_length: number;
  last_modified_at: string;
};

export type AiModuleConfig = {
  module_key: string;
  label: string;
  description: string | null;
  model: string;
  llm_model_id: string | null;
  effective_model: string;
  provider_type: LlmProvider;
  required_capabilities: string[];
  runtime_supported: boolean;
  temperature: number;
  max_tokens: number;
  system_prompt: string;
  kb_categories: string[];
  rag_top_k: number;
  rag_max_distance: number;
  enabled: boolean;
  version: number;
  last_modified_at: string;
  last_modified_by_client_id: number | null;
};

export type AiModuleHistoryEntry = {
  id: number;
  module_key: string;
  version: number;
  model: string;
  llm_model_id: string | null;
  temperature: number;
  max_tokens: number;
  rag_top_k: number;
  rag_max_distance: number;
  enabled: boolean;
  modified_by_client_id: number | null;
  change_notes: string | null;
  created_at: string;
  prompt_length?: number;
};

export type AiModuleHistoryFull = AiModuleHistoryEntry & {
  system_prompt: string;
  kb_categories: string[];
};

export const aiModulesApi = {
  list: () => api.get<{ modules: AiModuleSummary[] }>("/admin/ai-modules"),

  categories: () =>
    api.get<{
      categories: Array<{ slug: string; name: string; description: string | null }>;
    }>("/admin/ai-modules/categories"),

  detail: (moduleKey: string) =>
    api.get<{
      config: AiModuleConfig;
      history: AiModuleHistoryEntry[];
    }>(`/admin/ai-modules/${encodeURIComponent(moduleKey)}`),

  historyEntry: (moduleKey: string, historyId: number) =>
    api.get<{ entry: AiModuleHistoryFull }>(
      `/admin/ai-modules/${encodeURIComponent(moduleKey)}/history/${historyId}`,
    ),

  update: (
    moduleKey: string,
    payload: {
      model?: string;
      llm_model_id?: string | null;
      temperature?: number;
      max_tokens?: number;
      system_prompt?: string;
      kb_categories?: string[];
      rag_top_k?: number;
      rag_max_distance?: number;
      enabled?: boolean;
      change_notes?: string;
    },
  ) =>
    api.patch<{ updated: boolean; config: AiModuleConfig }>(
      `/admin/ai-modules/${encodeURIComponent(moduleKey)}`,
      payload,
    ),

  revert: (moduleKey: string, historyId: number) =>
    api.post<{ reverted: boolean; config: AiModuleConfig }>(
      `/admin/ai-modules/${encodeURIComponent(moduleKey)}/revert/${historyId}`,
    ),
};

export type LlmProvider = "gemini" | "openai" | "anthropic" | "deepseek";

export type LlmModelConfig = {
  id: string;
  display_name: string;
  provider_type: LlmProvider;
  model_id: string;
  has_api_key: boolean;
  is_active: boolean;
  supports_vision: boolean;
  supports_tools: boolean;
  supports_image_generation: boolean;
  created_at: string;
  updated_at: string;
};

export type LlmModelPayload = Pick<LlmModelConfig, "display_name" | "provider_type" | "model_id" | "is_active" | "supports_vision" | "supports_tools" | "supports_image_generation"> & { api_key?: string };

export const llmModelsApi = {
  list: () => api.get<{ models: LlmModelConfig[] }>("/admin/llm-models"),
  create: (payload: LlmModelPayload) => api.post<{ model: LlmModelConfig }>("/admin/llm-models", payload),
  update: (id: string, payload: Partial<LlmModelPayload>) => api.patch<{ model: LlmModelConfig }>(`/admin/llm-models/${encodeURIComponent(id)}`, payload),
};

export type LlmUsageCounters = {
  calls: number;
  // null oznacza brak zmierzonych tokenów w całym agregacie.
  input_tokens: number | null;
  output_tokens: number | null;
  cached_tokens: number | null;
  reasoning_tokens: number | null;
  total_tokens: number | null;
  error_calls: number;
  unmeasured_calls: number;
};

export type LlmUsageGroup = LlmUsageCounters & {
  model?: string | null;
  provider_type?: LlmProvider | null;
  module_key?: string | null;
  module_label?: string | null;
};

export type LlmUsageCall = {
  id: number;
  created_at: string;
  module_key: string;
  module_label: string | null;
  provider_type: LlmProvider | null;
  model: string | null;
  requested_model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cached_tokens: number | null;
  reasoning_tokens: number | null;
  total_tokens: number | null;
  usage_reported: boolean;
  status: "success" | "error";
  latency_ms: number | null;
  error_type: string | null;
};

export type LlmUsageFilters = { days: 7 | 30 | 90; module_key?: string; model?: string; provider_type?: LlmProvider };

export const llmUsageApi = {
  summary: (filters: LlmUsageFilters) => api.get<{
    totals: LlmUsageCounters;
    by_model: LlmUsageGroup[];
    by_module: LlmUsageGroup[];
    by_model_module: LlmUsageGroup[];
    filters: Record<string, unknown>;
  }>("/admin/llm-usage/summary", { params: filters }),
  calls: (filters: LlmUsageFilters & { limit: number; offset: number }) => api.get<{
    items: LlmUsageCall[];
    total: number;
    limit: number;
    offset: number;
  }>("/admin/llm-usage/calls", { params: filters }),
};

// =============================================================================
// AI Evals (testy regresyjne modułów AI)
// =============================================================================

export type AiEvalAvailable = {
  module_key: string;
  questions_count: number;
  path: string;
  runnable: boolean;
};

export type AiEvalLatest = {
  module_key: string;
  id: number;
  passed: number;
  failed: number;
  total: number;
  avg_score: number | null;
  started_at: string;
};

export type AiEvalRunSummary = {
  id: number;
  module_key: string;
  config_version: number | null;
  started_at: string;
  finished_at: string | null;
  status: "running" | "completed" | "failed" | "partial";
  total: number;
  passed: number;
  failed: number;
  avg_score: number | null;
  cost_usd: number;
  triggered_by_client_id: number | null;
  notes: string | null;
};

export type AiEvalResult = {
  id: number;
  question_id: string;
  question: string;
  expected_keywords: string | null;
  expected_refuse: boolean;
  actual_response: string | null;
  passed: boolean;
  failure_reason: string | null;
  judge_score: number | null;
  judge_reason: string | null;
  latency_ms: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
};

export type AiEvalRunDetail = AiEvalRunSummary & {
  results: AiEvalResult[];
};

export const aiEvalsApi = {
  stats: () =>
    api.get<{ latest_per_module: AiEvalLatest[] }>("/admin/ai-evals/stats"),

  available: () =>
    api.get<{ evals: AiEvalAvailable[]; supported_modules: string[] }>(
      "/admin/ai-evals/available",
    ),

  listRuns: (moduleKey?: string, limit = 50) => {
    const params = new URLSearchParams();
    if (moduleKey) params.set("module_key", moduleKey);
    params.set("limit", String(limit));
    return api.get<{ runs: AiEvalRunSummary[] }>(
      `/admin/ai-evals/runs?${params.toString()}`,
    );
  },

  getRun: (runId: number) =>
    api.get<{ run: AiEvalRunDetail }>(`/admin/ai-evals/runs/${runId}`),

  triggerRun: (moduleKey: string, payload: { use_judge?: boolean; notes?: string }) =>
    api.post<{ triggered: boolean; module_key: string }>(
      `/admin/ai-evals/run/${encodeURIComponent(moduleKey)}`,
      payload,
    ),
};

// =============================================================================
// Chat Heli (AI asystent)
// =============================================================================

export type ChatConversation = {
  id: number;
  title: string | null;
  created_at: string;
  last_msg_at: string;
  message_count: number;
};

export type ChatCitation = {
  chunk_id: number;
  doc_title: string | null;
  source_url: string | null;
  category: string | null;
  score: number | null;
};

export type ChatToolCall = {
  name: string;
  args: Record<string, unknown>;
  summary?: string;
};

export type ChatMessage = {
  id: number;
  role: "user" | "assistant" | "tool" | "system";
  content: string;
  tool_calls: ChatToolCall[] | null;
  citations: ChatCitation[] | null;
  finish_reason: string | null;
  created_at: string;
};

export const chatApi = {
  listConversations: () =>
    api.get<{ conversations: ChatConversation[] }>("/me/chat/conversations"),

  createConversation: (title?: string) =>
    api.post<{ id: number }>("/me/chat/conversations", { title: title || null }),

  getConversation: (id: number) =>
    api.get<{ conversation: ChatConversation; messages: ChatMessage[] }>(
      `/me/chat/conversations/${id}`,
    ),

  deleteConversation: (id: number) =>
    api.delete<{ success: boolean }>(`/me/chat/conversations/${id}`),

  /**
   * SSE streaming endpoint. Zwraca obiekt z helperem do iteracji eventów.
   * NIE używamy `api` axios bo on nie obsługuje streamingu — fetch z ReadableStream.
   */
  streamMessage: async (
    conversationId: number,
    content: string,
    onEvent: (event: string, data: Record<string, unknown>) => void,
  ): Promise<void> => {
    const csrf = getCsrfToken();
    if (!csrf) throw new Error("Sesja nie zawiera ochrony CSRF");

    const resp = await fetch(
      `/api/v1/me/chat/conversations/${conversationId}/messages`,
      {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrf,
      },
      body: JSON.stringify({ content }),
      },
    );
    if (!resp.ok || !resp.body) {
      throw new Error(`HTTP ${resp.status}`);
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE bloki rozdzielone podwójnym newlinem
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const lines = raw.split("\n");
        let event = "message";
        let data = "";
        for (const line of lines) {
          if (line.startsWith("event:")) event = line.slice(6).trim();
          else if (line.startsWith("data:")) data = line.slice(5).trim();
        }
        if (data) {
          try {
            onEvent(event, JSON.parse(data));
          } catch {
            onEvent(event, { raw: data });
          }
        }
      }
    }
  },
};

// =============================================================================
// Team management (Client team — zespół właściciela sklepu)
// =============================================================================

export type TeamPermission = {
  key: string;
  label: string;
  category: string;
};

export type TeamRole = {
  id: string;
  label: string;
  description: string;
  non_assignable?: boolean;
};

export type TeamCatalog = {
  permissions: TeamPermission[];
  roles: TeamRole[];
  role_presets: Record<string, string[]>;
};

export type TeamMember = {
  id: number;
  email: string;
  name: string | null;
  role: string;
  role_label: string;
  is_active: boolean;
  permissions: string[];
  is_owner: boolean;
};

export type TeamInvitation = {
  id: number;
  email: string;
  role_preset: string | null;
  permissions: string[];
  status: "pending" | "accepted" | "revoked" | "expired";
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
};

export type TeamInvitationCreateResponse = TeamInvitation & {
  token: string;
  accept_url: string;
};

export type TeamInvitationLookup = {
  email: string;
  role_preset: string | null;
  status: string;
  expires_at: string | null;
  owner_name: string;
};

export const teamApi = {
  catalog: () => api.get<TeamCatalog>("/me/team/catalog"),

  listMembers: () =>
    api.get<{ members: TeamMember[]; owner_id: number | null }>("/me/team/members"),

  updateMember: (
    memberId: number,
    payload: {
      role_preset?: string | null;
      permission_keys?: string[] | null;
      name?: string | null;
    },
  ) =>
    api.patch<{ member: TeamMember }>(
      `/me/team/members/${memberId}`,
      payload,
    ),

  removeMember: (memberId: number) =>
    api.delete<{ success: boolean }>(`/me/team/members/${memberId}`),

  createInvitation: (payload: {
    email: string;
    role_preset: string;
    permission_keys?: string[] | null;
  }) =>
    api.post<TeamInvitationCreateResponse>("/me/team/invitations", payload),

  listInvitations: (onlyPending = true) =>
    api.get<{ invitations: TeamInvitation[] }>(
      `/me/team/invitations?only_pending=${onlyPending}`,
    ),

  revokeInvitation: (invitationId: number) =>
    api.delete<{ success: boolean }>(`/me/team/invitations/${invitationId}`),

  // Public lookup po tokenie (bez auth) — UI akceptacji
  lookupInvitation: (token: string) =>
    api.get<TeamInvitationLookup>(
      `/team/invitations/${encodeURIComponent(token)}`,
    ),

  acceptInvitation: (token: string) =>
    api.post<{ success: boolean; owner_id: number }>(
      "/me/team/accept-invitation",
      { token },
    ),
};

// =============================================================================
// AI Feedback (👍 / 👎 pod odpowiedziami modułów AI)
// =============================================================================

export type AiFeedbackRating = "up" | "down";

export type AiFeedbackItem = {
  id: number;
  module_key: string;
  source_type: string;
  source_id: number;
  client_id: number;
  rating: AiFeedbackRating;
  comment: string | null;
  related_config_version: number | null;
  created_at: string;
  processed_at: string | null;
  processed_by_client_id: number | null;
  processed_notes: string | null;
};

export type AiFeedbackItemWithContext = AiFeedbackItem & {
  source_context: {
    id: number;
    role: string;
    content: string;
    citations: string | null;
    tool_calls: string | null;
    created_at: string;
  } | null;
};

export type AiFeedbackModuleStats = {
  module_key: string;
  total: number;
  up: number;
  down: number;
  unprocessed_down: number;
  negative_rate: number;
};

export type AiFeedbackStats = {
  window_days: number;
  per_module: AiFeedbackModuleStats[];
  overall: {
    total: number;
    up: number;
    down: number;
    unprocessed_down: number;
    negative_rate: number;
  };
};

export const aiFeedbackApi = {
  // User-side: pod wiadomością Heli
  submitForMessage: (messageId: number, rating: AiFeedbackRating, comment?: string) =>
    api.post<{ id: number; action: "created" | "updated"; rating: AiFeedbackRating }>(
      `/me/chat/messages/${messageId}/feedback`,
      { rating, comment },
    ),

  getMyVoteForMessage: (messageId: number) =>
    api.get<{
      vote: { id: number; rating: AiFeedbackRating; comment: string | null; created_at: string } | null;
    }>(`/me/chat/messages/${messageId}/feedback`),

  // Admin
  stats: (days = 30) =>
    api.get<AiFeedbackStats>(`/admin/ai-feedback/stats?days=${days}`),

  queue: (params: {
    module_key?: string;
    rating?: AiFeedbackRating;
    processed?: boolean;
    limit?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.module_key) qs.set("module_key", params.module_key);
    if (params.rating) qs.set("rating", params.rating);
    if (params.processed !== undefined) qs.set("processed", String(params.processed));
    if (params.limit) qs.set("limit", String(params.limit));
    return api.get<{ items: AiFeedbackItem[] }>(
      `/admin/ai-feedback/queue${qs.toString() ? "?" + qs.toString() : ""}`,
    );
  },

  detail: (id: number) =>
    api.get<AiFeedbackItemWithContext>(`/admin/ai-feedback/${id}`),

  process: (id: number, notes?: string) =>
    api.post<{ processed: boolean }>(`/admin/ai-feedback/${id}/process`, { notes }),

  unprocess: (id: number) =>
    api.post<{ processed: boolean }>(`/admin/ai-feedback/${id}/unprocess`),

  timeseries: (moduleKey: string, days = 30) =>
    api.get<{ points: Array<{ day: string; up: number; down: number }> }>(
      `/admin/ai-feedback/timeseries?module_key=${encodeURIComponent(moduleKey)}&days=${days}`,
    ),
};

// ============================================================================
// F1 (2026-08-12): Agencja AI — decisions/personas/events/hard-limits
// ============================================================================

export interface Persona {
  code: string;
  display_name: string;
  initials: string;
  color_key: "blue" | "violet" | "warn" | "good" | "bad" | "gray" | "accent";
  area: string;
  competencies: string[];
  available_from: string | null;
  is_available: boolean;
  autonomy_level: "Doradca" | "Wykonawca" | "Autopilot";
  joined_at: string | null;
  decisions_count: number;
  acceptance_pct: number | null;
  // F5-3 uzupełnienie: skuteczność (pomiary)
  measured_count?: number;
  effectiveness_pct?: number | null;
  total_value_pln?: number;
  // Sesja 4 (brief §2.6): warunek blokujący.
  // null gdy persona działa normalnie; obiekt gdy zablokowana na Doradcy.
  blocking?: { reason: string; voice: string } | null;
}

export interface PersonaDetail extends Persona {
  voice_style_prompt?: string;
}

export interface AffectedOffer {
  id: number;
  allegro_offer_id: string | null;
  title: string;
  image_url: string | null;
  url: string;
}

export interface AffectedCampaign {
  campaign_id: string;
  name: string;
  status: string | null;
  url: string;
}

/** Konkretna zmiana proponowana przez agenta w ramach decyzji.
 *  Renderowane jako BEFORE → AFTER inline pod tile'em target-a. */
export interface DecisionChange {
  target_type: "offer" | "campaign" | "adgroup" | "account";
  target_id: number | string;
  target_label: string;
  field: string;              // np. "price_amount", "ads_status"
  field_label: string;        // np. "Cena", "Ads dla tej oferty"
  before: number | string | null;
  after: number | string | null;
  unit: string | null;        // "zł", "%", "szt" lub null
  delta_pct: number | null;   // opcjonalny procent zmiany
  reason: string | null;      // mikro-uzasadnienie per zmianę
}

export interface DecisionExpectedEffect {
  metric: string;             // np. "monthly_margin_pln"
  current: number;
  projected: number;
  confidence: "low" | "medium" | "high";
}

/** Sprint IV/2 (2026-08-21): efekt zmierzony 14 dni po wykonaniu decyzji.
 *  Spec §Szymon Obszar A: rejestr decyzji musi mieć „skutek po 14 dniach".
 *  Backend liczy w measurement_service, tu tylko exposure. */
export interface DecisionMeasurement {
  effect_pln: number;
  outcome: "positive" | "neutral" | "negative";
  measured_at: string;
  period_days: number;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export interface Decision {
  id: number;
  persona_code: string;
  title: string;
  action_kind: string;
  reasoning: string;
  impact_pln: number | null;
  impact_note: string | null;
  risk_level: "low" | "medium" | "high";
  reversible: boolean;
  scope: Record<string, unknown> | null;
  affected_offers?: AffectedOffer[];
  affected_campaigns?: AffectedCampaign[];
  status: "pending" | "in_progress" | "awaiting_review" | "approved" | "executing" | "executed" | "measured" | "rejected" | "deferred" | "withdrawn";
  rejection_reason: string | null;
  deferred_until: string | null;
  force_executed: boolean;
  source_kind: string | null;
  source_id: string | null;
  created_at: string;
  decided_at: string | null;
  executed_at: string | null;
  blocked_by_persona: string | null;
  blocked_reason: string | null;
  /** Sprint IV/2: null gdy decyzja jeszcze nie zmierzona (młodsza niż period_days
   *  albo status != 'measured'). Populated dla status='measured'. */
  measurement?: DecisionMeasurement | null;
}

export interface AgencyEvent {
  id: number;
  persona_code: string;
  event_kind: string;
  summary: string;
  reasoning: string | null;
  impact_pln: number | null;
  decision_id: number | null;
  order_id: number | null;
  conflict_with_persona: string | null;
  occurred_at: string;
}

export interface BriefingItem {
  persona_code: string;
  display_name: string;
  initials: string;
  color_key: Persona["color_key"];
  events_count: number;
  events: AgencyEvent[];
}

export interface HardLimits {
  min_margin_pct: number | null;
  max_ads_spend_monthly_pln: number | null;
  max_price_change_pct: number | null;
  excluded_categories: string[];
  updated_at: string | null;
}

// Osobno bo nazwa `agencyApi` już zajęta przez stary Ads agency flow (line 574).
export const personasApi = {
  list: () => api.get<{ items: Persona[] }>("/personas"),
  get: (code: string) => api.get<PersonaDetail>(`/personas/${code}`),
  setAutonomy: (code: string, level: Persona["autonomy_level"]) =>
    api.put<{ success: boolean; code: string; autonomy_level: string }>(
      `/personas/${code}/autonomy`,
      { autonomy_level: level },
    ),
};

// Piotr — Strateg Konkurencji (widoczność w /konkurencja)
export interface CompetitorSummary {
  module_active: boolean;
  last_scan_at: string | null;
  tracked_categories: number;
  total_snapshots: number;
  snapshots_7d: number;
  autonomy_level: "Doradca" | "Wykonawca" | "Autopilot";
  persona_code: string;
}
export interface CompetitorCategory {
  category_id: string;
  category_name: string;
  snapshots_count: number;
  unique_offers: number;
  median_price: number;
  min_price: number;
  max_price: number;
  last_scan_at: string;
  top_cheapest: Array<{ title: string; price: number; seller: string | null }>;
}
export interface CompetitorEvent {
  kind: "new_player" | "price_change";
  occurred_at: string;
  seller: string | null;
  title: string;
  category_id: string;
  allegro_offer_id: string;
  price?: number;
  price_before?: number;
  price_after?: number;
  change_pct?: number;
}
export const competitorsApi = {
  summary: () => api.get<CompetitorSummary>("/competitors/summary"),
  categories: (params?: { limit?: number; max_age_hours?: number }) =>
    api.get<{ items: CompetitorCategory[]; max_age_hours: number }>(
      "/competitors/categories", { params },
    ),
  activity: (params?: { limit?: number; days?: number }) =>
    api.get<{ items: CompetitorEvent[]; days: number }>(
      "/competitors/activity", { params },
    ),
  scan: () =>
    api.post<{ success: boolean; voice: string; result?: unknown; error?: string }>(
      "/competitors/scan",
    ),
};

export const demoAgencyApi = {
  seed: () =>
    api.post<{
      success: boolean;
      decisions_created: number;
      events_created: number;
      measurements_created: number;
      orders_created: number;
    }>("/demo/agency-seed"),
  clear: () =>
    api.post<{
      success: boolean;
      decisions_deleted: number;
      events_deleted: number;
      orders_deleted: number;
    }>("/demo/agency-clear"),
};

export const decisionsApi = {
  list: (params?: { status?: string; persona?: string; limit?: number; offset?: number }) =>
    api.get<{ items: Decision[]; pending_count: number; limit: number; offset: number }>(
      "/decisions",
      { params },
    ),
  get: (id: number) => api.get<Decision>(`/decisions/${id}`),
  approve: (id: number) =>
    api.post<{ success: boolean; new_status: string; autonomy: string }>(`/decisions/${id}/approve`),
  forceExecute: (id: number) =>
    api.post<{ success: boolean; new_status: string; force_executed: boolean }>(
      `/decisions/${id}/force-execute`,
    ),
  reject: (id: number, reason: string) =>
    api.post<{ success: boolean; new_status: string }>(`/decisions/${id}/reject`, { reason }),
  defer: (id: number, days: number) =>
    api.post<{ success: boolean; new_status: string; deferred_until: string }>(
      `/decisions/${id}/defer`,
      { days },
    ),
  /** Sesja 27 (brief §5.10): symulacja bez zmiany na koncie. */
  simulate: (id: number) =>
    api.post<{
      decision_id: number;
      action_kind: string;
      persona_code: string;
      before: Record<string, unknown>;
      after: Record<string, unknown>;
      warnings: string[];
      guard_info: string[];
      reverse_plan: string;
      voice: string;
    }>(`/decisions/${id}/simulate`),
  /** T0.1k: Zatwierdzenie #1 — agent zaczyna pracę (pending → in_progress
   *  → od razu awaiting_review przez placeholder handler). */
  takeOn: (id: number) =>
    api.post<{ success: boolean; new_status: string }>(`/decisions/${id}/take-on`),
  /** T0.1k: Zatwierdzenie #2 — user obejrzał preview i puszcza do Allegro.
   *  approvedIndices=null → wszystkie. [] → reject wszystkiego. */
  confirm: (id: number, approvedIndices?: number[]) =>
    api.post<{ success: boolean; new_status: string }>(`/decisions/${id}/confirm`, {
      approved_indices: approvedIndices ?? null,
    }),
  /** T0.1j legacy: user zamyka plan manualnie. Zostaje dla kompat. */
  complete: (id: number) =>
    api.post<{ success: boolean; new_status: string }>(`/decisions/${id}/complete`),
  /** T0.1j: pobierz plan naprawy — dynamicznie z scope.audit_finding_ids. */
  getPlan: (id: number) =>
    api.get<{
      decision: Decision;
      items: DecisionPlanItem[];
      progress: { done: number; total: number };
    }>(`/decisions/${id}/plan`),
  /** T0.1j: zaznacz/odznacz pozycję planu. */
  togglePlanItem: (id: number, itemId: string, isDone: boolean) =>
    api.post<{ success: boolean; item_id: string; is_done: boolean; done_count: number }>(
      `/decisions/${id}/plan/${encodeURIComponent(itemId)}/toggle`,
      { is_done: isDone },
    ),
};

/** T0.1j: pozycja planu naprawy — dynamicznie z audit_rule_results. */
export interface DecisionPlanItem {
  id: string;
  rule_id: string | null;
  rule_name: string;
  recommendation: string;
  severity: "critical" | "high" | "medium" | "low" | "info";
  audit_scope: string;
  action_hint: {
    label: string;
    internal_url: string | null;
    external_url: string | null;
    external_label: string | null;
  };
  is_done: boolean;
}

export interface ChangeLogEntry {
  id: number;
  kind: string;
  target_type: string;
  target_id: string;
  summary: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  actor_type: string;
  source_module: string | null;
  created_at: string;
  reverted_at: string | null;
  reverted_by: number | null;
  revert_error: string | null;
  revertable: boolean;
}

export const changeLogApi = {
  list: (params?: { kind?: string; limit?: number; include_reverted?: boolean }) =>
    api.get<{ items: ChangeLogEntry[] }>("/change-log", { params }),
  get: (id: number) => api.get<ChangeLogEntry>(`/change-log/${id}`),
  revert: (id: number) =>
    api.post<{ success: boolean; change_id: number }>(`/change-log/${id}/revert`),
};

export const agencyEventsApi = {
  list: (params?: { persona?: string; event_kind?: string; limit?: number }) =>
    api.get<{ items: AgencyEvent[] }>("/agency-events", { params }),
  briefing: (hours = 24) =>
    api.get<{ since_hours: number; items: BriefingItem[] }>("/agency-events/briefing", {
      params: { hours },
    }),
};

export const hardLimitsApi = {
  get: () => api.get<HardLimits>("/hard-limits"),
  set: (payload: Partial<Omit<HardLimits, "updated_at">>) =>
    api.put<{ success: boolean }>("/hard-limits", payload),
};

// ============================================================================
// F1-9/10 (2026-08-12): Zlecanie zadań w NL (orders_agency)
// ============================================================================

export interface OrderTaskInput {
  persona_code: string;
  task_description: string;
  deadline_at: string | null;
  price_pln: number;
  /** NL parser 2026-08-13: co system ma wywołać natychmiast. */
  action_kind?: "title" | "description" | "parameters" | "thumbnail" | "gallery" | "report" | "audit" | "copy" | "generic" | null;
  action_data?: {
    filter?: {
      category_name?: string;
      category_id?: string;
      offer_ids?: string[];
      status?: string;
    };
    prompt_hint?: string;
    template_id?: number;
  } | null;
}

export interface NlParseResult {
  breakdown: OrderTaskInput[];
  price_total_pln: number;
  unresolved_persona_codes: string[];
  warning: string | null;
}

export interface OrderSummary {
  id: number;
  request_text: string;
  status: "accepted" | "in_progress" | "ready_for_approval" | "pending_review" | "completed" | "needs_revision" | "cancelled";
  price_total_pln: number;
  is_recurring: boolean;
  recurrence_pattern: string | null;
  next_run_at: string | null;
  created_at: string;
  completed_at: string | null;
  tasks_summary: Record<string, number>;
}

export interface OrderTask {
  id: number;
  persona_code: string;
  task_description: string;
  deadline_at: string | null;
  price_pln: number;
  status: "pending" | "in_progress" | "done" | "failed" | "needs_human" | "ready_for_approval" | "cancelled";
  result_json: string | null;
  decision_id: number | null;
  created_at: string;
  completed_at: string | null;
}

export interface OrderMessage {
  id: number;
  author: string;
  content: string;
  created_at: string;
}

export interface OrderDetail {
  order: OrderSummary & { breakdown_json: string };
  tasks: OrderTask[];
  messages: OrderMessage[];
}

export const ordersAgencyApi = {
  nlParse: (request_text: string, offer_ids?: string[]) =>
    api.post<NlParseResult>("/orders-agency/nl-parse", { request_text, offer_ids }),
  create: (payload: {
    request_text: string;
    breakdown: OrderTaskInput[];
    is_recurring?: boolean;
    recurrence_pattern?: string;
    offer_ids?: string[];
  }) => api.post<{ success: boolean; order_id: number; price_total_pln: number }>(
    "/orders-agency",
    payload,
  ),
  list: (params?: { status?: string; limit?: number }) =>
    api.get<{ items: OrderSummary[] }>("/orders-agency", { params }),
  get: (id: number) => api.get<OrderDetail>(`/orders-agency/${id}`),
  postMessage: (id: number, content: string) =>
    api.post<{ success: boolean; message_id: number }>(
      `/orders-agency/${id}/messages`,
      { content },
    ),
  cancel: (id: number) => api.post<{ success: boolean }>(`/orders-agency/${id}/cancel`),
};

// ============================================================================
// F2 (2026-08-12): Model kosztu z historią + silnik marży rzeczywistej
// ============================================================================

export interface CostRecord {
  purchase_price: number;
  shipping_cost: number;
  packaging_cost: number;
  other_costs: number;
  is_estimated: boolean;
  notes?: string | null;
  updated_at?: string;
}

export interface CoverageStats {
  total_offers: number;
  with_cost: number;
  with_cost_real: number;
  with_cost_estimated: number;
  missing_cost: number;
  coverage_pct: number;
}

export interface OfferProfitInPeriod {
  offer_id: number;
  allegro_offer_id: string | null;
  title: string | null;
  orders_count: number;
  units_sold: number;
  gross_revenue: number;
  total_cost: number;
  net_profit: number;
  margin_pct: number | null;
  has_missing_cost_data: boolean;
  is_estimated: boolean;
}

export interface PeriodProfitSummary {
  period: { from: string; to: string };
  orders_analyzed: number;
  orders_with_all_costs: number;
  orders_missing_cost: number;
  orders_with_estimated_cost: number;
  units_sold: number;
  gross_revenue: number;
  total_cost: number;
  net_profit: number;
  margin_pct: number | null;
}

export const costsApi = {
  setCost: (payload: {
    offer_id: number;
    purchase_price: number;
    shipping_cost?: number;
    packaging_cost?: number;
    other_costs?: number;
    is_estimated?: boolean;
    source?: string;
    notes?: string;
  }) => api.post<{ success: boolean; history_id: number }>("/costs", payload),
  importCsv: (csv_text: string, opts?: { mark_all_as_estimated?: boolean }) =>
    api.post<{ imported: number; skipped: number; errors: string[]; column_map_used: Record<string, string> }>(
      "/costs/import-csv",
      { csv_text, mark_all_as_estimated: opts?.mark_all_as_estimated || false },
    ),
  /**
   * Sesja 5 (brief §2.3): import z integratora zamówień.
   * Backend odpowiada 501 z komunikatem głosem Michała dopóki wpięcie
   * BaseLinker/Apilo/Subiekt nie jest gotowe.
   */
  importFromIntegrator: (provider: "baselinker" | "apilo" | "subiekt", api_token?: string) =>
    api.post<{ success: boolean }>("/costs/import-from-integrator", {
      provider,
      api_token,
      dry_run: true,
    }),
  /**
   * Sesja 6 (brief §2.4): szacunek kosztu z mediany marży kategorii.
   * Zwraca is_estimated=true + estimation_source + confidence.
   */
  estimate: (offer_id: number) =>
    api.get<{
      purchase_price: number;
      shipping_cost: number;
      packaging_cost: number;
      other_costs: number;
      is_estimated: true;
      estimation_source: "category" | "client_median" | "market_default_30pct";
      estimated_margin_pct: number;
      sample_size?: number;
      confidence: "low" | "medium" | "high";
    }>(`/costs/${offer_id}/estimate`),
  coverage: (shop_id?: number) =>
    api.get<CoverageStats>("/costs/coverage", { params: shop_id ? { shop_id } : {} }),
  getCost: (offer_id: number) =>
    api.get<{ offer_id: number; cost: CostRecord | null }>(`/costs/${offer_id}`),
  history: (offer_id: number, limit = 50) =>
    api.get<{ offer_id: number; history: unknown[] }>(`/costs/${offer_id}/history`, { params: { limit } }),
  costAt: (offer_id: number, ts: string) =>
    api.get<{ offer_id: number; at: string; cost: CostRecord | null }>(`/costs/${offer_id}/at`, {
      params: { ts },
    }),
  // Marża
  periodProfit: (d_from: string, d_to: string) =>
    api.get<PeriodProfitSummary>("/costs/margin/period", { params: { d_from, d_to } }),
  topOffersByProfit: (d_from: string, d_to: string, limit = 20) =>
    api.get<{ period: { from: string; to: string }; items: OfferProfitInPeriod[] }>(
      "/costs/margin/top-offers",
      { params: { d_from, d_to, limit } },
    ),
  offerProfitInRange: (offer_id: number, d_from: string, d_to: string) =>
    api.get<OfferProfitInPeriod>(`/costs/margin/offer/${offer_id}`, {
      params: { d_from, d_to },
    }),
};

// ============================================================================
// F5-3 (2026-08-12): Licznik wartości agencji
// ============================================================================

export interface ValueDeliveredResponse {
  period: { from: string | null; to: string | null; label: string };
  value: {
    net_pln: number;
    positive_pln: number;
    negative_pln: number;
    positive_count: number;
    negative_count: number;
  };
  subscription: {
    monthly_pln: number;
    return_multiplier: number | null;
  };
  breakdown_by_persona: Array<{ persona_code: string; count: number; total_pln: number }>;
}

export interface ValueBreakdownItem {
  id: number;
  decision_id: number;
  effect_pln: number;
  outcome: "positive" | "neutral" | "negative";
  measured_at: string;
  period_days: number;
  title: string;
  persona_code: string;
  action_kind: string;
  executed_at: string;
}

export const valueCounterApi = {
  get: (period: "month" | "30d" | "90d" | "all" = "month") =>
    api.get<ValueDeliveredResponse>("/me/value-delivered", { params: { period } }),
  breakdown: (period: "month" | "30d" | "90d" | "all" = "month", limit = 30) =>
    api.get<{ items: ValueBreakdownItem[]; period: string }>(
      "/me/value-delivered/breakdown",
      { params: { period, limit } },
    ),
};

// ============================================================================
// F6 (2026-08-12): Obsługa posprzedażowa Ani
// ============================================================================

export interface CustomerMessage {
  id: number;
  buyer_login: string | null;
  buyer_login_masked?: string | null;
  order_ref: string | null;
  subject: string | null;
  content: string;
  direction: "incoming" | "outgoing";
  status: "new" | "in_progress" | "answered" | "escalated" | "closed";
  received_at: string;
  replied_at: string | null;
  allegro_thread_id?: string | null;
  allegro_message_id?: string | null;
  kind: string | null;
  priority: string | null;
  is_dispute: number | boolean | null;
  sentiment: string | null;
  detected_issue: string | null;
}

export interface CustomerServiceCounters {
  new_messages: number;
  escalated_messages: number;
  open_tickets: number;
  pending_deadlines_48h: number;
  urgent_deadlines_24h: number;
  urgent_examples: Array<{
    id: number;
    related_type: string;
    related_id: number;
    deadline_kind: string;
    due_at: string;
  }>;
  /** #623: otwarte terminy po czasie; pola terminów wyżej liczą tylko terminy przed upływem. */
  overdue_deadlines?: number;
  /** #676: jedna liczba „Do Ciebie” — ta sama w menu, na pulpicie i w Obsłudze. */
  to_you?: number;
  late?: number;
  due_today?: number;
}

export interface ValidationFinding {
  code: string; // np. REPLY.CONTACT.PHONE
  severity: "block" | "warn" | "info";
  span: string; // fragment naruszający regułę
  suggestion: string; // sugestia dla usera
}

export interface ReplyValidationResult {
  status: "pass" | "warn" | "block";
  findings: ValidationFinding[];
  sanitized_text?: string | null;
  llm_pass_used?: boolean;
  validator_version?: string;
}

export interface ReplyProposal {
  reply_text: string | null;
  reasoning: string;
  requires_escalation: boolean;
  matched_policy_id: number | null;
  confidence: "low" | "medium" | "high";
  // Sprint XIII T1 (2026-08-24): walidator wyjścia.
  // Backend zwraca te pola gdy auto-generated reply przeszła przez guardrail.
  validation_status?: "pass" | "warn" | "block" | "unverified";
  validation_findings?: ValidationFinding[];
  blocked_reason?: string;
  sanitized_text?: string | null;
  // #676: pewność 0–1, dopasowana polityka i dane, na których oparto szkic.
  confidence_score?: number;
  matched_policy_name?: string | null;
  intent?: string;
  data_used?: string[];
  // Szkic zawsze trafia do akceptacji; ESCALATE znaczy, że decyzja należy do Ciebie.
  action?: "REPLY" | "DRAFT" | "ESCALATE";
  escalation_reason?: string | null;
  draft_unverified?: boolean;
}

export interface ServicePolicy {
  id: number;
  kind: string;
  name: string;
  trigger_condition: Record<string, unknown> | null;
  action_template: string;
  llm_instructions: string | null;
  is_active: number | boolean;
  priority: number;
  updated_at: string;
}

/** T1g (2026-08-26): wątek = korespondencja z 1 kupującym (jak w Allegro). */
export interface CustomerThread {
  thread_id: string;
  buyer_login: string | null;
  buyer_login_masked: string | null;
  subject: string | null;
  order_ref: string | null;
  last_message_at: string;
  last_message_snippet: string | null;
  latest_direction: "incoming" | "outgoing";
  latest_status: "new" | "in_progress" | "answered" | "escalated" | "closed";
  latest_message_id: number;
  latest_kind: string | null;
  latest_priority: string | null;
  latest_is_dispute: number | boolean | null;
  total_count: number;
  unread_count: number;
}

// ── Obsługa 2.0 (#676): skrzynka z kolejkami i terminami ────────────────

export type InboxQueue = "me" | "ania" | "wait" | "closed";
export type InboxSla = "late" | "today" | "later";

export interface InboxThread {
  thread_id: string;
  channel: string;
  queue: InboxQueue;
  buyer: string | null;
  subject: string | null;
  snippet: string;
  latest_direction: "incoming" | "outgoing";
  last_message_at: string | null;
  client_message_at: string | null;
  client_message_id: number | null;
  kind: string | null;
  sentiment: string | null;
  is_dispute: boolean;
  detected_issue: string | null;
  order_ref: string | null;
  total_count: number;
  unread_count: number;
  due_at: string | null;
  sla: InboxSla;
  sla_progress: number | null;
  /** Sprawa z Allegro (dyskusja, reklamacja): tylko do odczytu, odpowiedź w Allegro. */
  read_only?: boolean;
  /** Status sprawy w Allegro (np. CLAIM_SUBMITTED) dla rozmów tylko do odczytu. */
  issue_status?: string | null;
  /** Konto Allegro rozmowy (filtr kont, #908). */
  shop_id?: number | null;
  /** Zamknięta przy imporcie: przyszła ponad 14 dni przed pobraniem do Healsy (#908). */
  archived_import?: boolean;
  /** Pilne: termin w ciągu 5 godzin albo po terminie, otwarta dyskusja lub reklamacja (#908). */
  urgent?: boolean;
}

export interface InboxCounters {
  to_you: number;
  late: number;
  due_today: number;
  ania_sent: number;
  waiting: number;
  /** Pilne, Zamknięte, z tego archiwum importu, wszystkie rozmowy (#908). */
  urgent: number;
  closed: number;
  archived_import: number;
  total: number;
  /** Udział spraw obsłużonych przez Anię; null do czasu autopilota. */
  auto_share: number | null;
}

export type InboxBulkAction = "close" | "reopen" | "read";

export interface InboxData {
  counters: InboxCounters;
  channels: string[];
  /** Konta Allegro klienta do filtra listy (#908). */
  shops?: { id: number; label: string }[];
  items: InboxThread[];
  generated_at: string;
}

export interface ThreadContextMessage {
  id: number;
  direction: "incoming" | "outgoing";
  status: string;
  received_at: string | null;
  content: string;
  /** Autor wiadomości sprawy z Allegro: BUYER, SELLER, ADMIN, FULFILLMENT. */
  author_role?: string | null;
}

export interface ThreadContext {
  thread_id: string;
  channel: string;
  buyer: string | null;
  subject: string | null;
  client_message_id: number | null;
  classification: {
    kind: string | null;
    sentiment: string | null;
    is_dispute: boolean;
    detected_issue: string | null;
  } | null;
  messages: ThreadContextMessage[];
  order: {
    order_id: string;
    total_amount: number | null;
    currency: string;
    status: string | null;
    delivery_method: string | null;
    items: Array<{ name: string; quantity: number | null }>;
    created_at: string | null;
    paid_at: string | null;
    shipped_at: string | null;
    delivered_at: string | null;
  } | null;
  order_ref: string | null;
  offer: { allegro_offer_id: string; title: string | null; price_amount: number | null } | null;
  buyer_history: { orders: number; threads: number } | null;
  deadline: { kind: string; due_at: string | null } | null;
  /** Sprawa z Allegro: tylko do odczytu. */
  read_only?: boolean;
  issue?: {
    type: string | null;
    status: string | null;
    allegro_status: string | null;
    reference_number: string | null;
    description: string | null;
    opened_at: string | null;
  } | null;
}

/** Część stanu sprawy z Allegro; `available: false` z kodem błędu, gdy się nie udała. */
export interface CaseSection<T> {
  available: boolean;
  error: string | null;
  items: T[];
}

export interface CaseTrackingStatus {
  code: string | null;
  label: string | null;
  occurred_at: string | null;
}

export interface CaseShipment {
  waybill: string;
  carrier_id: string | null;
  carrier_name: string | null;
  created_at: string | null;
  tracking: {
    status: string | null;
    label: string | null;
    occurred_at: string | null;
    history: CaseTrackingStatus[];
  } | null;
}

export interface CaseReturn {
  status: string | null;
  label: string | null;
  created_at: string | null;
  reference_number: string | null;
  /** Typy powodów zwrotu z Allegro (bez komentarza kupującego). */
  reasons: string[];
  parcels: Array<{ waybill: string; carrier_id: string | null; created_at: string | null }>;
  rejection_code: string | null;
}

/** Zwrot płatności zamówienia z Allegro (#888). */
export interface CaseRefund {
  status: string | null;
  label: string | null;
  /** Wypłacony (całkowicie albo częściowo). */
  done: boolean;
  reason: string | null;
  reason_label: string | null;
  created_at: string | null;
  amount: { amount: number; currency: string } | null;
}

/** Otwarta dyskusja albo reklamacja zamówienia (z synchronizacji). */
export interface CaseIssue {
  type: "dispute" | "claim";
  status: string | null;
  label: string | null;
  opened_at: string | null;
  decision_due_at: string | null;
}

export interface CaseOrder {
  available: boolean;
  error: string | null;
  status?: string | null;
  status_label?: string | null;
  fulfillment_status?: string | null;
  fulfillment_label?: string | null;
  bought_at?: string | null;
  paid_at?: string | null;
  delivery_method?: string | null;
  pickup_point?: string | null;
  delivery_from?: string | null;
  delivery_to?: string | null;
  cancelled_at?: string | null;
  invoice_required?: boolean;
}

/** Stan sprawy z Allegro dla rozmowy (#864). */
export interface ThreadCase {
  order_id: string | null;
  /** `message`: zamówienie wskazane w rozmowie; `buyer`: jedyne zamówienie kupującego. */
  match: "message" | "buyer" | null;
  /**
   * `not_fetched`: brak stanu w pamięci (panel nie pyta Allegro sam);
   * `rate_limited`: limit sprawdzeń w Allegro; `mismatch`: zamówienie innego kupującego.
   */
  status:
    | "ok"
    | "no_order"
    | "invalid_order"
    | "unavailable"
    | "not_fetched"
    | "rate_limited"
    | "mismatch";
  case: {
    order_id: string;
    fetched_at: string;
    order: CaseOrder;
    shipments: CaseSection<CaseShipment> & { tracking_error?: string | null };
    returns: CaseSection<CaseReturn>;
    issues: CaseSection<CaseIssue>;
    refund: CaseSection<CaseRefund>;
  } | null;
}

export const customerServiceApi = {
  inbox: () => api.get<InboxData>("/customer-service/inbox"),
  /** `cached` nie pyta Allegro; `fetch` i `refresh` pobierają w granicach limitów. */
  threadCase: (threadId: string, mode: "cached" | "fetch" | "refresh" = "cached") =>
    api.get<ThreadCase>(
      `/customer-service/threads/${encodeURIComponent(threadId)}/case?mode=${mode}`,
    ),
  threadContext: (threadId: string) =>
    api.get<ThreadContext>(
      `/customer-service/threads/${encodeURIComponent(threadId)}/context`,
    ),
  markThreadRead: (threadId: string) =>
    api.post<{ success: boolean }>(
      `/customer-service/threads/${encodeURIComponent(threadId)}/read`,
    ),
  closeThread: (threadId: string) =>
    api.post<{ success: boolean }>(
      `/customer-service/threads/${encodeURIComponent(threadId)}/close`,
    ),
  /** Akcja masowa (#908): zamknij, przywróć do kolejki, oznacz jako przeczytane. */
  bulkThreads: (action: InboxBulkAction, threadIds: string[]) =>
    api.post<{ success: boolean; changed: number; skipped: number }>(
      "/customer-service/threads/bulk",
      { action, thread_ids: threadIds },
    ),
  /** Wysyła odpowiedź do wątku w Allegro (10 s na cofnięcie odlicza ekran). */
  replyToThread: (threadId: string, text: string) =>
    api.post<{ ok: boolean; recorded?: boolean; message: ThreadContextMessage }>(
      `/customer-service/threads/${encodeURIComponent(threadId)}/reply`,
      { text },
    ),
  listMessages: (params?: {
    status?: string; kind?: string; priority?: string;
    limit?: number; offset?: number;
  }) => api.get<{ items: CustomerMessage[]; limit: number; offset: number }>(
    "/customer-service/messages", { params },
  ),
  /** T1g: wątki (grouped by allegro_thread_id) — flow jak w panelu Allegro. */
  listThreads: (params?: {
    status?: string; kind?: string; limit?: number; offset?: number;
  }) => api.get<{ items: CustomerThread[]; limit: number; offset: number }>(
    "/customer-service/threads", { params },
  ),
  /** T1g: pełna historia wiadomości w wątku (chronologicznie, starsze → nowsze). */
  getThreadMessages: (threadId: string) =>
    api.get<{ thread_id: string; items: CustomerMessage[] }>(
      `/customer-service/threads/${encodeURIComponent(threadId)}/messages`,
    ),
  getMessage: (id: number) =>
    api.get<Record<string, unknown>>(`/customer-service/messages/${id}`),
  classify: (id: number, force = false) =>
    api.post<{ success: boolean; classification_id: number }>(
      `/customer-service/messages/${id}/classify`,
      null,
      { params: { force } },
    ),
  markAnswered: (id: number, reply_text?: string) =>
    api.post<{ success: boolean }>(`/customer-service/messages/${id}/answered`, { reply_text }),
  escalate: (id: number, reason?: string) =>
    api.post<{ success: boolean }>(`/customer-service/messages/${id}/escalate`, { reason }),
  generateReply: (
    id: number,
    opts?: { tone?: "shorter" | "warmer"; previous_draft?: string },
  ) =>
    api.post<ReplyProposal>(
      `/customer-service/messages/${id}/generate-reply`,
      opts ?? null,
    ),
  // Sprint XIII T1 (2026-08-24): waliduje ręcznie edytowany tekst przed wysłaniem.
  // Zwraca status/findings — frontend blokuje "Wyślij" gdy status='block'.
  validateReply: (id: number, text: string) =>
    api.post<ReplyValidationResult>(
      `/customer-service/messages/${id}/validate-reply`,
      { text },
    ),
  counters: () => api.get<CustomerServiceCounters>("/customer-service/counters"),
  /**
   * Sesja 32 (brief §6.1): manual sync wiadomości Allegro.
   * Zwraca stats + voice Ani.
   */
  syncFromAllegro: () =>
    api.post<{
      ok: boolean;
      threads_fetched: number;
      messages_new: number;
      messages_updated: number;
      error: string | null;
      /** Nieudany etap problemów z zakupem przy udanych wiadomościach zwykłych (#690). */
      warning?: string | null;
      purchase_failed_shops?: number;
      voice: string;
    }>("/customer-service/sync"),
  deadlines: (hours_ahead = 48) =>
    // #623: `items` przed upływem; `overdue` po czasie (do 100 pozycji), `overdue_total` — wszystkie.
    api.get<{ items: unknown[]; overdue?: unknown[]; overdue_total?: number; hours_ahead: number }>(
      "/customer-service/deadlines", { params: { hours_ahead } },
    ),
  policies: () => api.get<{ items: ServicePolicy[] }>("/customer-service/policies"),
  createPolicy: (payload: Partial<ServicePolicy> & Pick<ServicePolicy, "kind" | "name" | "action_template">) =>
    api.post<{ success: boolean; id: number }>("/customer-service/policies", payload),
  updatePolicy: (id: number, payload: Partial<ServicePolicy>) =>
    api.put<{ success: boolean }>(`/customer-service/policies/${id}`, payload),
  deletePolicy: (id: number) =>
    api.delete<{ success: boolean }>(`/customer-service/policies/${id}`),
};

// ============================================================================
// F4 (2026-08-12): Audyt przed publikacją
// ============================================================================

export interface PrePublishFinding {
  code: string;
  severity: "blocking" | "warning" | "info";
  message: string;
  persona_code: string;
  field: string | null;
  fix_hint: string | null;
  auto_fixable: boolean;
}

export interface PrePublishAuditResult {
  can_publish: boolean;
  total_blocking: number;
  total_warnings: number;
  summary: string;
  findings: PrePublishFinding[];
}

export interface PrePublishDraftPayload {
  title?: string;
  description_sections?: Array<{ name?: string; content: string }>;
  images?: string[];
  price_amount?: number | null;
  purchase_price?: number | null;
  category_id?: string | null;
  parameters?: Array<{
    id?: string;
    name: string;
    value?: string;
    values?: string[];
    is_required?: boolean;
  }>;
}

export const prePublishAuditApi = {
  audit: (draft: PrePublishDraftPayload) =>
    api.post<PrePublishAuditResult>("/audit/pre-publish", draft),
};

// ── Sprint XII: Mapowanie do Katalogu Allegro (Kasia) ──────────────────

export interface CatalogSuggestionCandidate {
  product_id: string;
  name: string | null;
  category: { id?: string; name?: string };
  images: Array<{ url?: string } | string>;
  vendor: string | null;
  score: number;
  match_reason: string;
}

export interface CatalogSuggestionResponse {
  success: boolean;
  error?: string;
  allegro_offer_id: string;
  offer_title: string | null;
  offer_ean: string | null;
  offer_category_id: string | null;
  offer_category_name: string | null;
  already_linked: boolean;
  current_product_id: string | null;
  candidates: CatalogSuggestionCandidate[];
}

export interface CatalogLinkResponse {
  success: boolean;
  allegro_offer_id: string;
  old_product_id: string | null;
  new_product_id: string;
  changed_at: string;
  changed_by: string;
  note?: string;
}

export const catalogApi = {
  suggest: (allegroOfferId: string, maxResults = 5) =>
    api.get<CatalogSuggestionResponse>(
      `/catalog/suggest/${encodeURIComponent(allegroOfferId)}`,
      { params: { max_results: maxResults } },
    ),
  link: (allegroOfferId: string, productId: string) =>
    api.put<CatalogLinkResponse>("/catalog/link", {
      allegro_offer_id: allegroOfferId,
      product_id: productId,
    }),
};

// ── Sprint X/1: One Fulfillment kalkulator (Michał) ─────────────────────

export interface OfPricingTier {
  tier: string;
  label: string;
  max_weight_kg: number;
  max_dimension_cm: number;
  base_fee_pln: number;
  storage_per_day_after_30d_pln: number;
  note: string;
}

export interface OfPricingReference {
  tiers: OfPricingTier[];
  free_storage_days: number;
  espr_restricted_category_ids: string[];
  espr_effective_from: string;
}

export interface OfFeeCalculation {
  success: boolean;
  error?: string;
  tier?: string;
  tier_label?: string;
  base_fee_pln?: number;
  free_storage_days?: number;
  storage_days_billable?: number;
  storage_per_day_pln?: number;
  storage_fee_pln?: number;
  total_fee_pln?: number;
  tier_note?: string;
}

export interface OfQualification {
  is_qualified: boolean;
  score: number;
  tier: string | null;
  tier_label?: string;
  of_fee: OfFeeCalculation | null;
  reasons: string[];
  warnings: string[];
  offer?: {
    allegro_offer_id: string;
    title: string | null;
    price_pln: number;
    main_image_url: string | null;
    category_id: string | null;
  };
}

export interface OfQualifyResponse {
  success: boolean;
  total_analyzed: number;
  qualified_count: number;
  items: OfQualification[];
}

export const oneFulfillmentApi = {
  pricing: () => api.get<OfPricingReference>("/one-fulfillment/pricing"),
  calculate: (weightKg: number, longestDimCm: number, estimatedDaysInStorage = 30) =>
    api.post<OfFeeCalculation>("/one-fulfillment/calculate", {
      weight_kg: weightKg,
      longest_dim_cm: longestDimCm,
      estimated_days_in_storage: estimatedDaysInStorage,
    }),
  qualify: (limit = 50) =>
    api.get<OfQualifyResponse>("/one-fulfillment/qualify", { params: { limit } }),
};

// ── Sprint IX: Walidator kreacji reklamowych (Agata) ────────────────────

export type AdCreativeType =
  | "banner_horizontal"
  | "banner_vertical"
  | "banner_square"
  | "video"
  | "thumbnail";

export interface AdCreativeFinding {
  code: string;
  severity: "blocking" | "warning" | "info";
  message: string;
  fix_hint: string | null;
  field: string | null;
}

export interface AdCreativeSpec {
  label: string;
  width: number;
  height: number;
  max_file_size_mb: number;
  accepted_formats: string[];
  note: string;
  min_duration_s?: number;
  max_duration_s?: number;
}

export interface AdCreativeValidation {
  ad_type: string;
  can_publish: boolean;
  total_blocking: number;
  total_warnings: number;
  summary: string;
  spec: AdCreativeSpec;
  findings: AdCreativeFinding[];
}

export interface AdCreativeSpecReference {
  types: Record<AdCreativeType, AdCreativeSpec>;
  forbidden_keywords: string[];
}

export const adCreativesApi = {
  spec: () => api.get<AdCreativeSpecReference>("/ad-creatives/spec"),
  validate: (file: File, adType: AdCreativeType, detectedText?: string) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("ad_type", adType);
    if (detectedText) fd.append("detected_text", detectedText);
    return api.post<AdCreativeValidation>("/ad-creatives/validate", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// ── Sprint VI/2: Dostawa Michał (limity 18.08.2026 + Smart! + terminowość) ──

export interface DeliveryRateValidation {
  method_name: string;
  method_type: string;
  method_label: string;
  price_pln: number;
  cap_pln: number | null;
  is_valid: boolean;
  exceeded_by_pln: number;
  note: string;
}

export interface DeliveryAnalysis {
  source: "live" | "unavailable";
  reason?: string;
  total_rates: number;
  total_methods: number;
  valid_count: number;
  invalid_count: number;
  rates: Array<{
    rate_id: string;
    rate_name: string;
    methods: DeliveryRateValidation[];
  }>;
}

export interface DeliveryCap {
  method_type: string;
  cap_pln: number | null;
  label: string;
  note: string;
}

export interface SmartCalculatorResult {
  order_value_pln: number;
  surcharge_pln: number;
  current_bucket_pln: number;
  next_bucket_pln: number | null;
  next_surcharge_pln: number | null;
  savings_at_next_bucket_pln: number;
  buckets: Array<{ from_pln: number; surcharge_pln: number }>;
}

export interface TimelinessSnapshot {
  source: "live" | "unavailable";
  reason?: string | null;
  on_time_pct: number | null;
  fast_dispatch_pct: number | null;
  days_with_data?: number;
  note?: string;
}

export const deliveryApi = {
  analyze: () => api.get<DeliveryAnalysis>("/delivery/analyze"),
  caps: () => api.get<{ caps: DeliveryCap[] }>("/delivery/caps"),
  smartCalculator: (orderValuePln: number) =>
    api.get<SmartCalculatorResult>("/delivery/smart-calculator", {
      params: { order_value_pln: orderValuePln },
    }),
  timeliness: () => api.get<TimelinessSnapshot>("/delivery/timeliness"),
};

// ── Sprint V/1: Monitor zmian Allegro (Robert) ────────────────────────────

export interface AllegroPlatformChange {
  id: number;
  source: "help_center" | "community" | "developer_portal" | "regulations_updates" | "manual";
  source_url: string | null;
  title: string;
  summary: string | null;
  full_text: string | null;
  published_at: string | null;
  category: string | null;
  owner_persona_code: string;
  required_action: string | null;
  effective_date: string | null;
  priority: "critical" | "high" | "medium" | "low";
  status: "new" | "reviewed" | "action_taken" | "dismissed" | "expired";
  classified_by: string | null;
  classified_at: string | null;
  linked_decision_id: number | null;
  created_at: string;
  updated_at: string;
  reviewed_at: string | null;
  reviewed_by_user_id: number | null;
}

export interface AllegroChangesSummary {
  per_status: Array<{ status: string; n: number }>;
  per_persona: Array<{ owner_persona_code: string; n: number }>;
  upcoming_30d: number;
  total: number;
  new_count: number;
}

export const allegroChangesApi = {
  list: (params?: {
    limit?: number;
    offset?: number;
    status?: string;
    owner_persona?: string;
    source?: string;
    priority?: string;
  }) =>
    api.get<{
      items: AllegroPlatformChange[];
      total: number;
      limit: number;
      offset: number;
    }>("/allegro-changes", { params }),
  get: (id: number) =>
    api.get<AllegroPlatformChange>(`/allegro-changes/${id}`),
  summary: () =>
    api.get<AllegroChangesSummary>("/allegro-changes/summary"),
  register: (payload: {
    source: string;
    title: string;
    summary?: string;
    source_url?: string;
    published_at?: string;
    category?: string;
    owner_persona_code?: string;
    required_action?: string;
    effective_date?: string;
    priority?: string;
    auto_classify?: boolean;
  }) => api.post<{ success: boolean; id: number }>("/allegro-changes", payload),
  updateStatus: (id: number, status: string, linked_decision_id?: number) =>
    api.put<{ success: boolean; id: number; new_status: string }>(
      `/allegro-changes/${id}/status`,
      { status, linked_decision_id },
    ),
};

// ── Sprint IV/3: Karta SKU + kalkulator kosztu jednostkowego ─────────────

/** Spec §Warstwa współdzielona pkt 1: Karta SKU jako single source of truth. */
export interface SkuMaster {
  sku_id: string;
  allegro_offer_id: string;
  ean_gtin: string | null;
  sku_code: string | null;
  id_produktu_katalog: string | null;
  nazwa: string | null;
  kategoria_allegro: {
    id: string | null;
    name: string | null;
  };
  cena_aktualna: number | null;
  cena_waluta: string;
  cena_minimalna: number | null;
  stan_magazynowy: number | null;
  is_smart: boolean;
  main_image_url: string | null;
  prowizja_kategoria_pct: number | null;
  data_ostatniej_zmiany: string | null;
  agent_ostatniej_zmiany: string | null;
  rynek: string;
  // Full-only fields
  status?: string | null;
  publication_status?: string | null;
  publication_started_at?: string | null;
  publication_ending_at?: string | null;
  shop_id?: number | null;
  images_count?: number | null;
  images_urls_json?: string | null;
}

/** Spec §Warstwa współdzielona pkt 2: 7 składników marży jednostkowej. */
export interface UnitCostBreakdown {
  success: boolean;
  error?: string;
  allegro_offer_id: string;
  cena_sprzedazy: number;
  cena_minimalna: number | null;
  kategoria: { id: string | null; name: string | null };
  skladniki: {
    koszt_zakupu: number;
    prowizja: {
      pct: number;
      pln: number;
      cap_pln: number | null;
      cap_applied: boolean;
    };
    doplata_smart: {
      pln: number;
      is_smart_offer: boolean;
      prog_pln: number;
    };
    dostawa_netto: number;
    one_fulfillment: { pln: number; note: string };
    koszt_ads_30d: number;
    rezerwa_zwrotu: { pct: number; pln: number };
    pakowanie: number;
    inne_koszty: number;
  };
  total_koszt: number;
  marza_jednostkowa: number;
  marza_pct: number;
  warnings: string[];
  quality: {
    cost_missing: boolean;
    is_estimated: boolean;
    commission_cached: boolean;
  };
}

export const skuApi = {
  list: (params?: {
    limit?: number;
    offset?: number;
    q?: string;
    has_floor?: boolean;
  }) =>
    api.get<{ items: SkuMaster[]; total: number; limit: number; offset: number }>(
      "/sku",
      { params },
    ),
  get: (allegroOfferId: string) =>
    api.get<SkuMaster>(`/sku/${encodeURIComponent(allegroOfferId)}`),
  costBreakdown: (allegroOfferId: string) =>
    api.get<UnitCostBreakdown>(
      `/sku/${encodeURIComponent(allegroOfferId)}/cost-breakdown`,
    ),
  costBreakdownBulk: (offerIds: string[]) =>
    api.post<{ items: UnitCostBreakdown[]; count: number }>(
      "/sku/cost-breakdown/bulk",
      { offer_ids: offerIds },
    ),
  setFloor: (allegroOfferId: string, cenaMinimalna: number | null) =>
    api.put<{
      success: boolean;
      allegro_offer_id: string;
      old_cena_minimalna: number | null;
      new_cena_minimalna: number | null;
      changed_at: string;
      changed_by: string;
    }>(`/sku/${encodeURIComponent(allegroOfferId)}/floor`, {
      cena_minimalna: cenaMinimalna,
    }),
};
